import ReceivingInvoiceDocument from "../components/ReceivingInvoiceDocument";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import { useTransferDestination, resolveTransferDestination } from '../hooks/useTransferDestination';
import { printTransferPdf } from '../utils/printTransferPdf';
import TopNav from '../components/TopNav';
import InvoicePrintEmailActions from '../components/InvoicePrintEmailActions';
import ConfirmModal from '../components/ConfirmModal';
import TransferLimitsModal from '../components/TransferLimitsModal';
import { assessTransferLimits, transferLimitDraft, transferLimitFields } from '../utils/transferLimits';
import { createTransferSummaryCache } from '../utils/transferSummaryCache';
import { useAuth } from '../context/AuthContext';
import { can, getPageAuthority, hasPermission } from '../accessControl';
import { getBusinessAddressLines } from '../utils/businessAddress';
import {
  formatCalendarDate,
  formatDateInputValue,
  formatDateTimeConsistent,
} from '../utils/dateDisplay';
import './css/InventoryPage.css';
import './css/InvoicesPage.css';
import './css/TransfersPage.css';

const money = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '$0.00';
  return number.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const REPAIR_ORDER_TRANSACTION_TYPE_ID = 640;
const SERIAL_LOOKUP_BATCH_SIZE = 10;
const LARGE_TRANSFER_SAVE_TIMEOUT_MS = 5 * 60 * 1000;
const autoEmailStatusText = (result) => {
  if (result?.status === 'sent') return ` PDF emailed to ${result.to}.`;
  if (result?.status === 'failed') return ` ${result.message}`;
  return '';
};
const ADJUSTMENT_SERIAL_SEARCH_INPUT_ID = 'adjustment-serial-search';
const INACTIVE_TRANSFER_LOCATION_MESSAGE = 'Transfers are not allowed to Inactive Locations.';

const TRANSFER_LIST_FILTER_LABELS = {
  type: 'Type',
  from: 'From',
  to: 'To',
};
const TRANSFER_LIST_FILTER_KEYS = Object.keys(TRANSFER_LIST_FILTER_LABELS);

const getTransferListFilterValue = (row, filterKey) => {
  if (filterKey === 'type') return String(row.transactionType || '').trim();
  if (filterKey === 'from') return String(row.fromName || '').trim();
  if (filterKey === 'to') return String(row.toName || '').trim();
  return '';
};

const formatDateInput = (value) => {
  return formatDateInputValue(value);
};

const getTodayDateInputValue = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const getDefaultTransferListDateRange = () => ({
  start: '',
  end: getTodayDateInputValue(),
});

const ADJUSTMENT_DATE_PRESETS = [
  'Today',
  'This Week',
  'This Month',
  'This Fiscal Year',
  'Yesterday',
  'Last Week',
  'Last Month',
  'Last Fiscal Year',
  'All',
];
const DEFAULT_ADJUSTMENT_DATE_PRESET = 'This Month';

const getDatePresetRange = (preset) => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  if (preset === 'All') return { start: '', end: '' };
  if (preset === 'Today') return { start: formatDateInput(start), end: formatDateInput(end) };
  if (preset === 'Yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  }
  if (preset === 'This Week') {
    start.setDate(start.getDate() - start.getDay());
    end.setDate(start.getDate() + 6);
  }
  if (preset === 'Last Week') {
    start.setDate(start.getDate() - start.getDay() - 7);
    end.setTime(start.getTime());
    end.setDate(end.getDate() + 6);
  }
  if (preset === 'This Month') {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 0);
  }
  if (preset === 'Last Month') {
    start.setMonth(start.getMonth() - 1, 1);
    end.setDate(0);
  }
  if (preset === 'This Fiscal Year') {
    start.setMonth(0, 1);
    end.setMonth(11, 31);
  }
  if (preset === 'Last Fiscal Year') {
    start.setFullYear(start.getFullYear() - 1, 0, 1);
    end.setFullYear(end.getFullYear() - 1, 11, 31);
  }
  return { start: formatDateInput(start), end: formatDateInput(end) };
};

const formatShortDate = (value) => {
  return formatCalendarDate(value);
};

const TIME_ZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

const formatLocalTimestampText = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  if (!text || TIME_ZONE_PATTERN.test(text)) return '';
  const match = text.match(LOCAL_TIMESTAMP_PATTERN);
  if (!match) return '';

  const [, year, month, day, hourText, minute, second = '00'] = match;
  const hour = Number(hourText);
  const hour12 = hour % 12 || 12;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${Number(month)}/${Number(day)}/${year}, ${hour12}:${minute}:${second} ${suffix}`;
};

const transactionTimestampDisplay = (value) => {
  const formatted = formatLocalTimestampText(value) || formatDateTimeConsistent(value);
  if (!formatted) return { primary: '-', secondary: '' };
  const [primary, ...secondaryParts] = formatted.split(', ');
  return {
    primary,
    secondary: secondaryParts.join(', '),
  };
};

const renderTransactionTimestamp = (value) => {
  const display = transactionTimestampDisplay(value);
  return (
    <>
      {display.primary}
      {display.secondary && <small className="transfer-date-note">{display.secondary}</small>}
    </>
  );
};

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const invoiceAmount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: number % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const plainAmount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0.00';
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const SerialList = ({ serials = [], highlightedSerial = '' }) => {
  const highlighted = String(highlightedSerial || '').trim().toLowerCase();
  return (
    <>
      {serials.map((serial, index) => {
        const isHighlighted = highlighted
          && String(serial || '').trim().toLowerCase() === highlighted;
        return (
          <React.Fragment key={`${serial}-${index}`}>
            <span className={isHighlighted ? 'transfer-serial-highlight' : ''}>
              {serial}
            </span>
            {index < serials.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      })}
    </>
  );
};

const splitInvoiceSerials = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => splitInvoiceSerials(entry));
  }
  return String(value || '')
    .split(/[,\n]+/)
    .map((serial) => serial.trim())
    .filter(Boolean);
};

const isInvoiceSerialCountRow = (item = {}) => {
  if (item.invoiceLineType === 'other_service' || item.serviceId || item.service_id || item.discountId || item.discount_id) {
    return false;
  }
  const label = `${item.serial || ''} ${item.description || ''} ${item.lineDescription || ''} ${item.item || ''}`.trim();
  if (/\b(discount|service|sales\s+tax|tax)\b/i.test(label)) return false;
  return true;
};

const countInvoiceSerials = (items = []) => (
  items.reduce((sum, item) => {
    if (!isInvoiceSerialCountRow(item)) return sum;
    const serials = item.serials?.length ? item.serials : item.serial;
    return sum + splitInvoiceSerials(serials).length;
  }, 0)
);

const groupTransferItems = (items = []) => {
  const groups = new Map();

  items.forEach((item, index) => {
    const key = [
      item.lineNo,
      item.itemId,
      item.item,
      item.description,
      item.lineDescription,
      item.actualPrice,
      item.costPer,
      item.vendorPart,
      item.uom,
      item.poNo,
    ].join('|');
      const current = groups.get(key) || {
        ...item,
        id: `group-${item.lineNo || index}-${item.itemId || item.item || index}`,
        quantity: 0,
        totalCost: 0,
        actualTotal: 0,
        serials: [],
        shipDocItemIds: [],
      };
    const serials = splitInvoiceSerials(item.serials?.length ? item.serials : item.serial);

    current.quantity += Number(item.quantity) || 0;
    current.totalCost += Number(item.totalCost) || 0;
    current.actualTotal += Number(item.actualTotal ?? item.actualPrice ?? item.totalCost) || 0;
    current.ordered = item.ordered;
    current.backOrder = item.backOrder;
    current.costPer = Number(item.costPer) || current.costPer || 0;
    current.actualPrice = Number(item.actualPrice) || current.actualPrice || 0;
    serials.forEach((serial) => {
      if (!current.serials.includes(serial)) {
        current.serials.push(serial);
      }
    });
    const shipDocItemIds = Array.isArray(item.shipDocItemIds)
      ? item.shipDocItemIds
      : [item.shipDocItemId].filter(Boolean);
    shipDocItemIds.forEach((id) => {
      const numericId = Number(id);
      if (Number.isFinite(numericId) && !current.shipDocItemIds.includes(numericId)) {
        current.shipDocItemIds.push(numericId);
      }
    });
    groups.set(key, current);
  });

  return Array.from(groups.values());
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const parseSerialSearch = (value) => String(value || '')
  .split(/[,\n]+/)
  .map((serial) => serial.trim())
  .filter(Boolean);

const DEFAULT_TRANSFER_FROM_LOCATION = 'IRENES WIGS - NEW YORK';
const TRANSFER_SOURCE_STOCK_MESSAGE = 'This item is not in stock at the selected location, please check your serial number';
const normalizeLocationName = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const normalizeLookupText = (value) => normalizeLocationName(value).replace(/[^\w]+/g, '');
const normalizeCustomerTypeName = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
const getCustomerDisplayName = (customer) => customer?.name || customer?.full_name || customer?.fullname || '';
const getCustomerId = (customer) => customer?.id || customer?.nc_customer_id || customer?.nccustomerid || '';

const getCustomerPhone = (customer) => customer?.phone || customer?.altphone || '';
const getCustomerInvoiceAddress = (customer) => ({
  name: getCustomerDisplayName(customer),
  addr1: customer?.billaddr1 || customer?.bill_addr_1 || customer?.billAddr1 || '',
  addr2: customer?.billaddr2 || customer?.bill_addr_2 || customer?.billAddr2 || '',
  addr3: customer?.billaddr3 || customer?.bill_addr_3 || customer?.billAddr3 || '',
  addr4: customer?.billaddr4 || customer?.bill_addr_4 || customer?.billAddr4 || '',
  city: customer?.billcity || customer?.bill_city || customer?.billCity || '',
  state: customer?.billstate || customer?.bill_state || customer?.billState || '',
  zip: customer?.billzip || customer?.bill_zip || customer?.billZip || '',
  country: customer?.billcountry || customer?.bill_country || customer?.billCountry || '',
  phone: getCustomerPhone(customer),
  email: customer?.email || '',
  website: customer?.website || '',
});

const buildAddressLines = (source = {}, prefix = '') => {
  const lines = [
    source[`${prefix}addr1`] || source.addr1 || source.address1,
    source[`${prefix}addr2`] || source.addr2 || source.address2,
    source[`${prefix}addr3`] || source.addr3 || source.address3,
    source[`${prefix}addr4`] || source.addr4 || source.address4,
    [
      source[`${prefix}city`] || source.city,
      source[`${prefix}state`] || source.state,
      source[`${prefix}zip`] || source.zip,
    ].filter(Boolean).join(' '),
    source[`${prefix}country`] || source.country,
  ];
  return lines.map((line) => String(line || '').trim()).filter(Boolean);
};

const getCustomerAddressLines = (customer = {}) => {
  const billingLines = buildAddressLines(customer, 'bill');
  if (billingLines.length > 0) return billingLines;
  const shippingLines = buildAddressLines(customer, 'ship');
  if (shippingLines.length > 0) return shippingLines;
  const address = Array.isArray(customer.addresses) ? customer.addresses[0] : null;
  return address ? buildAddressLines(address) : [];
};

const getRetailCustomerTypeId = (types = []) => {
  const retail = types.find((type) => String(type.name || '').trim().toLowerCase() === 'retail');
  return retail ? String(retail.id) : '';
};

const getWholesaleCustomerTypeId = (types = []) => {
  const wholesale = types.find((type) => String(type.name || '').trim().toLowerCase() === 'wholesale');
  return wholesale ? String(wholesale.id) : '';
};

const parsePositiveLimit = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const apiErrorMessage = (err, fallback) => (
  err?.response?.data?.error
  || err?.response?.data?.message
  || (err?.response?.status ? `${fallback} Server returned ${err.response.status}.` : '')
  || err?.message
  || fallback
);

const documentMemoText = (document = {}) => String(
  document.memo
  || document.comment
  || document.comments
  || document.sdComment
  || document.sd_comment
  || document.note
  || document.notes
  || ''
).trim();

const AddressBlock = ({ title, address }) => (
  <section className="invoice-address">
    <div className="invoice-address-title">{title}:</div>
    <div className="invoice-address-body">
      <strong>{address?.name}</strong>
      {address?.addr1 && <span>{address.addr1}</span>}
      {(address?.city || address?.state || address?.zip) && (
        <span className="invoice-city-line">
          <span>{address.city}</span>
          <span>{address.state}</span>
          <span>{address.zip}</span>
        </span>
      )}
      {address?.phone && <span>{address.phone}</span>}
      {address?.website && <span>{address.website}</span>}
      {address?.email && <span>{address.email}</span>}
    </div>
  </section>
);

const compactAddressLines = (address = {}) => {
  const lines = [];
  const seenText = new Set();
  const seenPhones = new Set();
  const addLine = (value) => {
    const text = String(value || '').trim();
    if (!text) return;
    const normalizedText = text.toLowerCase().replace(/\s+/g, ' ');
    const digits = text.replace(/[^0-9]/g, '');
    if (seenText.has(normalizedText)) return;
    if (digits.length >= 7 && seenPhones.has(digits)) return;
    lines.push(text);
    seenText.add(normalizedText);
    if (digits.length >= 7) seenPhones.add(digits);
  };

  addLine(address.name);
  [address.addr1, address.addr2, address.addr3, address.addr4]
    .filter(Boolean)
    .forEach(addLine);
  const cityLine = [
    address.city,
    address.state,
    address.zip,
    address.country,
  ].filter(Boolean).join(' ');
  addLine(cityLine);
  addLine(address.phone);
  addLine(address.email);
  return lines;
};

const isIrenesNewYorkLocation = (value) => {
  const normalized = normalizeLookupText(value);
  return normalized === normalizeLookupText(DEFAULT_TRANSFER_FROM_LOCATION)
    || normalized === normalizeLookupText("Irene's Wigs - New York")
    || normalized === normalizeLookupText("Irenes Wigs | New York");
};

export const getTransferInvoiceTitle = (transfer) => {
  if (transfer?.documentTitle === 'Credit Memo') return 'Credit Memo';
  const transactionTypeId = Number(transfer?.transactionTypeId);
  const destination = transfer?.receiveLocation || transfer?.to?.name || '';
  if (transactionTypeId === 5 && isIrenesNewYorkLocation(destination)) return 'Return';
  return 'Invoice';
};

const shouldShowTransferInvoiceDocument = (transfer) => {
  if (!transfer) return false;
  const transactionTypeId = Number(transfer.transactionTypeId);
  const documentTitle = String(transfer.documentTitle || transfer.type || '').trim();
  return transfer.documentLayout === 'invoice'
    || transactionTypeId === 4
    || transactionTypeId === 5
    || transactionTypeId === 10
    || /^credit memo$/i.test(documentTitle);
};

const getTransferPartyAddress = (transfer, partyName) => {
  if (isIrenesNewYorkLocation(partyName)) {
    return transfer?.businessAddress || transfer?.fromAddress || transfer?.billTo || {};
  }

  const billTo = transfer?.billTo || {};
  const name = partyName || billTo.name || transfer?.customerName || '';
  return {
    name,
    addr1: billTo.addr1 || '',
    addr2: billTo.addr2 || '',
    addr3: billTo.addr3 || '',
    city: billTo.city || '',
    state: billTo.state || '',
    zip: billTo.zip || '',
    country: billTo.country || '',
    phone: billTo.phone || '',
    email: billTo.email || '',
  };
};

export const transferToEmailInvoice = (transfer) => {
  if (!transfer) return null;
  const rawItems = transfer.items || [];
  const adjustmentItems = rawItems.filter((item) => item.invoiceLineType === 'other_service');
  const groupedItems = groupTransferItems(rawItems.filter((item) => item.invoiceLineType !== 'other_service'));
  const documentTitle = getTransferInvoiceTitle(transfer);
  const isCreditMemo = documentTitle === 'Credit Memo' || Number(transfer.transactionTypeId) === 10;
  const fromName = transfer.fromLocation || transfer.vendor || '';
  const toName = Number(transfer.transactionTypeId) === 4 || isCreditMemo
    ? (transfer.customerName || transfer.billTo?.name || '')
    : (transfer.receiveLocation || transfer.customerName || '');
  const itemReturnLocation = isCreditMemo
    ? rawItems.find((item) => String(item.location || '').trim())?.location
    : '';
  const returnLocation = isCreditMemo ? (
    transfer.returnLocation
    || transfer.receiveLocation
    || transfer.detailThirdValue
    || itemReturnLocation
    || transfer.fromLocation
    || transfer.to?.name
    || DEFAULT_TRANSFER_FROM_LOCATION
  ) : '';
  const items = groupedItems.map((item) => {
    const serials = item.serials?.length ? item.serials : [item.serial].filter(Boolean);
    const quantity = Math.abs(Number(item.quantity) || 0) || serials.length || 1;
    return {
      id: item.id,
      shipDocItemIds: item.shipDocItemIds || [],
      serial: serials.join(', '),
      description: item.item || item.description || '',
      quantity,
      price: Number(item.costPer) || 0,
    };
  }).concat(adjustmentItems.map((item, index) => ({
    id: item.id || `invoice-adjustment-${index}`,
    serial: '',
    lineDescription: item.lineDescription || item.description || '',
    description: item.item || item.description || '',
    quantity: 1,
    price: Number(item.actualTotal ?? item.price ?? item.costPer ?? 0) || 0,
    invoiceLineType: 'other_service',
    serviceId: item.serviceId || null,
    discountId: item.discountId || null,
    servicePrice: item.servicePrice,
    servicePriceType: item.servicePriceType,
  })));
  const itemTotal = items.reduce(
    (sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.price) || 0)),
    0
  );
  const totalAmount = Number.isFinite(Number(transfer.total)) ? Number(transfer.total) : itemTotal;
  return {
    id: transfer.docNo,
    invoiceNo: transfer.docNo,
    date: transfer.receiveDate,
    type: documentTitle,
    documentTitle,
    transactionTypeId: transfer.transactionTypeId || null,
    shipMethod: '',
    packageCount: groupedItems.length,
    memo: documentMemoText(transfer),
    comment: documentMemoText(transfer),
    preparedBy: transfer.preparedBy || '',
    from: transfer.fromAddress || getTransferPartyAddress(transfer, fromName),
    to: getTransferPartyAddress(transfer, toName),
    billTo: getTransferPartyAddress(transfer, toName),
    clientName: transfer.customerName || transfer.billTo?.name || toName,
    customerName: transfer.customerName || transfer.billTo?.name || toName,
    detailThirdLabel: isCreditMemo ? 'Return Location' : 'Project',
    detailThirdValue: returnLocation,
    returnLocation,
    businessAddress: transfer.businessAddress || transfer.fromAddress || null,
    items,
    totalQuantity: items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    totalAmount,
    taxAmount: Number(transfer.taxAmount) || 0,
    taxRate: Number(transfer.taxRate) || 0,
    payments: Number(transfer.payments) || 0,
    credits: Number(transfer.credits) || 0,
    balanceDue: Number.isFinite(Number(transfer.balanceDue))
      ? Number(transfer.balanceDue)
      : Math.max(totalAmount - (Number(transfer.payments) || 0) - (Number(transfer.credits) || 0), 0),
    paidDate: transfer.paidDate || null,
  };
};

const invoiceToShipDocTransfer = (invoice) => {
  if (!invoice) return null;
  const items = (invoice.items || []).map((item, index) => {
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const price = Number(item.price ?? item.lineTotal ?? item.actualTotal) || 0;
    const actualTotal = Number(item.actualTotal ?? item.lineTotal ?? quantity * price) || 0;
    const serials = splitInvoiceSerials(item.serials?.length ? item.serials : item.serial);
    return {
      id: item.id || `invoice-item-${index}`,
      lineKey: item.repairComponentKey || undefined,
      lineNo: item.repairComponentKey ? item.lineNo : undefined,
      shipDocItemId: item.shipDocItemId || null,
      shipDocItemIds: item.shipDocItemIds || (item.shipDocItemId ? [item.shipDocItemId] : []),
      item: item.description || '',
      description: item.description || '',
      serial: serials[0] || '',
      serials,
      quantity,
      price,
      costPer: price,
      actualPrice: price,
      actualTotal,
      invoiceLineType: item.invoiceLineType || '',
      serviceId: item.serviceId || item.service_id || null,
      discountId: item.discountId || item.discount_id || null,
      servicePrice: item.servicePrice,
      servicePriceType: item.servicePriceType,
      lineDescription: item.lineDescription || '',
    };
  });
  const itemTotal = items.reduce((sum, item) => sum + item.actualTotal, 0);
  const title = String(invoice.documentTitle || invoice.type || 'Invoice');
  return {
    docNo: invoice.invoiceNo || invoice.id || '',
    receiveDate: invoice.date,
    documentTitle: /credit memo/i.test(title) ? 'Credit Memo' : /return/i.test(title) ? 'Return' : 'Invoice',
    customerName: invoice.clientName || invoice.customerName || invoice.to?.name || '',
    billTo: invoice.billTo || invoice.to || {},
    clientName: invoice.clientName || invoice.customerName || invoice.to?.name || '',
    detailThirdLabel: invoice.detailThirdLabel || (/credit memo/i.test(title) ? 'Return Location' : 'Project'),
    detailThirdValue: invoice.detailThirdValue || invoice.returnLocation || invoice.project || '',
    returnLocation: invoice.returnLocation || '',
    businessAddress: invoice.businessAddress || invoice.from || {},
    fromAddress: invoice.from || invoice.businessAddress || {},
    items,
    total: Number.isFinite(Number(invoice.totalAmount)) ? Number(invoice.totalAmount) : itemTotal,
    taxAmount: Number(invoice.taxAmount) || 0,
    taxRate: Number(invoice.taxRate) || 0,
    payments: Number(invoice.payments) || 0,
    credits: Number(invoice.credits) || 0,
    balanceDue: invoice.balanceDue,
    paidDate: invoice.paidDate || null,
  };
};

const roundInvoiceMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const normalizeInvoiceTaxRate = (value) => {
  const rate = Number(value || 0);
  if (!Number.isFinite(rate) || rate < 0) return 0;
  return Math.round(rate * 10000) / 10000;
};

export const invoiceEditTaxOptions = (preferencesTaxRate = 0, currentTaxRate = 0) => {
  const rates = [0, preferencesTaxRate]
    .map((rate) => normalizeInvoiceTaxRate(rate))
    .filter((rate, index, list) => Number.isFinite(rate) && list.indexOf(rate) === index);
  return rates.sort((left, right) => left - right);
};

export const shippingInvoiceEditDraftFromDocument = (documentValue, preferencesTaxRate = 0) => {
  const sharedInvoice = documentValue?.invoiceNo ? documentValue : transferToEmailInvoice(documentValue);
  const document = documentValue?.invoiceNo ? invoiceToShipDocTransfer(documentValue) : (documentValue || null);
  const draftDocument = document || invoiceToShipDocTransfer(sharedInvoice);
  const groupedItems = groupTransferItems((draftDocument?.items || []).filter((item) => item.invoiceLineType !== 'other_service'));
  const items = groupedItems.map((item, index) => {
    const quantity = Math.abs(Number(item.quantity) || 0) || item.shipDocItemIds?.length || 1;
    const rate = roundInvoiceMoney(item.costPer ?? item.price ?? item.actualPrice ?? 0);
    const explicitTotal = Number(item.actualTotal ?? item.lineTotal ?? item.totalCost);
    const amount = roundInvoiceMoney(Number.isFinite(explicitTotal) ? explicitTotal : quantity * rate);
    const itemName = String(item.item || item.referenceNo || item.description || '').trim();
    const lineDescription = String(item.lineDescription || item.serial || '').trim();
    return {
      id: item.id || `invoice-edit-item-${index}`,
      lineKey: item.lineKey || item.id || `invoice-edit-item-${index}`,
      lineNo: item.lineNo,
      itemId: item.itemId,
      item: itemName,
      description: lineDescription,
      shipDocItemIds: item.shipDocItemIds || (item.shipDocItemId ? [item.shipDocItemId] : []),
      syntheticQuickBooksLine: /^quickbooks-/i.test(String(item.id || item.quickBooksLineKey || '')),
      quantity,
      amount: String(amount.toFixed(2)),
      rate: String(rate.toFixed(2)),
    };
  });
  const currentTaxRate = Number(sharedInvoice?.taxAmount || draftDocument?.taxAmount || 0) > 0
    ? Number(preferencesTaxRate || sharedInvoice?.taxRate || draftDocument?.taxRate || 0)
    : 0;
  return {
    docNo: String(documentValue?.invoiceNo || documentValue?.docNo || documentValue?.id || ''),
    memo: documentMemoText(documentValue) || documentMemoText(sharedInvoice) || documentMemoText(draftDocument),
    taxRate: String(normalizeInvoiceTaxRate(currentTaxRate)),
    items,
  };
};

const isInvoiceDiscountLine = (item = {}) => Boolean(item.discountId || item.discount_id);

const isInvoiceTaxAdjustmentLine = (item = {}) => {
  const label = `${item.item || ''} ${item.description || ''} ${item.lineDescription || ''}`.trim();
  return item.invoiceLineType === 'other_service' && /^sales\s+tax\b/i.test(label);
};

const LegacyShipDocInvoiceDocument = ({
  transfer,
  invoice,
  sourceDocument = null,
  highlightSerial = '',
  editable = false,
  editDraft = null,
  taxOptions = [0],
  billToLookup = null,
  onDraftItemChange = () => {},
  onDraftItemAdd = () => {},
  onDraftItemRemove = () => {},
  onDraftMemoChange = () => {},
  onDraftTaxRateChange = () => {},
  canEditLineText = false,
  canAddInvoiceLines = false,
  canEditAmounts = true,
  canEditLineAmount = true,
  canEditTax = true,
  canEditMemo = true,
}) => {
  const document = transfer || invoiceToShipDocTransfer(invoice);
  if (!document) return null;
  const highlightedSerial = String(highlightSerial || '').trim().toLowerCase();
  const documentItems = document.items || [];
  const otherServiceItems = documentItems.filter((item) => item.invoiceLineType === 'other_service');
  const discountItems = otherServiceItems.filter(isInvoiceDiscountLine);
  const serviceItems = otherServiceItems.filter((item) => !isInvoiceDiscountLine(item));
  const hasTaxAdjustmentLine = otherServiceItems.some(isInvoiceTaxAdjustmentLine);
  const groupedItems = groupTransferItems(documentItems.filter((item) => item.invoiceLineType !== 'other_service')).map((item) => {
    const quantity = Math.abs(Number(item.quantity) || 0);
    const rate = Number(item.costPer) || 0;
    const explicitTotal = Number(item.actualTotal ?? item.lineTotal ?? item.totalCost);
    const actualAmount = Number.isFinite(explicitTotal) ? explicitTotal : quantity * rate;
    return {
      ...item,
      quantity,
      rate,
      amount: actualAmount,
    };
  });
  const draftItems = Array.isArray(editDraft?.items) ? editDraft.items : [];
  const editItemsById = new Map(draftItems.map((item) => [String(item.id), item]));
  const editLineText = editable && canEditLineText;
  const editAmounts = editable && canEditAmounts;
  const editTax = editable && canEditTax;
  const editMemo = editable && canEditMemo;
  const displayItems = editLineText ? draftItems.map((item, index) => ({
    id: item.id || item.lineKey || `invoice-edit-item-${index}`,
    item: item.item || '',
    description: item.item || '',
    serial: item.description || '',
    quantity: Math.abs(Number(item.quantity) || 0) || 1,
    amount: Number(item.amount) || 0,
    rate: Number(item.rate) || 0,
  })) : groupedItems.map((item, index) => {
    const draftItem = editItemsById.get(String(item.id)) || editDraft?.items?.[index];
    if (!editAmounts || !draftItem) return item;
    return {
      ...item,
      amount: Number(draftItem.amount) || 0,
      rate: Number(draftItem.rate) || 0,
    };
  });
  const subtotal = displayItems.reduce((sum, item) => sum + item.amount, 0);
  const adjustmentAmount = (item) => Number(item.price ?? item.actualTotal ?? item.lineTotal ?? item.amount ?? item.costPer ?? 0) || 0;
  const discountTotal = discountItems.reduce((sum, item) => sum + adjustmentAmount(item), 0);
  const editableServiceItems = editTax ? serviceItems.filter((item) => !isInvoiceTaxAdjustmentLine(item)) : serviceItems;
  const serviceTotal = editableServiceItems.reduce((sum, item) => sum + adjustmentAmount(item), 0);
  const taxRate = editTax ? Number(editDraft?.taxRate || 0) : Number(document.taxRate) || 0;
  const taxAmount = editTax
    ? roundInvoiceMoney(Math.max(subtotal + discountTotal, 0) * taxRate / 100)
    : Number(document.taxAmount) || 0;
  const hasTaxAmount = Math.abs(taxAmount) > 0.001;
  const total = editAmounts || editTax
    ? roundInvoiceMoney(subtotal + discountTotal + taxAmount + serviceTotal)
    : Number.isFinite(Number(document.total)) ? Number(document.total) : subtotal + taxAmount;
  const paymentsCredits = (Number(document.payments) || 0) + (Number(document.credits) || 0);
  const balanceDue = editAmounts || editTax
    ? Math.max(total - paymentsCredits, 0)
    : Number.isFinite(Number(document.balanceDue))
      ? Number(document.balanceDue)
      : Math.max(total - paymentsCredits, 0);
  const documentTitle = document.documentTitle || 'Invoice';
  const isCreditMemoDocument = Number(document.transactionTypeId) === 10
    || /credit memo/i.test(String(documentTitle || ''))
    || /return location/i.test(String(document.detailThirdLabel || ''));
  const paidDate = document.paidDate ? formatShortDate(document.paidDate) : '';
  const showPaidWatermark = balanceDue <= 0.001 && paidDate && /^invoice$/i.test(String(documentTitle || '').trim());
  const billToAddress = editable && editDraft?.billTo ? editDraft.billTo : (document.billTo || { name: document.customerName || document.receiveLocation || '' });
  const billToLines = compactAddressLines(billToAddress);
  const clientName = editable && editDraft?.customerName
    ? editDraft.customerName
    : (document.clientName || document.customerName || document.billTo?.name || document.receiveLocation || '');
  const rawDetailThirdLabel = document.detailThirdLabel || (isCreditMemoDocument ? 'Return Location' : 'Project');
  const detailThirdLabel = !isCreditMemoDocument && /^project$/i.test(String(rawDetailThirdLabel || ''))
    ? 'Sold'
    : rawDetailThirdLabel;
  const detailThirdValue = !isCreditMemoDocument && /^sold$/i.test(String(detailThirdLabel || ''))
    ? String(countInvoiceSerials(displayItems))
    : (document.detailThirdValue || document.returnLocation || document.project || '');
  const businessAddressLines = getBusinessAddressLines(
    document.businessAddress || document.fromAddress || {}
  );
  const memoText = editMemo ? String(editDraft?.memo || '') : (documentMemoText(document) || documentMemoText(sourceDocument));
  const renderAdjustmentItem = (item, index, keyPrefix) => {
    const serviceAmount = Number(item.price ?? item.actualTotal ?? item.lineTotal ?? item.amount ?? item.costPer ?? 0) || 0;
    const taxRateMatch = isInvoiceTaxAdjustmentLine(item)
      ? String(item.lineDescription || item.description || '').match(/Sales Tax \(([^)]+)\)/i)
      : null;
    const serviceRate = taxRateMatch
      ? taxRateMatch[1]
      : item.servicePriceType === 'percent'
        ? `${Number(item.servicePrice || 0)}%`
        : plainAmount(item.servicePrice ?? item.price);
    return (
      <tr key={item.id || `${keyPrefix}-${index}`}>
        <td>{item.description || item.item}</td>
        <td>{item.lineDescription || ''}</td>
        <td>{plainAmount(serviceAmount)}</td>
        <td>{serviceRate}</td>
      </tr>
    );
  };

  return (
    <article className="shipdoc-invoice-sheet">
      {showPaidWatermark && (
        <div className="shipdoc-paid-watermark" aria-label={`Paid ${paidDate}`}>
          <strong>PAID</strong>
          <span>{paidDate}</span>
        </div>
      )}
      <header className="shipdoc-invoice-top">
        <div className="shipdoc-company">
          {businessAddressLines.map((line) => <div key={line}>{line}</div>)}
        </div>
        <div className="shipdoc-title-block">
          <h1>{documentTitle}</h1>
          <table className="shipdoc-invoice-meta">
            <thead>
              <tr>
                <th>Date</th>
                <th>{documentTitle === 'Credit Memo' ? 'Credit Memo #' : 'Invoice #'}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formatShortDate(document.receiveDate)}</td>
                <td>{document.docNo}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </header>

      <section className="shipdoc-bill-to-box">
        <div className="shipdoc-bill-to-title-row">
          <h2>Bill To</h2>
          {editable && billToLookup && (
            <div className="shipdoc-bill-to-lookup">
              <input
                className="shipdoc-bill-to-lookup-input"
                value={billToLookup.query}
                onChange={(event) => billToLookup.onQueryChange(event.target.value)}
                onFocus={billToLookup.onFocus}
                onBlur={billToLookup.onBlur}
                onKeyDown={billToLookup.onKeyDown}
                placeholder="Search customer"
                autoComplete="off"
                role="combobox"
                aria-expanded={Boolean(billToLookup.open)}
                aria-controls="invoice-bill-to-customer-menu"
              />
              {billToLookup.open && billToLookup.options?.length > 0 && (
                <div className="shipdoc-bill-to-lookup-menu" id="invoice-bill-to-customer-menu" role="listbox">
                  {billToLookup.options.map((customer, index) => (
                    <button
                      type="button"
                      key={customer.id || customer.nccustomerid || customer.name}
                      className={index === billToLookup.activeIndex ? 'active' : ''}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseMove={() => billToLookup.onActiveIndexChange(index)}
                      onClick={() => billToLookup.onSelect(customer)}
                      role="option"
                      aria-selected={index === billToLookup.activeIndex}
                    >
                      <span>{billToLookup.getLabel ? billToLookup.getLabel(customer) : customer.name}</span>
                      {billToLookup.getMeta && <small>{billToLookup.getMeta(customer)}</small>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="shipdoc-bill-to">
          {billToLines.map((line, index) => <div key={`${line}-${index}`}>{line}</div>)}
        </div>
      </section>

      <div className="shipdoc-lines-area">
        <table className="shipdoc-detail-strip">
          <thead>
            <tr>
              <th>Client's Name</th>
              <th>Terms</th>
              <th>{detailThirdLabel}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{clientName}</td>
              <td></td>
              <td>{detailThirdValue}</td>
            </tr>
          </tbody>
        </table>

        {editLineText && canAddInvoiceLines && (
          <div className="shipdoc-invoice-line-actions">
            <button type="button" className="button small blue" onClick={onDraftItemAdd}>
              Add Item
            </button>
          </div>
        )}
        <table className="shipdoc-invoice-items">
          <thead>
            <tr>
              <th>Item</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item, index) => {
              const serials = item.serials?.length ? item.serials : [item.serial].filter(Boolean);
              const isHighlighted = highlightedSerial
                && serials.some((serial) => String(serial || '').trim().toLowerCase() === highlightedSerial);
              const draftItem = editItemsById.get(String(item.id)) || editDraft?.items?.[index];
              return (
                <tr key={item.id || `${item.item}-${index}`} className={isHighlighted ? 'shipdoc-highlight-row' : ''}>
                  <td>
                    {editLineText && draftItem ? (
                      <div className="shipdoc-invoice-line-edit">
                        <input
                          className="shipdoc-invoice-edit-input shipdoc-invoice-edit-text-input"
                          type="text"
                          value={draftItem.item || ''}
                          onChange={(event) => onDraftItemChange(draftItem.id, 'item', event.target.value)}
                          aria-label={`Item for invoice line ${index + 1}`}
                        />
                        {canAddInvoiceLines && index > 0 && (
                          <button
                            type="button"
                            className="button small red shipdoc-invoice-line-remove"
                            onClick={() => onDraftItemRemove(draftItem.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ) : item.item || item.description}
                  </td>
                  <td>
                    {editLineText && draftItem ? (
                      <div className="shipdoc-invoice-line-edit">
                        <textarea
                          className="shipdoc-invoice-edit-textarea"
                          value={draftItem.description || ''}
                          onChange={(event) => onDraftItemChange(draftItem.id, 'description', event.target.value)}
                          aria-label={`Description for invoice line ${index + 1}`}
                          rows="2"
                        />
                      </div>
                    ) : serials.length > 0 ? (
                      <span className="shipdoc-serial-list">
                        {serials.map((serial, serialIndex) => (
                          <span key={`${serial}-${serialIndex}`} className="shipdoc-serial-value">
                            {serial}
                          </span>
                        ))}
                      </span>
                    ) : item.lineDescription || ''}
                  </td>
                  <td>
                    {editAmounts && canEditLineAmount && draftItem ? (
                      <input
                        className="shipdoc-invoice-edit-input"
                        type="text"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={draftItem.amount}
                        onChange={(event) => onDraftItemChange(draftItem.id, 'amount', event.target.value)}
                        aria-label={`Amount for ${item.item || item.description || 'invoice item'}`}
                      />
                    ) : plainAmount(item.amount)}
                  </td>
                  <td>
                    {editAmounts && draftItem ? (
                      <input
                        className="shipdoc-invoice-edit-input"
                        type="text"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={draftItem.rate}
                        onChange={(event) => onDraftItemChange(draftItem.id, 'rate', event.target.value)}
                        aria-label={`Rate for ${item.item || item.description || 'invoice item'}`}
                      />
                    ) : plainAmount(item.rate)}
                  </td>
                </tr>
              );
            })}
            {discountItems.map((item, index) => renderAdjustmentItem(item, index, 'discount'))}
            {(!hasTaxAdjustmentLine || editTax) && (
              <tr>
                <td></td>
                <td>Sales Tax</td>
                <td>{plainAmount(taxAmount)}</td>
                <td>
                  {editTax ? (
                    <select
                      className="shipdoc-invoice-tax-select"
                      value={String(taxRate)}
                      onChange={(event) => onDraftTaxRateChange(event.target.value)}
                      aria-label="Sales tax rate"
                    >
                      {taxOptions.map((rate) => (
                        <option key={rate} value={String(rate)}>{String(rate)}%</option>
                      ))}
                    </select>
                  ) : hasTaxAmount && taxRate ? `${String(taxRate)}%` : ''}
                </td>
              </tr>
            )}
            {editableServiceItems.map((item, index) => renderAdjustmentItem(item, index, 'other-service'))}
          </tbody>
        </table>
        <div className="shipdoc-summary-row">
          <div className="shipdoc-summary-spacer">
            {editMemo ? (
              <label className="shipdoc-invoice-memo shipdoc-invoice-memo-edit">
                <strong>Memo:</strong>
                <textarea
                  value={memoText}
                  onChange={(event) => onDraftMemoChange(event.target.value)}
                  rows="4"
                />
              </label>
            ) : memoText && (
              <div className="shipdoc-invoice-memo">
                <strong>Memo:</strong> {memoText}
              </div>
            )}
          </div>
          <section className="shipdoc-summary">
            <div>
              <span>Total</span>
              <strong>{invoiceAmount(total)}</strong>
            </div>
            <div>
              <span>Payments/Credits</span>
              <strong>{invoiceAmount(paymentsCredits)}</strong>
            </div>
            <div>
              <span>Balance Due</span>
              <strong>{invoiceAmount(balanceDue)}</strong>
            </div>
          </section>
        </div>
      </div>
    </article>
  );
};

export const CompletedTransferInvoice = ({ invoice, highlightSerial = '' }) => {
  const invoiceItems = invoice.items || [];
  const highlightedSerial = String(highlightSerial || '').trim().toLowerCase();
  const requestedTitle = String(invoice.documentTitle || invoice.type || '').trim();
  const isCreditMemo = /credit memo/i.test(requestedTitle);
  const isTransferDocument = Number(invoice.transactionTypeId) === 5
    || /^return$/i.test(requestedTitle)
    || /^transfer$/i.test(requestedTitle);
  const documentTitle = isCreditMemo
    ? 'CREDIT MEMO'
    : isTransferDocument && isIrenesNewYorkLocation(invoice.to?.name) ? 'RETURN' : 'INVOICE';
  const numberLabel = isCreditMemo ? 'Credit Memo #' : 'Invoice #';
  const totalQty = invoiceItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );
  const totalPrice = invoiceItems.reduce(
    (sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.price) || 0)),
    0
  );
  const preparedBy = invoice.preparedBy || '-';
  const comment = invoice.comment || invoice.memo || '';

  return (
    <article className={`invoice-sheet invoice-sheet-modal${invoiceItems.length > 8 ? ' invoice-sheet-long' : ''}`}>
      <header className="invoice-header">
        <h1>{documentTitle}</h1>
        <div className="invoice-brand">
          <div>IRENE'S</div>
          <span>nyc</span>
        </div>
        <table className="invoice-meta">
          <tbody>
            <tr>
              <th>{numberLabel}</th>
              <th>Date</th>
            </tr>
            <tr>
              <td>{invoice.invoiceNo}</td>
              <td>{formatShortDate(invoice.date)}</td>
            </tr>
            <tr>
              <th>Ship Method</th>
              <td>{invoice.shipMethod}</td>
            </tr>
          </tbody>
        </table>
      </header>

      <div className="invoice-parties">
        <AddressBlock title="From" address={invoice.from} />
        <AddressBlock title="To" address={invoice.to} />
      </div>

      <table className="invoice-items">
        <thead>
          <tr>
            <th>Serial #</th>
            <th>Description</th>
            <th>Quantity</th>
            <th>Price</th>
          </tr>
        </thead>
        <tbody>
          {invoiceItems.map((item, index) => {
            const isHighlighted = highlightedSerial
              && String(item.serial || '')
                .split(',')
                .some((serial) => serial.trim().toLowerCase() === highlightedSerial);
            return (
              <tr
                key={`${item.serial}-${index}`}
                className={isHighlighted ? 'invoice-highlight-row' : ''}
              >
                <td>{item.serial}</td>
                <td>{item.description}</td>
                <td>{formatNumber(item.quantity)}</td>
                <td>{invoiceAmount(item.price)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="transfer-invoice-footer-fields">
        <table className="transfer-invoice-total-table">
          <tbody>
            <tr>
              <td>Sub-Total</td>
              <td>{formatNumber(totalQty)}</td>
              <td>{invoiceAmount(totalPrice)}</td>
            </tr>
            <tr>
              <td>Total</td>
              <td>{formatNumber(totalQty)}</td>
              <td>{invoiceAmount(totalPrice)}</td>
            </tr>
          </tbody>
        </table>
        <div className="transfer-invoice-prepared-by">
          <strong>Prepared By:</strong> {preparedBy}
        </div>
        <div className="transfer-invoice-memo">
          <strong>Comment:</strong> {comment || '-'}
        </div>
      </div>
    </article>
  );
};

export const ShipDocInvoiceDocument = ({
  transfer,
  invoice,
  highlightSerial = '',
  editable = false,
  editDraft = null,
  taxOptions = [0],
  billToLookup = null,
  onDraftItemChange = () => {},
  onDraftItemAdd = () => {},
  onDraftItemRemove = () => {},
  onDraftMemoChange = () => {},
  onDraftTaxRateChange = () => {},
  canEditLineText = false,
  canAddInvoiceLines = false,
  canEditAmounts = true,
  canEditLineAmount = true,
  canEditTax = true,
  canEditMemo = true,
}) => {
  const sharedInvoice = transfer ? transferToEmailInvoice(transfer) : invoice;
  if (!sharedInvoice) return null;
  if (Number(sharedInvoice.transactionTypeId) === 2 || /^receiving invoice$/i.test(String(sharedInvoice.type || ''))) {
    return <ReceivingInvoiceDocument invoice={sharedInvoice} />;
  }
  const documentType = String(sharedInvoice.documentTitle || sharedInvoice.type || '').trim();
  const isTransferDocument = Number(sharedInvoice.transactionTypeId) === 5
    || /^return$/i.test(documentType)
    || /^transfer$/i.test(documentType);
  if (isTransferDocument) {
    return (
      <CompletedTransferInvoice
        invoice={sharedInvoice}
        highlightSerial={highlightSerial}
      />
    );
  }
  return (
    <LegacyShipDocInvoiceDocument
      invoice={sharedInvoice}
      sourceDocument={transfer || invoice}
      highlightSerial={highlightSerial}
      editable={editable}
      editDraft={editDraft}
      taxOptions={taxOptions}
      billToLookup={billToLookup}
      onDraftItemChange={onDraftItemChange}
      onDraftItemAdd={onDraftItemAdd}
      onDraftItemRemove={onDraftItemRemove}
      onDraftMemoChange={onDraftMemoChange}
      onDraftTaxRateChange={onDraftTaxRateChange}
      canEditLineText={canEditLineText}
      canAddInvoiceLines={canAddInvoiceLines}
      canEditAmounts={canEditAmounts}
      canEditLineAmount={canEditLineAmount}
      canEditTax={canEditTax}
      canEditMemo={canEditMemo}
    />
  );
};

export const TransferPrintLabel = ({ location, pickup = false, services = false }) => {
  const cityLine = [location?.city, location?.state].filter(Boolean).join(' ');
  const address1 = location?.address1 || location?.addr1 || '';
  const address2 = location?.address2 || location?.addr2 || '';
  const address3 = location?.address3 || location?.addr3 || '';
  const address4 = location?.address4 || location?.addr4 || '';
  const phoneDigits = String(location?.phone || '').replace(/\D/g, '');
  const isPhoneLine = (value) => phoneDigits && String(value || '').replace(/\D/g, '') === phoneDigits;
  const normalizeLabelLine = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizedName = normalizeLabelLine(location?.name);
  const isNameLine = (value) => normalizedName && normalizeLabelLine(value) === normalizedName;
  const labelClassName = pickup ? 'transfer-print-label transfer-print-label--pickup' : 'transfer-print-label';
  if (pickup) {
    return (
      <article className={labelClassName}>
        <div className="transfer-print-label-address">
          <strong>{location?.name || ''}</strong>
          <div className="transfer-print-label-pickup">*PICKUP<br />ONLY*</div>
          {location?.phone && <span className="transfer-print-label-pickup-phone">{location.phone}</span>}
        </div>
        <div className="transfer-print-label-footer">
          {services && <div className="transfer-print-label-services">*SERVICES*</div>}
        </div>
      </article>
    );
  }
  return (
    <article className={labelClassName}>
      <div className="transfer-print-label-address">
        <strong>{location?.name || ''}</strong>
        {address1 && !isPhoneLine(address1) && !isNameLine(address1) && <span>{address1}</span>}
        {address2 && !isPhoneLine(address2) && !isNameLine(address2) && <span>{address2}</span>}
        {address3 && !isPhoneLine(address3) && !isNameLine(address3) && <span>{address3}</span>}
        {address4 && !isPhoneLine(address4) && !isNameLine(address4) && <span>{address4}</span>}
        {cityLine && <span className="transfer-print-label-break-before">{cityLine}</span>}
        {location?.phone && <span className="transfer-print-label-break-before">{location.phone}</span>}
      </div>
      <div className="transfer-print-label-footer">
        {services && <div className="transfer-print-label-services">*SERVICES*</div>}
      </div>
    </article>
  );
};

export const printTransferLabel = ({ location, pickup = false, services = false, docNo = '' }) => {
  if (!location) return;
  const address1 = location.address1 || location.addr1 || '';
  const address2 = location.address2 || location.addr2 || '';
  const address3 = location.address3 || location.addr3 || '';
  const address4 = location.address4 || location.addr4 || '';
  const cityLine = [location.city, location.state].filter(Boolean).join(' ');
  const phoneDigits = String(location.phone || '').replace(/\D/g, '');
  const isPhoneLine = (value) => phoneDigits && String(value || '').replace(/\D/g, '') === phoneDigits;
  const normalizeLabelLine = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizedName = normalizeLabelLine(location.name);
  const isNameLine = (value) => normalizedName && normalizeLabelLine(value) === normalizedName;
  const shippingAddressLines = [
    !isPhoneLine(address1) && !isNameLine(address1) ? address1 : '',
    !isPhoneLine(address2) && !isNameLine(address2) ? address2 : '',
    !isPhoneLine(address3) && !isNameLine(address3) ? address3 : '',
    !isPhoneLine(address4) && !isNameLine(address4) ? address4 : '',
  ].filter(Boolean);
  const labelClass = pickup ? 'label label-pickup' : 'label';
  const iframe = document.createElement('iframe');
  iframe.title = pickup ? 'Pick Up label print output' : 'Delivery label print output';
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '384px';
  iframe.style.height = '576px';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => setTimeout(() => iframe.remove(), 250);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <!doctype html>
    <html>
      <head>
        <title>${pickup ? 'Pick Up' : 'Shipping'} Label - Transfer ${escapeHtml(docNo)}</title>
        <style>
          @page { size: 4in 6in; margin: 0; }
          * { box-sizing: border-box; }
          html, body { height: 6in; margin: 0; overflow: hidden; padding: 0; width: 4in; }
          .label {
            align-items: center;
            background: #fff;
            color: #111;
            display: flex;
            flex-direction: column;
            font-family: Arial, Helvetica, sans-serif;
            height: 6in;
            justify-content: space-between;
            overflow: hidden;
            padding: 0.48in 0.2in 0.38in;
            text-align: center;
            text-transform: uppercase;
            width: 4in;
          }
          .label-pickup {
            align-items: flex-start;
            border: 1px solid #111;
            justify-content: flex-start;
            padding: 0.26in 0.22in;
            text-align: left;
          }
          .address { display: flex; flex-direction: column; font-size: 0.44in; font-weight: 700; line-height: 1.18; width: 100%; }
          .name { font-size: 0.44in; line-height: 1.08; margin-bottom: 0.18in; }
          .break-before { margin-top: 0.32in; }
          .footer { display: flex; flex-direction: column; gap: 0.18in; width: 100%; }
          .label-pickup .footer {
            align-items: center;
            margin-top: auto;
          }
          .label-pickup .address {
            align-items: flex-start;
            font-size: 0.44in;
            line-height: 1.15;
            width: auto;
          }
          .label-pickup .name {
            font-size: 0.44in;
            line-height: 1.05;
            margin: 0 0 0.38in;
          }
          .pickup {
            -webkit-print-color-adjust: exact;
            align-self: center;
            background: #000;
            color: #fff;
            display: inline-block;
            font-size: 0.3in;
            font-weight: 900;
            line-height: 1.05;
            margin-left: 0;
            padding: 0.06in 0.24in;
            print-color-adjust: exact;
            text-align: center;
          }
          .pickup-phone {
            align-self: center;
            font-size: 0.44in;
            font-weight: 800;
            letter-spacing: 0.01in;
            line-height: 1.1;
            margin-top: 0.35in;
          }
          .services {
            -webkit-print-color-adjust: exact;
            align-self: center;
            background: #000;
            color: #fff;
            display: inline-block;
            font-size: 0.38in;
            font-weight: 900;
            line-height: 1;
            padding: 0.06in 0.24in;
            print-color-adjust: exact;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <article class="${labelClass}">
          ${pickup
            ? `<div class="address">
                <strong class="name">${escapeHtml(location.name || '')}</strong>
                <div class="pickup">*PICKUP<br>ONLY*</div>
                ${location.phone ? `<span class="pickup-phone">${escapeHtml(location.phone)}</span>` : ''}
              </div>
              <div class="footer">
                ${services ? '<div class="services">*SERVICES*</div>' : ''}
              </div>`
            : `<div class="address">
                <strong class="name">${escapeHtml(location.name || '')}</strong>
                ${shippingAddressLines.map((line) => `<span>${escapeHtml(line)}</span>`).join('')}
                ${cityLine ? `<span class="break-before">${escapeHtml(cityLine)}</span>` : ''}
                ${location.phone ? `<span class="break-before">${escapeHtml(location.phone)}</span>` : ''}
              </div>
              <div class="footer">
                ${services ? '<div class="services">*SERVICES*</div>' : ''}
              </div>`}
        </article>
      </body>
    </html>
  `);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
    iframe.contentWindow.print();
    setTimeout(cleanup, 1000);
  }, 50);
};

function TypeaheadSelect({
  id,
  value,
  onChange,
  placeholder,
  options,
  openTypeahead,
  setOpenTypeahead,
  className = '',
  onEnter,
  disabled = false,
  renderOption,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const query = String(value || '').trim().toLowerCase();
  const compactQuery = normalizeLookupText(value);
  const getOptionLabel = (option) => (typeof option === 'string' ? option : option?.label || '');
  const getOptionSearchText = (option) => {
    if (typeof option === 'string') return option;
    return option?.searchText || option?.label || '';
  };
  const filteredOptions = options
    .filter((option) => {
      const searchText = getOptionSearchText(option).toLowerCase();
      const compactSearchText = normalizeLookupText(searchText);
      return !query || searchText.includes(query) || (compactQuery && compactSearchText.includes(compactQuery));
    })
    .slice(0, 60);
  const isOpen = openTypeahead === id;
  const menuId = `${id}-typeahead-menu`;

  const chooseOption = (option) => {
    if (disabled) return;
    setTooltip(null);
    onChange(getOptionLabel(option), option);
    setOpenTypeahead('');
    setActiveIndex(0);
  };

  const showTooltip = (event, lines = []) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      lines: lines.length > 0 ? lines : ['No address on file.'],
      top: rect.bottom + 6,
      left: rect.left,
    });
  };

  const hideTooltip = () => setTooltip(null);

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpenTypeahead(id);
      setActiveIndex((current) => (
        filteredOptions.length ? Math.min(current + 1, filteredOptions.length - 1) : 0
      ));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpenTypeahead(id);
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpenTypeahead('');
      setActiveIndex(0);
      setTooltip(null);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const selectedOption = isOpen ? filteredOptions[activeIndex] : '';
      if (selectedOption) {
        onChange(getOptionLabel(selectedOption), selectedOption);
      }
      setOpenTypeahead('');
      setActiveIndex(0);
      if (onEnter) {
        onEnter(e, selectedOption ? getOptionLabel(selectedOption) : value, selectedOption || null);
      }
      return;
    }

    if (e.key === 'Tab' && isOpen && filteredOptions[activeIndex]) {
      chooseOption(filteredOptions[activeIndex]);
    }
  };

  return (
    <div className={`transfer-typeahead ${className} ${isOpen ? 'open' : ''}`}>
      <input
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          if (disabled) return;
          onChange(e.target.value);
          setOpenTypeahead(id);
          setActiveIndex(0);
        }}
        onFocus={() => {
          if (disabled) return;
          setOpenTypeahead(id);
          setActiveIndex(0);
        }}
        onBlur={() => setTimeout(() => {
          setOpenTypeahead('');
          setTooltip(null);
        }, 120)}
        onKeyDown={handleKeyDown}
        className="transfer-filter-input"
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        disabled={disabled}
      />
      {!disabled && isOpen && filteredOptions.length > 0 && (
        <div className="transfer-typeahead-menu" id={menuId} role="listbox">
          {filteredOptions.map((option, index) => (
            <button
              type="button"
              key={`${getOptionLabel(option)}-${index}`}
              className={index === activeIndex ? 'active' : ''}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                chooseOption(option);
              }}
              role="option"
              aria-selected={index === activeIndex}
            >
              {renderOption ? renderOption(option, { show: showTooltip, hide: hideTooltip }) : getOptionLabel(option)}
            </button>
          ))}
        </div>
      )}
      {tooltip && (
        <div
          className="transfer-customer-address-tooltip"
          role="tooltip"
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.lines.map((line) => <span key={line}>{line}</span>)}
        </div>
      )}
    </div>
  );
}

const TransferListFilterDropdown = ({
  filterKey,
  filterLabel,
  options,
  appliedSelection,
  hasTypeahead,
  onApply,
}) => {
  const maxRenderedOptions = hasTypeahead ? 120 : 250;
  const [search, setSearch] = useState('');
  const [draftSelection, setDraftSelection] = useState(() => (
    appliedSelection ? [...appliedSelection] : null
  ));

  useEffect(() => {
    setSearch('');
    setDraftSelection(appliedSelection ? [...appliedSelection] : null);
  }, [appliedSelection, filterKey]);

  const selected = draftSelection || options;
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleOptions = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    if (!hasTypeahead || !searchText) return options;
    return options.filter((value) => String(value || '-').toLowerCase().includes(searchText));
  }, [hasTypeahead, options, search]);
  const renderedOptions = visibleOptions.slice(0, maxRenderedOptions);
  const hiddenOptionCount = Math.max(0, visibleOptions.length - renderedOptions.length);
  const hasExplicitSelection = Array.isArray(draftSelection);
  const allOptionsSelected = !hasExplicitSelection || (
    options.length > 0 && options.every((value) => selectedSet.has(value))
  );

  const toggleAll = () => {
    setDraftSelection(allOptionsSelected ? [] : null);
  };

  const toggleValue = (value) => {
    const nextSelected = draftSelection || options;
    setDraftSelection(
      selectedSet.has(value)
        ? nextSelected.filter((item) => item !== value)
        : [...nextSelected, value]
    );
  };

  const applyDraft = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const nextSelection = draftSelection
      ? [...draftSelection]
      : null;
    onApply(filterKey, nextSelection);
  };

  return (
    <div
      className="filter-dropdown"
      onClick={(event) => event.stopPropagation()}
    >
      {hasTypeahead && (
        <input
          type="search"
          className="transfer-list-filter-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Search ${filterLabel}`}
          aria-label={`Search ${filterLabel} filter options`}
          autoComplete="off"
          autoFocus
        />
      )}
      <div className="filter-actions">
        <button
          type="button"
          className="transfer-list-filter-action"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleAll();
          }}
        >
          {allOptionsSelected ? 'Unselect All' : 'Select All'}
        </button>
        <button type="button" className="transfer-list-filter-action" onClick={applyDraft}>Apply</button>
      </div>
      <div className="filter-options">
        {renderedOptions.map((value) => (
          <label key={value || '__blank__'} className="filter-option">
            <input
              type="checkbox"
              checked={selectedSet.has(value)}
              onChange={() => toggleValue(value)}
            />
            <span className="filter-value">{value || '-'}</span>
          </label>
        ))}
        {visibleOptions.length === 0 && (
          <div className="transfer-list-filter-empty">No matches</div>
        )}
        {hiddenOptionCount > 0 && (
          <div className="transfer-list-filter-empty">
            {hiddenOptionCount.toLocaleString()} more. Keep typing to narrow results.
          </div>
        )}
      </div>
    </div>
  );
};

function TransfersPage({ mode = 'transfers', pageTitle = 'Transfers' } = {}) {
  const { user } = useAuth();
  const salesOnly = mode === 'sales';
  const canOpenCreateCustomer = hasPermission(user, 'customers.create');
  const canCreateRetailCustomer = can(user, 'customers.create', { customerTypeName: 'Retail', typeName: 'Retail' });
  const canCreateWholesaleCustomer = can(user, 'customers.create', { customerTypeName: 'Wholesale', typeName: 'Wholesale' });
  const canEditInvoice = can(user, 'invoices.edit');
  const canCreateTransfer = can(user, 'transfers.create');
  const canDeleteTransfer = can(user, 'transfers.delete');
  const canOpenCreateSale = hasPermission(user, 'sales.create');
  const canDeleteSale = can(user, 'sales.delete');
  const canOpenCreateReturn = hasPermission(user, 'returns.create');
  const canEditInventory = can(user, 'inventory.edit');
  const userRole = String(user?.role || '').toLowerCase();
  const isManagerUser = ['admin', 'super'].includes(userRole) || getPageAuthority(user, 'transfers') === 'elevated';
  const requestedSearchTab = new URLSearchParams(window.location.search).get('tab');
  const initialSearchTab = !salesOnly && ['new', 'transfers', 'serial', 'adjustments', 'location', 'doc', 'vendor'].includes(requestedSearchTab)
    ? requestedSearchTab
    : (salesOnly ? 'sale' : 'new');
  const newSaleSerialRef = useRef(null);
  const newTransferSerialRef = useRef(null);
  const serialSearchRef = useRef(null);
  const skipNewTransferSerialBlurLookupRef = useRef(false);
  const lastNewTransferNotFoundSerialRef = useRef('');
  const highlightedTransferRowRef = useRef(null);
  const skipTransactionUnloadPromptRef = useRef(false);
  const initialTransferListLoadRef = useRef(false);
  const currentYear = String(new Date().getFullYear());
  const [docNo, setDocNo] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [newTransferSerial, setNewTransferSerial] = useState('');
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activeSearchTab, setActiveSearchTab] = useState(initialSearchTab);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [newTransferSerialInfo, setNewTransferSerialInfo] = useState(null);
  const [newTransferFrom, setNewTransferFrom] = useState('');
  const [newTransferTo, setNewTransferTo, newTransferToId] = useTransferDestination();
  const [newTransferMemo, setNewTransferMemo] = useState('');
  const [newTransferQueue, setNewTransferQueue] = useState([]);
  const [newTransferPrintOptions, setNewTransferPrintOptions] = useState({
    invoice: true,
    shippingLabel: true,
    pickupLabel: false,
  });
  const [completedTransferPrintJob, setCompletedTransferPrintJob] = useState(null);
  const [completedTransferPrintView, setCompletedTransferPrintView] = useState('invoice');
  const [openedTransferPrintView, setOpenedTransferPrintView] = useState('invoice');
  const [customers, setCustomers] = useState([]);
  const [transferLimitsModal, setTransferLimitsModal] = useState(null);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [newSaleFrom, setNewSaleFrom] = useState('');
  const [newSaleCustomer, setNewSaleCustomer] = useState('');
  const [newSaleShippingAddress, setNewSaleShippingAddress] = useState('billing');
  const [newSaleSerial, setNewSaleSerial] = useState('');
  const [newSaleSerialInfo, setNewSaleSerialInfo] = useState(null);
  const [newSaleMemo, setNewSaleMemo] = useState('');
  const [newSaleQueue, setNewSaleQueue] = useState([]);
  const [newSaleTaxRate, setNewSaleTaxRate] = useState('0');
  const [preferencesTaxRate, setPreferencesTaxRate] = useState(0);
  const [retailCustomerModalOpen, setRetailCustomerModalOpen] = useState(false);
  const [newRetailCustomer, setNewRetailCustomer] = useState({ companyname: '', phone: '', email: '' });
  const [retailCustomerStatus, setRetailCustomerStatus] = useState('');
  const [retailDuplicatePhoneCustomers, setRetailDuplicatePhoneCustomers] = useState([]);
  const [retailCustomerSaving, setRetailCustomerSaving] = useState(false);
  const [customerReturnCustomer, setCustomerReturnCustomer] = useState('');
  const [customerReturnLocation, setCustomerReturnLocation] = useState(DEFAULT_TRANSFER_FROM_LOCATION);
  const [customerReturnSerial, setCustomerReturnSerial] = useState('');
  const [customerReturnMemo, setCustomerReturnMemo] = useState('');
  const [customerReturnQueue, setCustomerReturnQueue] = useState([]);
  const [customerReturnLocationChoice, setCustomerReturnLocationChoice] = useState({
    open: false,
    customer: null,
    customerLocationName: '',
  });
  const [removeConfirm, setRemoveConfirm] = useState({
    open: false,
    row: null,
    value: '',
    error: '',
  });
  const [locationSwitchConfirm, setLocationSwitchConfirm] = useState({
    open: false,
    row: null,
    serial: '',
    selectedLocation: '',
    currentLocation: '',
    value: '',
    error: '',
  });
  const [serialChoiceModal, setSerialChoiceModal] = useState({
    open: false,
    serial: '',
    rows: [],
    source: null,
    destination: null,
    preserveSerialInfo: false,
  });
  const [managerAdjustmentModal, setManagerAdjustmentModal] = useState({
    open: false,
    serial: '',
  });
  const [deleteTransferConfirm, setDeleteTransferConfirm] = useState({
    open: false,
    row: null,
    value: '',
    error: '',
  });
  const [serialDestination, setSerialDestination, serialDestinationId] = useTransferDestination();
  const [adjustmentSerial, setAdjustmentSerial] = useState('');
  const [adjustmentSource, setAdjustmentSource] = useState(null);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [adjustmentItemQuery, setAdjustmentItemQuery] = useState('');
  const [adjustmentItemResults, setAdjustmentItemResults] = useState([]);
  const [adjustmentTargetItem, setAdjustmentTargetItem] = useState(null);
  const [adjustmentMemo, setAdjustmentMemo] = useState('');
  const [adjustmentList, setAdjustmentList] = useState([]);
  const [adjustmentDatePreset, setAdjustmentDatePreset] = useState(DEFAULT_ADJUSTMENT_DATE_PRESET);
  const [adjustmentDateRange, setAdjustmentDateRange] = useState(() => getDatePresetRange(DEFAULT_ADJUSTMENT_DATE_PRESET));
  const [adjustmentListError, setAdjustmentListError] = useState('');
  const [openTypeahead, setOpenTypeahead] = useState('');
  const [lastSearchFilters, setLastSearchFilters] = useState({ vendor: '', location: '' });
  const [vendorResults, setVendorResults] = useState([]);
  const [vendorYear, setVendorYear] = useState(currentYear);
  const [vendorYears, setVendorYears] = useState([]);
  const [transferListResults, setTransferListResults] = useState([]);
  const [transferListSearch, setTransferListSearch] = useState('');
  const [transferListYear, setTransferListYear] = useState(currentYear);
  const [transferListDateRange, setTransferListDateRange] = useState(getDefaultTransferListDateRange);
  const [appliedTransferListDateRange, setAppliedTransferListDateRange] = useState(getDefaultTransferListDateRange);
  const [transferListFilters, setTransferListFilters] = useState({
    type: null,
    from: null,
    to: null,
  });
  const [openTransferListFilter, setOpenTransferListFilter] = useState('');
  const [salesResults, setSalesResults] = useState([]);
  const [salesSearch, setSalesSearch] = useState('');
  const [salesYear, setSalesYear] = useState(currentYear);
  const [serialResults, setSerialResults] = useState([]);
  const [selectedSerialRowIds, setSelectedSerialRowIds] = useState([]);
  const [transfer, setTransfer] = useState(null);
  const [transferHighlightSerial, setTransferHighlightSerial] = useState('');
  const [completedTransferInvoice, setCompletedTransferInvoice] = useState(null);
  const [completedTransferHighlightSerial, setCompletedTransferHighlightSerial] = useState('');
  const [completedRepairPrintView, setCompletedRepairPrintView] = useState('invoice');
  const [shippingInvoiceEditor, setShippingInvoiceEditor] = useState({
    open: false,
    target: '',
    docNo: '',
    draft: null,
    saving: false,
    error: '',
  });
  const [shippingInvoiceCustomerLookupOpen, setShippingInvoiceCustomerLookupOpen] = useState(false);
  const [shippingInvoiceCustomerLookupIndex, setShippingInvoiceCustomerLookupIndex] = useState(0);
  const [serialPopup, setSerialPopup] = useState(null);
  const [selectedPopupSerials, setSelectedPopupSerials] = useState([]);
  const [popupSerialDestination, setPopupSerialDestination, popupSerialDestinationId] = useTransferDestination();
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    message: '',
    focusTargetId: '',
  });
  const [loading, setLoading] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [serialLoading, setSerialLoading] = useState(false);
  const [serialTransferLoading, setSerialTransferLoading] = useState(false);
  const [transferListLoading, setTransferListLoading] = useState(false);
  const [saleLoading, setSaleLoading] = useState(false);
  const [salesListLoading, setSalesListLoading] = useState(false);
  const [customerReturnLoading, setCustomerReturnLoading] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [adjustmentItemLoading, setAdjustmentItemLoading] = useState(false);
  const [adjustmentListLoading, setAdjustmentListLoading] = useState(false);
  const [transferDeleteLoading, setTransferDeleteLoading] = useState(false);
  const [invoiceEmailLoading, setInvoiceEmailLoading] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState({
    open: false,
    onConfirm: () => {},
  });

  const queuedTransactionCount = salesOnly ? newSaleQueue.length : newTransferQueue.length;

  const clearQueuedTransaction = useCallback(() => {
    if (salesOnly) {
      setNewSaleQueue([]);
    } else {
      setNewTransferQueue([]);
    }
  }, [salesOnly]);

  const confirmLeavingTransaction = useCallback((onConfirm) => {
    setLeaveConfirm({ open: true, onConfirm });
  }, []);

  useEffect(() => {
    document.title = `${pageTitle} - Services Irene's Wigs.com`;
  }, [pageTitle]);

  useEffect(() => {
    if (queuedTransactionCount === 0) return undefined;

    const handleBeforeUnload = (event) => {
      if (skipTransactionUnloadPromptRef.current) return undefined;
      event.preventDefault();
      event.returnValue = 'Current Transaction will be lost.';
      return event.returnValue;
    };

    const handlePageLinkClick = (event) => {
      const link = event.target.closest?.('a');
      if (!link || !link.href) return;
      if (link.target && link.target !== '_self') return;
      if (link.origin !== window.location.origin) return;

      event.preventDefault();
      confirmLeavingTransaction(() => {
        skipTransactionUnloadPromptRef.current = true;
        clearQueuedTransaction();
        window.location.assign(link.href);
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handlePageLinkClick, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handlePageLinkClick, true);
    };
  }, [clearQueuedTransaction, confirmLeavingTransaction, queuedTransactionCount]);

  const setStatus = useCallback((message, focusTargetId = '') => {
    if (message) {
      setStatusDialog({
        open: true,
        message,
        focusTargetId,
      });
    } else {
      setStatusDialog({
        open: false,
        message: '',
        focusTargetId: '',
      });
    }
  }, []);

  const closeStatusDialog = () => {
    const focusTargetId = statusDialog.focusTargetId;
    const shouldClearSaleSerial = salesOnly && Boolean(statusDialog.message);
    const shouldClearTransferSerial = !salesOnly && activeSearchTab === 'new' && Boolean(statusDialog.message);
    const shouldClearSerialSearch = !salesOnly && activeSearchTab === 'serial' && Boolean(statusDialog.message);
    const shouldClearAdjustmentSearch = !salesOnly && activeSearchTab === 'adjustments' && Boolean(statusDialog.message);
    setStatusDialog({
      open: false,
      message: '',
      focusTargetId: '',
    });
    if (shouldClearSaleSerial) {
      setNewSaleSerial('');
      setNewSaleSerialInfo(null);
      setTimeout(() => newSaleSerialRef.current?.focus(), 0);
      return;
    }
    if (shouldClearTransferSerial) {
      setNewTransferSerial('');
      setNewTransferSerialInfo(null);
      setTimeout(() => newTransferSerialRef.current?.focus(), 0);
      return;
    }
    if (shouldClearSerialSearch) {
      setSerialNo('');
      setSerialResults([]);
      setSelectedSerialRowIds([]);
      setSerialPopup(null);
      setTimeout(() => serialSearchRef.current?.focus(), 0);
      return;
    }
    if (shouldClearAdjustmentSearch) {
      setAdjustmentSerial('');
      setAdjustmentSource(null);
      setAdjustmentHistory([]);
      setAdjustmentTargetItem(null);
      setAdjustmentItemQuery('');
      setAdjustmentItemResults([]);
      setAdjustmentMemo('');
      setTimeout(() => document.getElementById(ADJUSTMENT_SERIAL_SEARCH_INPUT_ID)?.focus(), 0);
      return;
    }
    if (focusTargetId) {
      setTimeout(() => document.getElementById(focusTargetId)?.focus(), 0);
      return;
    }
    if (activeSearchTab === 'new') {
      setTimeout(() => newTransferSerialRef.current?.focus(), 0);
    }
  };

  const salesYearOptions = Array.from({ length: Number(currentYear) - 2014 + 1 }, (_, index) => String(Number(currentYear) - index));

  const changeSearchTab = (tab) => {
    const refreshListTab = () => {
      if (tab === 'transfers') {
        loadTransferList(transferListSearch, transferListYear).catch((err) => {
          console.error('Failed to load transfers:', err);
        });
      }
      if (tab === 'sales') {
        loadSales(salesSearch, salesYear).catch((err) => {
          console.error('Failed to load sales:', err);
        });
      }
    };

    const switchTab = () => {
      if (salesOnly && tab !== activeSearchTab) {
        setStatus('');
      }
      setActiveSearchTab(tab);
      if (!['location', 'vendor'].includes(tab)) {
        setVendorResults([]);
      }
      refreshListTab();
    };

    if (tab !== activeSearchTab && queuedTransactionCount > 0) {
      confirmLeavingTransaction(() => {
        clearQueuedTransaction();
        setLeaveConfirm({ open: false, onConfirm: () => {} });
        switchTab();
      });
      return;
    }
    switchTab();
  };

  const loadTransferList = useCallback(async (query = '', year = currentYear, { clearStatus = true, dateRange = null } = {}) => {
    setTransferListLoading(true);
    if (clearStatus) {
      setStatus('');
    }
    try {
      const res = await api.get('/transfers/list', { params: { q: query, year } });
      setTransferListResults(res.data || []);
      if (dateRange) {
        setAppliedTransferListDateRange(dateRange);
      }
    } catch (err) {
      console.error('Failed to load transfers:', err);
      setStatus(err.response?.data?.error || 'Failed to load transfers.');
    } finally {
      setTransferListLoading(false);
    }
  }, [currentYear, setStatus]);

  const searchTransferList = async (e) => {
    e?.preventDefault();
    await loadTransferList(transferListSearch, transferListYear, { dateRange: transferListDateRange });
  };

  useEffect(() => {
    if (initialTransferListLoadRef.current || salesOnly || initialSearchTab !== 'transfers') return;
    initialTransferListLoadRef.current = true;
    loadTransferList(transferListSearch, transferListYear, {
      clearStatus: false,
      dateRange: transferListDateRange,
    }).catch((err) => {
      console.error('Failed to load transfers:', err);
    });
  }, [initialSearchTab, loadTransferList, salesOnly, transferListDateRange, transferListSearch, transferListYear]);

  const loadSales = useCallback(async (query = '', year = currentYear, { clearStatus = true } = {}) => {
    setSalesListLoading(true);
    if (clearStatus) {
      setStatus('');
    }
    try {
      const res = await api.get('/transfers/sales', { params: { q: query, year } });
      setSalesResults(res.data || []);
    } catch (err) {
      console.error('Failed to load sales:', err);
      setStatus(err.response?.data?.error || 'Failed to load sales.');
    } finally {
      setSalesListLoading(false);
    }
  }, [currentYear, setStatus]);

  const searchSales = async (e) => {
    e?.preventDefault();
    await loadSales(salesSearch, salesYear);
  };

  const loadAdjustmentList = useCallback(async (range = adjustmentDateRange) => {
    setAdjustmentListLoading(true);
    setAdjustmentListError('');
    try {
      const params = {};
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const res = await api.get('/transfers/adjustments', { params });
      setAdjustmentList(res.data || []);
    } catch (err) {
      console.error('Failed to load adjustments:', err);
      setAdjustmentList([]);
      setAdjustmentListError(err.response?.data?.error || 'Failed to load adjustments.');
    } finally {
      setAdjustmentListLoading(false);
    }
  }, [adjustmentDateRange]);

  const changeAdjustmentDatePreset = async (preset) => {
    const range = getDatePresetRange(preset);
    setAdjustmentDatePreset(preset);
    setAdjustmentDateRange(range);
    await loadAdjustmentList(range);
  };

  const dateFilteredTransferListResults = useMemo(() => (
    transferListResults.filter((row) => {
      const hasDateFilter = Boolean(appliedTransferListDateRange.start || appliedTransferListDateRange.end);
      const dateKey = formatDateInput(row.transferDate);
      if (!hasDateFilter) return true;
      if (!dateKey) return false;
      if (appliedTransferListDateRange.start && dateKey < appliedTransferListDateRange.start) return false;
      if (appliedTransferListDateRange.end && dateKey > appliedTransferListDateRange.end) return false;
      return true;
    })
  ), [appliedTransferListDateRange.end, appliedTransferListDateRange.start, transferListResults]);
  const transferListFilterSets = useMemo(() => (
    TRANSFER_LIST_FILTER_KEYS.reduce((sets, filterKey) => ({
      ...sets,
      [filterKey]: transferListFilters[filterKey] ? new Set(transferListFilters[filterKey]) : null,
    }), {})
  ), [transferListFilters]);
  const rowMatchesTransferListFilters = useCallback((row, ignoredFilterKey = '') => (
    TRANSFER_LIST_FILTER_KEYS.every((filterKey) => {
      if (filterKey === ignoredFilterKey) return true;
      const selected = transferListFilterSets[filterKey];
      if (!selected) return true;
      return selected.has(getTransferListFilterValue(row, filterKey));
    })
  ), [transferListFilterSets]);
  const transferListFilterOptions = useMemo(() => (
    TRANSFER_LIST_FILTER_KEYS.reduce((optionsByKey, filterKey) => ({
      ...optionsByKey,
      [filterKey]: [
        ...new Set(
          dateFilteredTransferListResults
            .filter((row) => rowMatchesTransferListFilters(row, filterKey))
            .map((row) => getTransferListFilterValue(row, filterKey))
        ),
      ].sort((a, b) => a.localeCompare(b)),
    }), {})
  ), [dateFilteredTransferListResults, rowMatchesTransferListFilters]);
  const filteredTransferListResults = useMemo(() => (
    dateFilteredTransferListResults.filter((row) => rowMatchesTransferListFilters(row))
  ), [dateFilteredTransferListResults, rowMatchesTransferListFilters]);

  const applyTransferListFilter = (filterKey, selection) => {
    setTransferListFilters((current) => ({
      ...current,
      [filterKey]: selection,
    }));
    setOpenTransferListFilter('');
  };

  const openTransferListFilterMenu = (filterKey) => {
    setOpenTransferListFilter((current) => {
      return current === filterKey ? '' : filterKey;
    });
  };

  const renderTransferListFilterHeader = (filterKey) => {
    const options = transferListFilterOptions[filterKey];
    const isOpen = openTransferListFilter === filterKey;
    const filterLabel = TRANSFER_LIST_FILTER_LABELS[filterKey];
    const hasTypeahead = ['from', 'to'].includes(filterKey);

    return (
      <th
        className={`filter-header transfer-list-filter-header transfer-list-${filterKey}-filter ${isOpen ? 'filter-open' : ''}`}
        onClick={() => openTransferListFilterMenu(filterKey)}
      >
        <div className="inventory-filter-trigger">
          <span>{filterLabel}</span>
          <button type="button" className="filter-icon-button" aria-label={`Filter ${filterLabel}`}>
            <svg viewBox="0 0 24 24" aria-hidden="true" className="filter-icon">
              <path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" />
            </svg>
          </button>
          {isOpen && (
            <TransferListFilterDropdown
              filterKey={filterKey}
              filterLabel={filterLabel}
              options={options}
              appliedSelection={transferListFilters[filterKey]}
              hasTypeahead={hasTypeahead}
              onApply={applyTransferListFilter}
            />
          )}
        </div>
      </th>
    );
  };

  const hasTransferListDateFilter = Boolean(
    transferListDateRange.start || transferListDateRange.end !== getTodayDateInputValue()
  );
  const transferListDateRangeChanged = (
    transferListDateRange.start !== appliedTransferListDateRange.start
    || transferListDateRange.end !== appliedTransferListDateRange.end
  );

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [vendorsRes, locationsRes, customersRes, customerTypesRes, preferencesRes] = await Promise.all([
          api.get('/vendors'),
          api.get('/locations'),
          api.get('/customers'),
          api.get('/customers/types'),
          api.get('/preferences'),
        ]);
        const loadedLocations = locationsRes.data || [];
        setVendors(vendorsRes.data || []);
        setLocations(loadedLocations);
        const defaultTransferFrom = loadedLocations.find((location) => (
          location.active !== false
          && location.deleted !== true
          && normalizeLocationName(location.name) === normalizeLocationName(DEFAULT_TRANSFER_FROM_LOCATION)
        ));
        setCustomerReturnLocation((current) => (
          !current || normalizeLocationName(current) === normalizeLocationName(DEFAULT_TRANSFER_FROM_LOCATION)
            ? defaultTransferFrom?.name || DEFAULT_TRANSFER_FROM_LOCATION
            : current
        ));
        setCustomers(customersRes.data || []);
        setCustomerTypes(customerTypesRes.data || []);
        setPreferencesTaxRate(Number(preferencesRes.data?.taxRate || 0));
      } catch (err) {
        console.error('Failed to load transfer filters:', err);
        setStatus('Failed to load vendor/location filters.');
      }
    };

    loadOptions();
  }, [setStatus]);

  const loadCustomers = async () => {
    const response = await api.get('/customers');
    const rows = response.data || [];
    setCustomers(rows);
    return rows;
  };

  useEffect(() => {
    if (!salesOnly || activeSearchTab !== 'sales') return undefined;
    const delay = salesSearch.trim() ? 250 : 0;
    const timer = setTimeout(() => {
      loadSales(salesSearch, salesYear).catch((err) => {
        console.error('Failed to load sales:', err);
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [activeSearchTab, loadSales, salesOnly, salesSearch, salesYear]);

  useEffect(() => {
    if (salesOnly || activeSearchTab !== 'adjustments') return;
    loadAdjustmentList().catch((err) => {
      console.error('Failed to load adjustments:', err);
    });
  }, [activeSearchTab, loadAdjustmentList, salesOnly]);

  useEffect(() => {
    if (activeSearchTab !== 'adjustments') return undefined;
    const query = adjustmentItemQuery.trim();
    if (query.length < 2 || adjustmentTargetItem) {
      setAdjustmentItemResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setAdjustmentItemLoading(true);
      try {
        const res = await api.get('/transfers/adjustments/items', { params: { q: query } });
        setAdjustmentItemResults(res.data || []);
      } catch (err) {
        console.error('Failed to search adjustment items:', err);
        setAdjustmentItemResults([]);
      } finally {
        setAdjustmentItemLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [activeSearchTab, adjustmentItemQuery, adjustmentTargetItem]);

  useEffect(() => {
    setNewSaleShippingAddress('billing');
  }, [newSaleCustomer]);

  const loadTransfer = async (
    e,
    docOverride = '',
    { preserveSerialResults = false, transactionTypeId = null, highlightSerial = '' } = {}
  ) => {
    e?.preventDefault();
    const query = String(docOverride || docNo).trim();
    if (!query) {
      setStatus('Enter a doc number.');
      return;
    }

    setLoading(true);
    setStatus('');
    setTransfer(null);
    setSerialPopup(null);
    if (!preserveSerialResults) {
      setSerialResults([]);
      setSelectedSerialRowIds([]);
    }

    try {
      const res = await api.get(`/transfers/${query}`, {
        params: transactionTypeId ? { transactionTypeId } : {},
      });
      let transferData = res.data;
      if (Number(transferData?.transactionTypeId ?? transactionTypeId) === 2) {
        const invoiceRes = await api.get('/invoices/' + encodeURIComponent(query), {
          params: { transactionTypeId: 2 },
        });
        setCompletedTransferInvoice(invoiceRes.data);
        setCompletedTransferHighlightSerial(highlightSerial);
        setDocNo(String(invoiceRes.data.invoiceNo || query));
        return;
      }
      if (Number(transferData?.transactionTypeId ?? transactionTypeId) === 4 && !documentMemoText(transferData)) {
        try {
          const invoiceRes = await api.get(`/invoices/${encodeURIComponent(query)}`, {
            params: { transactionTypeId: 4 },
          });
          const memo = documentMemoText(invoiceRes.data);
          if (memo) {
            transferData = {
              ...transferData,
              memo,
              comment: memo,
            };
          }
        } catch (invoiceError) {
          console.warn('Could not load invoice memo fallback:', invoiceError);
        }
      }
      setTransfer(transferData);
      setOpenedTransferPrintView('invoice');
      setTransferHighlightSerial(highlightSerial);
      setDocNo(String(transferData.docNo || query));
    } catch (err) {
      console.error('Failed to load transfer:', err);
      setStatus(err.response?.status === 404 ? 'No transfer doc found.' : 'Failed to load transfer doc.');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceDetail = async (
    docOverride = '',
    { preserveSerialResults = false, transactionTypeId = null, highlightSerial = '' } = {}
  ) => {
    const query = String(docOverride || '').trim();
    if (!query) {
      setStatus('Enter a doc number.');
      return;
    }

    if (Number(transactionTypeId) === REPAIR_ORDER_TRANSACTION_TYPE_ID) {
      setLoading(true);
      setStatus('');
      setTransfer(null);
      setSerialPopup(null);
      if (!preserveSerialResults) {
        setSerialResults([]);
        setSelectedSerialRowIds([]);
      }

      try {
        const repairInvoiceId = /^RO-/i.test(query) ? query : `RO-${query}`;
        const res = await api.get(`/invoices/${repairInvoiceId}`);
        setCompletedRepairPrintView('invoice');
        setCompletedTransferInvoice(res.data);
        setCompletedTransferHighlightSerial(highlightSerial);
        setDocNo(String(res.data.invoiceNo || repairInvoiceId));
      } catch (err) {
        console.error('Failed to load repair invoice:', err);
        setStatus(err.response?.status === 404 ? 'No repair invoice found.' : 'Failed to load repair invoice.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (transactionTypeId && Number(transactionTypeId) !== 5) {
      await loadTransfer(null, query, { preserveSerialResults, transactionTypeId, highlightSerial });
      return;
    }

    setLoading(true);
    setStatus('');
    setTransfer(null);
    setSerialPopup(null);
    if (!preserveSerialResults) {
      setSerialResults([]);
      setSelectedSerialRowIds([]);
    }

    try {
      const res = await api.get(`/invoices/${query}`);
      setCompletedTransferInvoice(res.data);
      setCompletedTransferHighlightSerial(highlightSerial);
      setDocNo(String(res.data.invoiceNo || query));
    } catch (err) {
      if (err.response?.status === 404) {
        await loadTransfer(null, query, { preserveSerialResults, transactionTypeId, highlightSerial });
        return;
      }
      console.error('Failed to load invoice detail:', err);
      setStatus('Failed to load invoice detail.');
    } finally {
      setLoading(false);
    }
  };

  const openShippingInvoiceEditor = (target, documentValue) => {
    if (!canEditInvoice) {
      setStatus('You do not have permission to edit invoices.');
      return;
    }
    const draft = shippingInvoiceEditDraftFromDocument(documentValue, preferencesTaxRate);
    if (!draft.docNo) return;
    const documentCustomerId = documentValue?.customerId || documentValue?.customer_id || documentValue?.customer?.id || '';
    const draftCustomer = customers.find((customer) => String(getCustomerId(customer)) === String(documentCustomerId))
      || (documentValue?.customerName ? findCustomerOption(documentValue.customerName) : null)
      || (documentValue?.billTo?.name ? findCustomerOption(documentValue.billTo.name) : null)
      || null;
    setShippingInvoiceEditor({
      open: true,
      target,
      docNo: draft.docNo,
      draft: {
        ...draft,
        customer: draftCustomer,
        customerId: getCustomerId(draftCustomer) || documentCustomerId,
        customerQuery: draftCustomer ? getCustomerDisplayName(draftCustomer) : (documentValue?.customerName || documentValue?.billTo?.name || ''),
        customerName: draftCustomer ? getCustomerDisplayName(draftCustomer) : (documentValue?.customerName || documentValue?.billTo?.name || ''),
        billTo: draftCustomer ? getCustomerInvoiceAddress(draftCustomer) : (documentValue?.billTo || documentValue?.to || null),
      },
      saving: false,
      error: '',
    });
    setShippingInvoiceCustomerLookupOpen(false);
    setShippingInvoiceCustomerLookupIndex(0);
  };

  const cancelShippingInvoiceEditor = () => {
    setShippingInvoiceEditor({ open: false, target: '', docNo: '', draft: null, saving: false, error: '' });
    setShippingInvoiceCustomerLookupOpen(false);
    setShippingInvoiceCustomerLookupIndex(0);
  };

  const selectShippingInvoiceCustomer = (customer) => {
    setShippingInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? {
        ...current.draft,
        customer,
        customerId: getCustomerId(customer),
        customerQuery: getCustomerDisplayName(customer),
        customerName: getCustomerDisplayName(customer),
        billTo: getCustomerInvoiceAddress(customer),
      } : current.draft,
    }));
    setShippingInvoiceCustomerLookupOpen(false);
    setShippingInvoiceCustomerLookupIndex(0);
  };

  const updateShippingInvoiceCustomerQuery = (value) => {
    setShippingInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? {
        ...current.draft,
        customer: null,
        customerId: '',
        customerQuery: value,
      } : current.draft,
    }));
    setShippingInvoiceCustomerLookupOpen(true);
    setShippingInvoiceCustomerLookupIndex(0);
  };

  const handleShippingInvoiceCustomerLookupKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setShippingInvoiceCustomerLookupOpen(true);
      setShippingInvoiceCustomerLookupIndex((index) => Math.min(index + 1, Math.max(shippingInvoiceCustomerLookupOptions.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setShippingInvoiceCustomerLookupIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setShippingInvoiceCustomerLookupOpen(false);
    } else if (event.key === 'Enter' && shippingInvoiceCustomerLookupOptions[shippingInvoiceCustomerLookupIndex]) {
      event.preventDefault();
      selectShippingInvoiceCustomer(shippingInvoiceCustomerLookupOptions[shippingInvoiceCustomerLookupIndex]);
    } else if (event.key === 'Tab' && shippingInvoiceCustomerLookupOpen && shippingInvoiceCustomerLookupOptions[shippingInvoiceCustomerLookupIndex]) {
      selectShippingInvoiceCustomer(shippingInvoiceCustomerLookupOptions[shippingInvoiceCustomerLookupIndex]);
    }
  };

  const updateShippingInvoiceDraftItem = (itemId, field, value) => {
    setShippingInvoiceEditor((current) => {
      if (!current.draft) return current;
      const items = current.draft.items.map((item) => {
        if (String(item.id) !== String(itemId)) return item;
        const quantity = Math.abs(Number(item.quantity) || 0) || 1;
        if (field === 'amount') {
          const amountNumber = Number(value);
          return {
            ...item,
            amount: value,
            rate: Number.isFinite(amountNumber) ? String(roundInvoiceMoney(amountNumber / quantity).toFixed(2)) : item.rate,
          };
        }
        const rateNumber = Number(value);
        return {
          ...item,
          rate: value,
          amount: Number.isFinite(rateNumber) ? String(roundInvoiceMoney(rateNumber * quantity).toFixed(2)) : item.amount,
        };
      });
      return {
        ...current,
        error: '',
        draft: {
          ...current.draft,
          items,
        },
      };
    });
  };

  const updateShippingInvoiceDraftMemo = (memo) => {
    setShippingInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? { ...current.draft, memo } : current.draft,
    }));
  };

  const updateShippingInvoiceDraftTaxRate = (taxRate) => {
    setShippingInvoiceEditor((current) => ({
      ...current,
      error: '',
      draft: current.draft ? { ...current.draft, taxRate } : current.draft,
    }));
  };

  const saveShippingInvoiceEditor = async () => {
    if (!shippingInvoiceEditor.docNo || !shippingInvoiceEditor.draft) return;
    if (!canEditInvoice) {
      setShippingInvoiceEditor((current) => ({ ...current, error: 'You do not have permission to edit invoices.' }));
      return;
    }
    const targetCustomerId = shippingInvoiceEditor.draft.customerId || getCustomerId(shippingInvoiceEditor.draft.customer);
    if (!targetCustomerId) {
      setShippingInvoiceEditor((current) => ({ ...current, error: 'Select a customer from the Bill To lookup.' }));
      return;
    }
    setShippingInvoiceEditor((current) => ({ ...current, saving: true, error: '' }));
    setStatus('');
    try {
      const payloadItems = shippingInvoiceEditor.draft.items
        .filter((item) => !item.syntheticQuickBooksLine)
        .map((item) => ({
          id: item.id,
          lineNo: item.lineNo,
          itemId: item.itemId,
          shipDocItemIds: item.shipDocItemIds,
          quantity: item.quantity,
          amount: item.amount,
          rate: item.rate,
        }));
      const res = await api.patch(`/invoices/${shippingInvoiceEditor.docNo}`, {
        customerId: targetCustomerId,
        items: payloadItems,
        memo: shippingInvoiceEditor.draft.memo,
        taxRate: shippingInvoiceEditor.draft.taxRate,
      });
      if (shippingInvoiceEditor.target === 'transfer') {
        const refreshed = await api.get(`/transfers/${shippingInvoiceEditor.docNo}`, {
          params: { transactionTypeId: 4 },
        });
        setTransfer(refreshed.data);
      } else {
        setCompletedTransferInvoice(res.data);
      }
      if (salesOnly || activeSearchTab === 'sales') {
        await loadSales(salesSearch, salesYear, { clearStatus: false });
      }
      cancelShippingInvoiceEditor();
      setStatus(`Invoice #${res.data?.invoiceNo || shippingInvoiceEditor.docNo} saved.${autoEmailStatusText(res.data?.autoEmail)}`);
    } catch (err) {
      setShippingInvoiceEditor((current) => ({
        ...current,
        saving: false,
        error: err.response?.data?.error
          || err.response?.data?.message
          || (err.response?.status ? `Failed to save invoice. Server returned ${err.response.status}.` : '')
          || err.message
          || 'Failed to save invoice.',
      }));
    }
  };

  const openHistoryDocument = (row) => {
    if (row?.canOpenDocument === false) return;
    const sourceDocNo = row?.sourceDocNo || row?.docNo || row?.id;
    const transactionTypeId = Number(row?.transactionTypeId) || null;
    if (transactionTypeId === REPAIR_ORDER_TRANSACTION_TYPE_ID && row.repairOrderStatus === 'Open') {
      window.open(`/repair-orders?orderId=${encodeURIComponent(sourceDocNo)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    const options = {
      preserveSerialResults: true,
      transactionTypeId,
      highlightSerial: row?.serial || '',
    };

    if (transactionTypeId && transactionTypeId !== REPAIR_ORDER_TRANSACTION_TYPE_ID) {
      return loadTransfer(null, sourceDocNo, options);
    }
    return loadInvoiceDetail(sourceDocNo, options);
  };

  const searchTransfers = async ({ vendor = '', location = '', yearOverride = vendorYear } = {}) => {
    const vendorText = vendor.trim();
    const locationText = location.trim();
    if (!vendorText && !locationText) {
      setStatus('Select a vendor or receiving location.');
      return;
    }

    setVendorLoading(true);
    setStatus('');
    setVendorResults([]);
    setSerialResults([]);
    setSelectedSerialRowIds([]);
    setLastSearchFilters({ vendor: vendorText, location: locationText });

    try {
      const res = await api.get('/transfers/search/vendor', {
        params: { vendor: vendorText, location: locationText, year: yearOverride },
      });
      const payload = res.data || {};
      const rows = Array.isArray(payload) ? payload : payload.rows || [];
      const years = Array.isArray(payload.years) ? payload.years.map(String) : [];
      setVendorResults(rows);
      setVendorYears(years);
      if (rows.length === 0) {
        setStatus('No transfer docs found for those filters.');
      }
    } catch (err) {
      console.error('Failed to search transfer docs:', err);
      setStatus('Failed to search transfer docs.');
    } finally {
      setVendorLoading(false);
    }
  };

  const searchByLocation = (e, yearOverride = currentYear) => {
    e?.preventDefault();
    setVendorYear(yearOverride);
    searchTransfers({ location: selectedLocation, yearOverride });
  };

  const searchByVendor = (e, yearOverride = currentYear) => {
    e?.preventDefault();
    setVendorYear(yearOverride);
    searchTransfers({ vendor: selectedVendor, yearOverride });
  };

  const fetchSerialRows = async (serials, { availableOnly = false } = {}) => {
    const serialList = [...new Set(serials.map((serial) => String(serial || '').trim()).filter(Boolean))];
    if (serialList.length === 0) {
      return [];
    }

    const responses = [];
    for (let index = 0; index < serialList.length; index += SERIAL_LOOKUP_BATCH_SIZE) {
      const batch = serialList.slice(index, index + SERIAL_LOOKUP_BATCH_SIZE);
      const batchResponses = await Promise.all(
        batch.map((serial) => api.get(availableOnly ? '/transfers/search/serial/available' : '/transfers/search/serial', {
          params: { serial, refresh: Date.now() },
        }))
      );
      responses.push(...batchResponses);
    }

    const seen = new Set();
    return responses
      .flatMap((res) => res.data || [])
      .filter((row) => !availableOnly || (row.subLedgerId && Number(row.quantityBalance) > 0))
      .filter((row) => {
        const key = String(row.subLedgerId || `${row.serial}-${row.sourceDocNo}`);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const selectedNewTransferSerialMatches = (serialText) => (
    Boolean(newTransferSerialInfo?.subLedgerId)
    && String(newTransferSerialInfo?.serial || '').trim().toLowerCase() === String(serialText || '').trim().toLowerCase()
  );

  const resetNewSaleSerialLookup = (serial = newSaleSerial) => {
    setNewSaleSerial(serial);
    setNewSaleSerialInfo(null);
    if (salesOnly && newSaleQueue.length === 0) {
      setNewSaleFrom('');
    }
  };

  const resetNewTransferSerialLookup = (serial = newTransferSerial) => {
    setNewTransferSerial(serial);
    setNewTransferSerialInfo(null);
    lastNewTransferNotFoundSerialRef.current = '';
    if (newTransferQueue.length === 0) {
      setNewTransferFrom('');
    }
  };

  const getSoldNotReturnedMessage = (rows, availableRows = []) => {
    const hasAvailableRow = [...rows, ...availableRows].some((row) => (
      row.subLedgerId && Number(row.quantityBalance) > 0
    ));
    const latestSale = rows.find((row) => Number(row.transactionTypeId) === 4);
    if (!hasAvailableRow && latestSale) {
      return `Serial number ${latestSale.serial || newSaleSerial} was sold on ${formatShortDate(latestSale.sourceDate) || 'a prior sale'} and has not been returned by the customer.`;
    }
    return '';
  };

  const getSaleSerialStatusMessage = (status, serialText) => {
    if (!status?.soldNotReturned) return '';
    return `Serial number ${status.serial || serialText} was sold on ${formatShortDate(status.saleDate) || 'a prior sale'} and has not been returned by the customer.`;
  };

  const lookupNewSaleSerial = async (e, serialOverride = newSaleSerial) => {
    e?.preventDefault();
    const serialText = String(serialOverride || '').trim();
    if (!serialText) {
      return;
    }
    if (/[\r\n,]/.test(serialText)) {
      setStatus('Enter only one serial number at a time.');
      return;
    }
    if (
      String(newSaleSerialInfo?.serial || '').trim().toLowerCase() === serialText.toLowerCase()
    ) {
      return;
    }

    setSaleLoading(true);
    setStatus('');
    setNewSaleSerialInfo(null);

    try {
      const statusResponse = await api.get('/transfers/sales/serial-status', {
        params: { serial: serialText, refresh: Date.now() },
      });
      const soldNotReturnedMessage = getSaleSerialStatusMessage(statusResponse.data, serialText);
      if (soldNotReturnedMessage) {
        setStatus(soldNotReturnedMessage);
        return;
      }

      const availableRows = await fetchSerialRows([serialText], { availableOnly: true });
      const row = availableRows[0];
      if (!row) {
        setStatus('No available serial number found.');
        return;
      }

      if (salesOnly && availableRows.length > 1) {
        if (newSaleQueue.length === 0) {
          setSerialChoiceModal({
            open: true,
            serial: serialText,
            rows: availableRows,
            source: null,
            destination: null,
            preserveSerialInfo: true,
          });
          return;
        }

        const source = findNewSaleSource(serialText);
        const sourceRows = availableRows.filter((item) => serialRowMatchesLocation(item, source));
        if (sourceRows.length > 1) {
          setSerialChoiceModal({
            open: true,
            serial: serialText,
            rows: sourceRows,
            source,
            destination: null,
            preserveSerialInfo: true,
          });
          return;
        }
      }

      const currentLocation = findAnyLocationOption(row.currentLocation)
        || locations.find((location) => Number(location.id) === Number(row.locationId));
      setNewSaleSerialInfo(row);
      if (salesOnly && newSaleQueue.length === 0) {
        setNewSaleFrom(currentLocation?.name || row.currentLocation || '');
        defaultNewSaleCustomerFromLocation(currentLocation || { name: row.currentLocation });
      } else if (salesOnly && newSaleQueue.length > 0) {
        const source = findNewSaleSource(serialText);
        if (!source?.id) {
          setStatus('Select a valid From Location.');
          return;
        }
        if (Number(row.locationId) !== Number(source.id)) {
          setStatus('Location does not match the sale queue.');
          return;
        }
        await addRowsToNewSaleQueue(serialText, source, availableRows, { preserveSerialInfo: true });
        setTimeout(() => newSaleSerialRef.current?.focus(), 0);
      }
    } catch (err) {
      console.error('Failed to look up sale serial number:', err);
      setStatus(err.response?.data?.error || 'Failed to look up serial number.');
    } finally {
      setSaleLoading(false);
    }
  };

  const lookupNewTransferSerial = async (e, serialOverride = newTransferSerial) => {
    e?.preventDefault();
    const serialText = String(serialOverride || '').trim();
    if (!serialText) {
      return;
    }
    if (/[\r\n,]/.test(serialText)) {
      setStatus('Enter only one serial number at a time.');
      return;
    }
    if (selectedNewTransferSerialMatches(serialText)) {
      return;
    }
    const normalizedSerialText = serialText.toLowerCase();
    if (lastNewTransferNotFoundSerialRef.current === normalizedSerialText) {
      return;
    }

    setSerialLoading(true);
    setStatus('');
    if (newTransferQueue.length === 0) {
      setNewTransferSerialInfo(null);
    }

    try {
      const serialRows = await fetchSerialRows([serialText], { availableOnly: true });
      const availableRows = serialRows.filter((item) => item.subLedgerId && Number(item.quantityBalance) > 0);
      const row = availableRows[0];
      if (!row) {
        lastNewTransferNotFoundSerialRef.current = normalizedSerialText;
        setStatus('No available serial number found.');
        return;
      }
      lastNewTransferNotFoundSerialRef.current = '';

      if (newTransferQueue.length === 0 && availableRows.length > 1) {
        if (!isManagerUser) {
          openManagerAdjustmentModal(serialText);
          return;
        }
        setSerialChoiceModal({
          open: true,
          serial: serialText,
          rows: availableRows,
          source: null,
          destination: null,
          preserveSerialInfo: true,
        });
        return;
      }

      const currentLocation = findAnyLocationOption(row.currentLocation)
        || locations.find((location) => Number(location.id) === Number(row.locationId));
      setNewTransferSerialInfo(row);
      if (newTransferQueue.length === 0) {
        setNewTransferFrom(currentLocation?.name || row.currentLocation || '');
      } else {
        const source = findNewTransferSource(serialText);
        const destination = findTransferDestination(newTransferTo, newTransferToId);
        const destinationInactiveError = getInactiveTransferDestinationError(newTransferTo, newTransferToId);
        if (!source?.id) {
          setStatus('Select a valid Location From.');
          return;
        }
        if (destinationInactiveError) {
          setStatus(destinationInactiveError, 'new-transfer-to');
          return;
        }
        if (!destination?.id) {
          setStatus('Select a valid Location To.');
          return;
        }
        if (Number(source.id) === Number(destination.id)) {
          setStatus('Location From and Location To must be different.');
          return;
        }
        if (Number(row.locationId) !== Number(source.id)) {
          setStatus(TRANSFER_SOURCE_STOCK_MESSAGE);
          return;
        }

        await addRowsToNewTransferQueue(serialText, source, destination, serialRows, { preserveSerialInfo: true });
        setTimeout(() => newTransferSerialRef.current?.focus(), 0);
      }
    } catch (err) {
      console.error('Failed to look up transfer serial number:', err);
      setStatus(err.response?.data?.error || 'Failed to look up serial number.');
    } finally {
      setSerialLoading(false);
    }
  };

  const loadSerialResults = async (serials) => {
    const serialList = [...new Set(serials.map((serial) => String(serial || '').trim()).filter(Boolean))];
    if (serialList.length === 0) {
      setStatus('Enter a serial number.');
      return;
    }

    setSerialLoading(true);
    setStatus('');
    setSerialResults([]);
    setSelectedSerialRowIds([]);

    try {
      const rows = await fetchSerialRows(serialList);
      setSerialResults(rows);
      setSelectedSerialRowIds([]);
      if (rows.length === 0) {
        setStatus('No transfer history found for those serial numbers.');
      }
    } catch (err) {
      console.error('Failed to search serial number:', err);
      setStatus(err.response?.data?.error || 'Failed to search serial number.');
    } finally {
      setSerialLoading(false);
    }
  };

  const searchBySerial = async (e) => {
    e?.preventDefault();
    await loadSerialResults(parseSerialSearch(serialNo));
  };

  const resetAdjustmentSearch = useCallback(({ keepHistory = false, keepStatus = false } = {}) => {
    setAdjustmentSerial('');
    setAdjustmentSource(null);
    if (!keepHistory) {
      setAdjustmentHistory([]);
    }
    setAdjustmentTargetItem(null);
    setAdjustmentItemQuery('');
    setAdjustmentItemResults([]);
    setAdjustmentMemo('');
    if (!keepStatus) {
      setStatus('');
    }
    window.setTimeout(() => {
      document.getElementById(ADJUSTMENT_SERIAL_SEARCH_INPUT_ID)?.focus();
    }, 0);
  }, [setStatus]);

  const loadAdjustmentSerial = async (e) => {
    e?.preventDefault();
    const serialText = String(adjustmentSerial || '').trim();
    if (!serialText) {
      setStatus('Enter a serial number.');
      return;
    }

    setAdjustmentLoading(true);
    setStatus('');
    setAdjustmentSource(null);
    setAdjustmentHistory([]);
    setAdjustmentTargetItem(null);
    setAdjustmentItemQuery('');
    setAdjustmentItemResults([]);

    try {
      const rows = await fetchSerialRows([serialText]);
      const availableRows = await fetchSerialRows([serialText], { availableOnly: true });
      setAdjustmentHistory(rows);
      if (availableRows.length === 0) {
        setStatus('No available inventory row found for that serial number.');
        return;
      }
      setAdjustmentSource(availableRows[0]);
    } catch (err) {
      console.error('Failed to load adjustment serial:', err);
      setStatus(err.response?.data?.error || 'Failed to load serial number.');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const selectAdjustmentItem = (item) => {
    setAdjustmentTargetItem(item);
    setAdjustmentItemQuery(item.name || item.fullName || String(item.id || ''));
    setAdjustmentItemResults([]);
  };

  const clearAdjustmentItem = () => {
    setAdjustmentTargetItem(null);
    setAdjustmentItemQuery('');
    setAdjustmentItemResults([]);
  };

  const submitAdjustment = async () => {
    if (!canEditInventory) {
      setStatus('You do not have permission to adjust inventory.');
      return;
    }
    if (!adjustmentSource?.subLedgerId) {
      setStatus('Find an available serial number first.');
      return;
    }
    if (!adjustmentTargetItem?.id) {
      setStatus('Select the new item.');
      return;
    }
    if (!isManagerUser && Number(adjustmentSource.itemId) === Number(adjustmentTargetItem.id)) {
      setStatus('Serial number is already assigned to that item.');
      return;
    }

    setAdjustmentLoading(true);
    setStatus('');

    try {
      const res = await api.post('/transfers/adjustments', {
        subLedgerId: adjustmentSource.subLedgerId,
        serial: adjustmentSource.serial,
        targetItemId: adjustmentTargetItem.id,
        preparedBy,
        memo: adjustmentMemo.trim(),
      });
      const docNoResult = res.data?.docNo;
      setStatus(
        `Serial ${adjustmentSource.serial} adjusted to ${adjustmentTargetItem.name || adjustmentTargetItem.fullName}. Doc # ${docNoResult}`,
        ADJUSTMENT_SERIAL_SEARCH_INPUT_ID
      );
      const rows = await fetchSerialRows([adjustmentSource.serial]);
      setAdjustmentHistory(rows);
      await loadAdjustmentList(adjustmentDateRange);
      resetAdjustmentSearch({ keepHistory: true, keepStatus: true });
    } catch (err) {
      console.error('Failed to adjust serial number:', err);
      setStatus(err.response?.data?.error || 'Failed to adjust serial number.');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const activeLocations = locations.filter((location) => (
    location.active !== false
    && (
      location.deleted !== true
      || location.available === true
      || location.show_on_item_list === true
    )
  ));

  const findLocationOption = (name) => activeLocations.find(
    (location) => {
      const query = String(name || '').trim();
      const normalizedQuery = normalizeLocationName(query);
      const compactQuery = normalizeLookupText(query);
      return (
        normalizeLocationName(location.name) === normalizedQuery
        || (query && String(location.id) === query)
        || (compactQuery && normalizeLookupText(`${location.id || ''} ${location.name || ''}`) === compactQuery)
      );
    }
  );

  const findInactiveLocationOption = (name) => locations.find(
    (location) => {
      if (location.active !== false) return false;
      const query = String(name || '').trim();
      if (!query) return false;
      const normalizedQuery = normalizeLocationName(query);
      const compactQuery = normalizeLookupText(query);
      return (
        normalizeLocationName(location.name) === normalizedQuery
        || (query && String(location.id) === query)
        || (compactQuery && normalizeLookupText(`${location.id || ''} ${location.name || ''}`) === compactQuery)
      );
    }
  );

  const findTransferDestination = (name, selectedId) => resolveTransferDestination(
    activeLocations, selectedId, () => findLocationOption(name)
  );

  const getInactiveTransferDestinationError = (name, selectedId) => {
    const destination = resolveTransferDestination(
      locations, selectedId, () => findLocationOption(name) || findInactiveLocationOption(name)
    );
    return destination?.active === false ? INACTIVE_TRANSFER_LOCATION_MESSAGE : '';
  };

  const findAnyLocationOption = (name) => locations.find(
    (location) => {
      const query = String(name || '').trim();
      const normalizedQuery = normalizeLocationName(query);
      const compactQuery = normalizeLookupText(query);
      return (
        normalizeLocationName(location.name) === normalizedQuery
        || (query && String(location.id) === query)
        || (compactQuery && normalizeLookupText(`${location.id || ''} ${location.name || ''}`) === compactQuery)
      );
    }
  );

  const findLocationById = (id) => locations.find(
    (location) => Number(location.id) === Number(id)
  );

  const locationFromSerialRow = (row) => {
    if (!row?.locationId) return null;
    const location = findLocationById(row.locationId);
    return location || {
      id: row.locationId,
      name: row.currentLocation || newTransferFrom,
    };
  };

  const serialRowMatchesLocation = (row, location) => {
    if (!row || !location) return false;
    if (row.locationId && location.id && Number(row.locationId) === Number(location.id)) {
      return true;
    }

    const locationNames = [location.name, location.loc_location].filter(Boolean);
    const rowLocationNames = [row.currentLocation, row.location, row.shipTo, row.name].filter(Boolean);
    return locationNames.some((locationName) => (
      rowLocationNames.some((rowLocationName) => (
        normalizeLocationName(rowLocationName) === normalizeLocationName(locationName)
      ))
    ));
  };

  const findNewTransferSource = (serialText = newTransferSerial) => {
    const queuedLocation = locationFromSerialRow(newTransferQueue[0]);
    if (queuedLocation?.id) return queuedLocation;

    const serialKey = String(serialText || '').trim().toLowerCase();
    const lookupSerialKey = String(newTransferSerialInfo?.serial || '').trim().toLowerCase();
    if (serialKey && lookupSerialKey === serialKey) {
      const lookupLocation = locationFromSerialRow(newTransferSerialInfo);
      if (lookupLocation?.id) return lookupLocation;
    }

    return findLocationOption(newTransferFrom) || findAnyLocationOption(newTransferFrom);
  };

  const findNewSaleSource = (serialText = newSaleSerial) => {
    const queuedLocation = locationFromSerialRow(newSaleQueue[0]);
    if (queuedLocation?.id) return queuedLocation;

    const serialKey = String(serialText || '').trim().toLowerCase();
    const lookupSerialKey = String(newSaleSerialInfo?.serial || '').trim().toLowerCase();
    if (serialKey && lookupSerialKey === serialKey) {
      const lookupLocation = locationFromSerialRow(newSaleSerialInfo);
      if (lookupLocation?.id) return lookupLocation;
    }

    return findLocationOption(newSaleFrom) || findAnyLocationOption(newSaleFrom);
  };

  const findCustomerOption = (name) => customers.find(
    (customer) => String(customer.name || customer.full_name || customer.fullname || '').trim().toLowerCase() === String(name || '').trim().toLowerCase()
  );

  const customerForAccessCheck = (customer) => {
    if (!customer) return customer;
    const currentTypeName = customer.customerTypeName
      || customer.customer_type_name
      || customer.typeName
      || customer.type_name;
    if (currentTypeName) return customer;

    const typeId = customer.customertypeid ?? customer.customerTypeId ?? customer.type;
    const matchedType = customerTypes.find((type) => String(type.id) === String(typeId));
    if (!matchedType?.name) return customer;
    return {
      ...customer,
      customerTypeName: matchedType.name,
      typeName: matchedType.name,
    };
  };

  const defaultNewSaleCustomerFromLocation = (location) => {
    const locationName = location?.name || location?.loc_location || '';
    if (!locationName || normalizeLocationName(locationName) === normalizeLocationName(DEFAULT_TRANSFER_FROM_LOCATION)) {
      return;
    }
    const locationCustomer = findCustomerOption(locationName);
    setNewSaleCustomer(getCustomerDisplayName(locationCustomer) || locationName);
    setNewSaleShippingAddress('billing');
  };

  const ensureNewSaleCustomer = async (source) => {
    const selectedCustomer = findCustomerOption(newSaleCustomer);
    if (selectedCustomer?.id) return selectedCustomer;

    const sourceName = source?.name || source?.loc_location || '';
    const customerName = String(newSaleCustomer || '').trim();
    const isSourceLocationCustomer = salesOnly
      && sourceName
      && normalizeLocationName(sourceName) !== normalizeLocationName(DEFAULT_TRANSFER_FROM_LOCATION)
      && normalizeLocationName(customerName) === normalizeLocationName(sourceName);
    if (!isSourceLocationCustomer) return null;
    if (!canCreateWholesaleCustomer) {
      throw new Error('You do not have permission to create Wholesale customers.');
    }

    try {
      const response = await api.post('/customers', {
        name: sourceName,
        companyname: sourceName,
        customertypeid: getWholesaleCustomerTypeId(customerTypes),
      });
      const rows = await loadCustomers();
      const createdId = response.data?.id ? String(response.data.id) : '';
      const createdCustomer = rows.find((customer) => String(getCustomerId(customer)) === createdId)
        || rows.find((customer) => normalizeLocationName(getCustomerDisplayName(customer)) === normalizeLocationName(sourceName));
      if (createdCustomer?.id) {
        setNewSaleCustomer(getCustomerDisplayName(createdCustomer));
        setNewSaleShippingAddress('billing');
        return createdCustomer;
      }
    } catch (error) {
      if (error.response?.status === 409) {
        const rows = await loadCustomers();
        const existingCustomer = rows.find((customer) => (
          normalizeLocationName(getCustomerDisplayName(customer)) === normalizeLocationName(sourceName)
        ));
        if (existingCustomer?.id) {
          setNewSaleCustomer(getCustomerDisplayName(existingCustomer));
          setNewSaleShippingAddress('billing');
          return existingCustomer;
        }
      }
      throw error;
    }

    return null;
  };

  const closeRetailCustomerModal = () => {
    if (retailCustomerSaving) return;
    setRetailCustomerModalOpen(false);
    setRetailCustomerStatus('');
    setRetailDuplicatePhoneCustomers([]);
    setNewRetailCustomer({ companyname: '', phone: '', email: '' });
  };

  const createRetailCustomer = async (event) => {
    event.preventDefault();
    setRetailDuplicatePhoneCustomers([]);
    if (!canCreateRetailCustomer) {
      setRetailCustomerStatus('You do not have permission to create Retail customers.');
      return;
    }
    const name = newRetailCustomer.companyname.trim();
    if (!name) {
      setRetailCustomerStatus('Customer is required.');
      return;
    }

    setRetailCustomerSaving(true);
    setRetailCustomerStatus('');
    try {
      const response = await api.post('/customers', {
        name,
        companyname: name,
        customertypeid: getRetailCustomerTypeId(customerTypes),
        phone: newRetailCustomer.phone.trim(),
        email: newRetailCustomer.email.trim(),
      });
      const rows = await loadCustomers();
      const createdId = response.data?.id ? String(response.data.id) : '';
      const createdCustomer = rows.find((customer) => String(getCustomerId(customer)) === createdId)
        || rows.find((customer) => normalizeLocationName(getCustomerDisplayName(customer)) === normalizeLocationName(name));
      setNewSaleCustomer(getCustomerDisplayName(createdCustomer) || name);
      setNewSaleShippingAddress('billing');
      setRetailCustomerModalOpen(false);
      setRetailCustomerStatus('');
      setNewRetailCustomer({ companyname: '', phone: '', email: '' });
    } catch (error) {
      setRetailCustomerStatus(error.response?.data?.error || error.message || 'Failed to add customer.');
      if (error.response?.data?.code === 'DUPLICATE_CUSTOMER_PHONE') {
        setRetailDuplicatePhoneCustomers(error.response.data.customers || []);
      }
    } finally {
      setRetailCustomerSaving(false);
    }
  };

  const transferSummaryCache = useRef(null);
  if (!transferSummaryCache.current) transferSummaryCache.current = createTransferSummaryCache();
  useEffect(() => {
    transferSummaryCache.current.clear();
  }, [newTransferFrom, newTransferTo, newTransferToId, user]);

  const loadTransferLimitSummary = useCallback((destination, customer, force = false) => (
    transferSummaryCache.current.get(`${destination.id}:${customer.id}`, async () => {
      const { data } = await api.get(`/transfers/locations/${destination.id}/inventory-summary`, { params: { customerId: customer.id } });
      return data;
    }, { force })
  ), []);

  const limitPrefetchDestination = findTransferDestination(newTransferTo, newTransferToId);
  const limitPrefetchCustomer = findCustomerOption(limitPrefetchDestination?.name || newTransferTo);
  useEffect(() => {
    if (salesOnly || !canCreateTransfer || !limitPrefetchDestination?.id || !limitPrefetchCustomer?.id) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data: customer } = await api.get(`/customers/${limitPrefetchCustomer.id}`);
        if (cancelled || !transferLimitFields.some(({ key }) => parsePositiveLimit(customer[key]) !== null)) return;
        await loadTransferLimitSummary(limitPrefetchDestination, customer);
      } catch {
        // A background failure grants no permission to add; Add retries the check.
      }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [salesOnly, canCreateTransfer, limitPrefetchDestination, limitPrefetchCustomer, newTransferFrom, user, loadTransferLimitSummary]);

  const getTransferLimitViolation = async (destination, rowsToAdd = [], { force = false } = {}) => {
    const matchedCustomer = findCustomerOption(destination?.name || newTransferTo);
    if (!matchedCustomer?.id || !destination?.id) return null;
    const { data: customer } = await api.get(`/customers/${matchedCustomer.id}`);
    if (!transferLimitFields.some(({ key }) => parsePositiveLimit(customer[key]) !== null)) return null;
    const summary = await loadTransferLimitSummary(destination, customer, force);
    const entries = assessTransferLimits(customer, summary, newTransferQueue, rowsToAdd);
    if (!entries.some((entry) => entry.violated)) return null;
    return { customer, destination, summary, queuedRows: [...newTransferQueue], newRows: rowsToAdd,
      entries, draft: transferLimitDraft(customer), editing: false, saving: false, error: '' };
  };

  const closeTransferLimits = () => {
    if (!transferLimitsModal?.saving) setTransferLimitsModal(null);
  };

  const saveTransferLimits = async (event) => {
    event.preventDefault();
    const modal = transferLimitsModal;
    if (!modal || modal.saving || !(can(user, 'customers.adjust_limits', modal.customer) || can(user, 'customers.edit', modal.customer))) return;
    const payload = {};
    for (const { key, label, step } of transferLimitFields) {
      const text = String(modal.draft[key] ?? '').trim();
      const value = text === '' ? null : Number(text);
      if (value !== null && (!Number.isFinite(value) || value < 0 || (step === '1' && !Number.isInteger(value)))) {
        setTransferLimitsModal((current) => ({ ...current, error: `Enter a valid nonnegative value for ${label}.` }));
        return;
      }
      payload[key] = value;
    }
    setTransferLimitsModal((current) => ({ ...current, saving: true, error: '' }));
    try {
      const { data } = await api.put(`/customers/${modal.customer.id}/limits`, payload);
      const customer = data.customer || { ...modal.customer, ...payload };
      setCustomers((rows) => rows.map((row) => String(getCustomerId(row)) === String(customer.id) ? { ...row, ...customer } : row));
      const summary = await loadTransferLimitSummary(modal.destination, customer, true);
      const entries = assessTransferLimits(customer, summary, modal.queuedRows, modal.newRows);
      if (entries.some((entry) => entry.violated)) {
        setTransferLimitsModal({ ...modal, customer, summary, entries, draft: transferLimitDraft(customer),
          editing: true, saving: false, error: 'Limits saved. The highlighted limits are still exceeded.' });
      } else {
        setTransferLimitsModal(null);
      }
    } catch (error) {
      setTransferLimitsModal((current) => ({ ...current, saving: false,
        error: apiErrorMessage(error, 'Could not save or recheck customer limits.') }));
    }
  };

  const isRetailOrCodCustomer = (customer) => {
    const typeName = normalizeCustomerTypeName(
      customer?.customerTypeName
      || customer?.customer_type_name
      || customer?.typeName
      || customer?.type_name
    );
    return typeName === 'retail' || typeName === 'cod';
  };

  const findCustomerLocationName = (customer) => {
    const names = [
      customer?.name,
      customer?.full_name,
      customer?.fullname,
    ].map((value) => String(value || '').trim()).filter(Boolean);
    const location = activeLocations.find((candidate) => (
      names.some((name) => normalizeLocationName(candidate.name) === normalizeLocationName(name))
    ));
    return location?.name || names[0] || '';
  };

  const closeCustomerReturnLocationChoice = () => {
    setCustomerReturnLocationChoice({
      open: false,
      customer: null,
      customerLocationName: '',
    });
  };

  const chooseCustomerReturnLocation = (locationName) => {
    setCustomerReturnLocation(locationName);
    closeCustomerReturnLocationChoice();
  };

  const handleCustomerReturnCustomerChange = (value) => {
    setCustomerReturnCustomer(value);
    if (isCustomerReturnLocked) return;

    const customer = findCustomerOption(value);
    if (!customer?.id) {
      closeCustomerReturnLocationChoice();
      return;
    }

    if (isRetailOrCodCustomer(customer)) {
      setCustomerReturnLocation(DEFAULT_TRANSFER_FROM_LOCATION);
      closeCustomerReturnLocationChoice();
      return;
    }

    setCustomerReturnLocationChoice({
      open: true,
      customer,
      customerLocationName: findCustomerLocationName(customer),
    });
  };

  const isIrenesCompanyCustomer = (customer) => {
    const customerName = String(customer?.name || customer?.full_name || customer?.fullname || '').trim().toLowerCase();
    return customerName === 'irenes wigs' || customerName === "irene's wigs";
  };

  const getCustomerSaleAddressOptions = () => ([
    {
      key: 'billing',
      source: 'billing',
      seqNo: null,
      label: 'Default',
    },
    {
      key: 'shipping',
      source: 'shipping',
      seqNo: null,
      label: 'Alternate',
    },
  ]);

  const findSaleAddressOption = (customer) => (
    getCustomerSaleAddressOptions(customer).find((address) => address.key === newSaleShippingAddress)
      || getCustomerSaleAddressOptions(customer)[0]
  );

  const closeTransferModal = () => {
    setTransfer(null);
    setTransferHighlightSerial('');
    setSerialPopup(null);
    setOpenedTransferPrintView('invoice');
    cancelShippingInvoiceEditor();
  };

  const cancelNewTransfer = () => {
    if (serialTransferLoading) return;
    transferSummaryCache.current.clear();
    setNewTransferQueue([]);
    setNewTransferSerial('');
    setNewTransferSerialInfo(null);
    lastNewTransferNotFoundSerialRef.current = '';
    setNewTransferFrom('');
    setNewTransferTo('');
    setNewTransferMemo('');
    setNewTransferPrintOptions({ invoice: true, shippingLabel: true, pickupLabel: false });
    setManagerAdjustmentModal({ open: false, serial: '' });
    setStatus('');
    setOpenTypeahead('');
    setTimeout(() => newTransferSerialRef.current?.focus(), 0);
  };

  const addRowsToNewTransferQueue = async (
    serialText,
    source,
    destination,
    availableRows = null,
    { preserveSerialInfo = false, skipSerialChoice = false } = {}
  ) => {
    const rows = availableRows || await fetchSerialRows([serialText], { availableOnly: true });
    const queuedIds = new Set(newTransferQueue.map((row) => String(row.subLedgerId)));
    const sourceRows = rows.filter((row) => Number(row.locationId) === Number(source.id));
    const duplicateRows = sourceRows.filter((row) => queuedIds.has(String(row.subLedgerId)));
    let nextRows = sourceRows.filter((row) => !queuedIds.has(String(row.subLedgerId)));

    if (nextRows.length === 0) {
      if (duplicateRows.length > 0) {
        setStatus('This serial number is already in Queue');
        return false;
      }
      if (newTransferQueue.length > 0) {
        setStatus(TRANSFER_SOURCE_STOCK_MESSAGE);
        return false;
      }
      const otherLocationRow = rows.find((row) => (
        row.subLedgerId
        && Number(row.quantityBalance) > 0
        && Number(row.locationId) !== Number(source.id)
      ));
      if (otherLocationRow) {
        const currentLocation = locations.find((location) => Number(location.id) === Number(otherLocationRow.locationId));
        setLocationSwitchConfirm({
          open: true,
          row: otherLocationRow,
          serial: serialText,
          selectedLocation: source.name || newTransferFrom,
          currentLocation: otherLocationRow.currentLocation || currentLocation?.name || 'another location',
          value: '',
          error: '',
        });
        return false;
      }
      setStatus('No available serial numbers found at the selected source location.');
      return false;
    }

    if (!skipSerialChoice && nextRows.length > 1) {
      const selectedLookupRow = nextRows.find((row) => (
        String(row.subLedgerId) === String(newTransferSerialInfo?.subLedgerId)
        && String(row.serial || '').trim().toLowerCase() === String(serialText || '').trim().toLowerCase()
      ));
      if (selectedLookupRow) {
        nextRows = [{
          ...selectedLookupRow,
          duplicatePositiveSubLedgerIds: newTransferSerialInfo?.duplicatePositiveSubLedgerIds || [],
        }];
      }
    }

    if (!skipSerialChoice && nextRows.length > 1) {
      if (!isManagerUser) {
        openManagerAdjustmentModal(serialText);
        return false;
      }
      setSerialChoiceModal({
        open: true,
        serial: serialText,
        rows: nextRows,
        source,
        destination,
        preserveSerialInfo,
      });
      return false;
    }

    const limitViolation = await getTransferLimitViolation(destination, nextRows);
    if (limitViolation) {
      setTransferLimitsModal(limitViolation);
      return false;
    }

    setNewTransferQueue((current) => [...nextRows, ...current]);
    setNewTransferSerial('');
    if (!preserveSerialInfo) {
      setNewTransferSerialInfo(null);
    }
    setTimeout(() => newTransferSerialRef.current?.focus(), 0);
    return true;
  };

  const closeSerialChoiceModal = () => {
    setSerialChoiceModal({
      open: false,
      serial: '',
      rows: [],
      source: null,
      destination: null,
      preserveSerialInfo: false,
    });
  };

  const openManagerAdjustmentModal = (serialText) => {
    setManagerAdjustmentModal({
      open: true,
      serial: String(serialText || '').trim(),
    });
  };

  const closeManagerAdjustmentModal = () => {
    setManagerAdjustmentModal({
      open: false,
      serial: '',
    });
    setNewTransferSerial('');
    setNewTransferSerialInfo(null);
    lastNewTransferNotFoundSerialRef.current = '';
    setTimeout(() => newTransferSerialRef.current?.focus(), 0);
  };

  const confirmSerialChoice = async (row) => {
    if (!row?.subLedgerId) return;
    const serialText = serialChoiceModal.serial || row.serial || newTransferSerial;
    const duplicatePositiveSubLedgerIds = serialChoiceModal.rows
      .filter((choice) => (
        choice?.subLedgerId
        && String(choice.subLedgerId) !== String(row.subLedgerId)
        && Number(choice.quantityBalance) > 0
      ))
      .map((choice) => choice.subLedgerId);
    const selectedRow = {
      ...row,
      duplicatePositiveSubLedgerIds,
    };
    const currentLocation = findAnyLocationOption(row.currentLocation)
      || locations.find((location) => Number(location.id) === Number(row.locationId));
    const source = serialChoiceModal.source || currentLocation;
    const destination = serialChoiceModal.destination || findTransferDestination(newTransferTo, newTransferToId);

    if (salesOnly) {
      setNewSaleSerialInfo(selectedRow);
      if (source?.name || row.currentLocation) {
        setNewSaleFrom(source?.name || row.currentLocation || '');
        defaultNewSaleCustomerFromLocation(source || { name: row.currentLocation });
      }
      closeSerialChoiceModal();

      if (!serialChoiceModal.source && newSaleQueue.length === 0) {
        return;
      }

      if (!source?.id) {
        setStatus('Select a valid From Location.');
        return;
      }

      setSaleLoading(true);
      setStatus('');
      try {
        await addRowsToNewSaleQueue(serialText, source, [selectedRow], {
          preserveSerialInfo: serialChoiceModal.preserveSerialInfo,
          skipSerialChoice: true,
        });
      } catch (err) {
        console.error('Failed to add selected serial number:', err);
        setStatus(apiErrorMessage(err, 'Failed to add selected serial number.'));
      } finally {
        setSaleLoading(false);
      }
      return;
    }

    setNewTransferSerialInfo(selectedRow);
    if (source?.name || row.currentLocation) {
      setNewTransferFrom(source?.name || row.currentLocation || '');
    }
    closeSerialChoiceModal();

    if (!serialChoiceModal.source && newTransferQueue.length === 0) {
      return;
    }

    if (!source?.id) {
      setStatus('Select a valid Location From.');
      return;
    }
    const destinationInactiveError = getInactiveTransferDestinationError(newTransferTo, newTransferToId);
    if (destinationInactiveError) {
      setStatus(destinationInactiveError, 'new-transfer-to');
      return;
    }
    if (!destination?.id) {
      setStatus('Select a valid Location To.');
      return;
    }

    setSerialLoading(true);
    setStatus('');
    try {
      await addRowsToNewTransferQueue(serialText, source, destination, [selectedRow], {
        preserveSerialInfo: serialChoiceModal.preserveSerialInfo,
        skipSerialChoice: true,
      });
    } catch (err) {
      console.error('Failed to add selected serial number:', err);
      setStatus(apiErrorMessage(err, 'Failed to add selected serial number.'));
    } finally {
      setSerialLoading(false);
    }
  };

  const addSerialsToNewTransfer = async (e) => {
    e?.preventDefault();
    if (!canCreateTransfer) {
      setStatus('You do not have permission to create transfers.');
      return;
    }
    const serialText = String(newTransferSerial || '').trim();
    if (!serialText) {
      setStatus('Enter a serial number.');
      return;
    }
    if (/[\r\n,]/.test(serialText)) {
      setStatus('Enter only one serial number at a time.');
      return;
    }
    if (
      String(newTransferSerialInfo?.serial || '').trim().toLowerCase() !== serialText.toLowerCase()
    ) {
      setStatus('Look up the serial number before adding it to the transfer.');
      return;
    }

    const source = findNewTransferSource(serialText);
    const destination = findTransferDestination(newTransferTo, newTransferToId);
    const destinationInactiveError = getInactiveTransferDestinationError(newTransferTo, newTransferToId);
    if (!source?.id) {
      setStatus('Select a valid Location From.', 'new-transfer-from');
      return;
    }
    if (destinationInactiveError) {
      setStatus(destinationInactiveError, 'new-transfer-to');
      return;
    }
    if (!destination?.id) {
      setStatus('Select a valid Location To.', 'new-transfer-to');
      return;
    }
    if (Number(source.id) === Number(destination.id)) {
      setStatus('Location From and Location To must be different.');
      return;
    }
    setSerialLoading(true);
    setStatus('');

    try {
      await addRowsToNewTransferQueue(serialText, source, destination);
    } catch (err) {
      console.error('Failed to add serial numbers:', err);
      setStatus(apiErrorMessage(err, 'Failed to add serial numbers.'));
    } finally {
      setSerialLoading(false);
    }
  };

  const addSerialToNewSale = async (e) => {
    e?.preventDefault();
    if (!canOpenCreateSale) {
      setStatus('You do not have permission to create sales.');
      return;
    }
    const serialText = String(newSaleSerial || '').trim();
    if (!serialText) {
      setStatus('Enter a serial number.');
      return;
    }
    if (/[\r\n,]/.test(serialText)) {
      setStatus('Enter only one serial number at a time.');
      return;
    }
    if (
      salesOnly
      && String(newSaleSerialInfo?.serial || '').trim().toLowerCase() !== serialText.toLowerCase()
    ) {
      setStatus('Look up the serial number before adding it to the sale.');
      return;
    }

    let source = findNewSaleSource(serialText);
    if (!salesOnly && !source?.id) {
      setStatus('Select a valid From Location.');
      return;
    }
    if (!source?.id) {
      setStatus('Select a valid From Location.');
      return;
    }

    let customer;
    try {
      customer = await ensureNewSaleCustomer(source);
    } catch (err) {
      console.error('Failed to prepare sale customer:', err);
      setStatus(err.response?.data?.error || 'Failed to prepare sale customer.');
      return;
    }
    if (!customer?.id) {
      setStatus('Select a valid Customer.');
      return;
    }
    if (!can(user, 'sales.create', customerForAccessCheck(customer))) {
      setStatus('You can only create sales for allowed customer types.');
      return;
    }
    const sourceCustomer = source?.name ? findCustomerOption(source.name) : null;
    if (sourceCustomer?.id && isIrenesCompanyCustomer(customer)) {
      setStatus(`Customer and From Location appear reversed. Use Customer ${sourceCustomer.name || source.name} and From Location IRENES WIGS - NEW YORK.`);
      return;
    }

    setSaleLoading(true);
    setStatus('');

    try {
      const rows = await fetchSerialRows([serialText], { availableOnly: true });
      const lookupRow = (
        salesOnly
        && newSaleSerialInfo
        && String(newSaleSerialInfo.serial || '').trim().toLowerCase() === serialText.toLowerCase()
        && newSaleSerialInfo.subLedgerId
        && Number(newSaleSerialInfo.quantityBalance) > 0
      ) ? newSaleSerialInfo : null;
      const rowsWithLookup = lookupRow && !rows.some((row) => String(row.subLedgerId) === String(lookupRow.subLedgerId))
        ? [lookupRow, ...rows]
        : rows;
      const serialRows = await fetchSerialRows([serialText]);
      const soldNotReturnedMessage = getSoldNotReturnedMessage(serialRows, rowsWithLookup);
      if (soldNotReturnedMessage) {
        setStatus(soldNotReturnedMessage);
        return;
      }
      if (salesOnly) {
        const preferredRow = source?.id
          ? rowsWithLookup.find((row) => serialRowMatchesLocation(row, source))
          : rowsWithLookup[0];
        if (!preferredRow) {
          setStatus('No available serial number found.');
          return;
        }
        const currentLocation = findAnyLocationOption(preferredRow.currentLocation)
          || locations.find((location) => Number(location.id) === Number(preferredRow.locationId));
        if (currentLocation?.id) {
          source = currentLocation;
          setNewSaleFrom(currentLocation.name || preferredRow.currentLocation || '');
        }
      }
      if (!source?.id) {
        setStatus('Select a valid From Location.');
        return;
      }
      await addRowsToNewSaleQueue(serialText, source, rowsWithLookup);
    } catch (err) {
      console.error('Failed to add serial number to sale:', err);
      setStatus(err.response?.data?.error || 'Failed to add serial number to sale.');
    } finally {
      setSaleLoading(false);
    }
  };

  const addRowsToNewSaleQueue = async (
    serialText,
    source,
    availableRows = null,
    { preserveSerialInfo = false, skipSerialChoice = false } = {}
  ) => {
    const rows = availableRows || await fetchSerialRows([serialText]);
    const queuedIds = new Set(newSaleQueue.map((row) => String(row.subLedgerId)));
    const queuedSerials = new Set(newSaleQueue.map((row) => String(row.serial || '').trim().toLowerCase()).filter(Boolean));
    const sourceRows = rows.filter((row) => serialRowMatchesLocation(row, source));
    const duplicateRows = sourceRows.filter((row) => (
      queuedIds.has(String(row.subLedgerId))
      || queuedSerials.has(String(row.serial || '').trim().toLowerCase())
    ));
    const uniqueRowsBySerial = new Map();
    sourceRows
      .filter((row) => (
        !queuedIds.has(String(row.subLedgerId))
        && !queuedSerials.has(String(row.serial || '').trim().toLowerCase())
      ))
      .forEach((row) => {
        const serialKey = String(row.serial || serialText || '').trim().toLowerCase();
        if (!serialKey) return;
        const current = uniqueRowsBySerial.get(serialKey);
        if (!current || Number(row.subLedgerId || 0) > Number(current.subLedgerId || 0)) {
          uniqueRowsBySerial.set(serialKey, row);
        }
      });
    const nextRows = [...uniqueRowsBySerial.values()];

    if (nextRows.length === 0) {
      if (duplicateRows.length > 0) {
        setStatus('This serial number is already in the sale queue.');
        return false;
      }
      if (newSaleQueue.length > 0) {
        setStatus('Location does not match the sale queue.');
        return false;
      }
      setStatus(salesOnly ? 'No available serial number found.' : 'No available serial number found at the selected source location.');
      return false;
    }

    if (!skipSerialChoice && sourceRows.length > 1) {
      const selectedLookupRow = sourceRows.find((row) => (
        String(row.subLedgerId) === String(newSaleSerialInfo?.subLedgerId)
        && String(row.serial || '').trim().toLowerCase() === String(serialText || '').trim().toLowerCase()
      ));
      if (selectedLookupRow) {
        setNewSaleQueue((current) => [{
          ...selectedLookupRow,
          duplicatePositiveSubLedgerIds: newSaleSerialInfo?.duplicatePositiveSubLedgerIds || [],
        }, ...current]);
        setNewSaleSerial('');
        if (!preserveSerialInfo) {
          setNewSaleSerialInfo(null);
        }
        if (salesOnly) {
          setNewSaleFrom(source.name || source.loc_location || newSaleFrom);
        }
        setTimeout(() => newSaleSerialRef.current?.focus(), 0);
        return true;
      }

      if (salesOnly) {
        setSerialChoiceModal({
          open: true,
          serial: serialText,
          rows: sourceRows,
          source,
          destination: null,
          preserveSerialInfo,
        });
        return false;
      }
    }

    setNewSaleQueue((current) => [...nextRows, ...current]);
    setNewSaleSerial('');
    if (!preserveSerialInfo) {
      setNewSaleSerialInfo(null);
    }
    if (salesOnly) {
      setNewSaleFrom(source.name || source.loc_location || newSaleFrom);
    }
    setTimeout(() => newSaleSerialRef.current?.focus(), 0);
    return true;
  };

  const removeSaleSerial = (row) => {
    setNewSaleQueue((current) => current.filter(
      (item) => String(item.subLedgerId) !== String(row.subLedgerId)
    ));
  };

  const updateSaleSerialPrice = (row, value) => {
    setNewSaleQueue((current) => current.map((item) => (
      String(item.subLedgerId) === String(row.subLedgerId)
        ? { ...item, price: value }
        : item
    )));
  };

  const clearNewSale = () => {
    if (saleLoading) return;
    setNewSaleQueue([]);
    setNewSaleSerial('');
    setNewSaleSerialInfo(null);
    setNewSaleFrom('');
    setNewSaleCustomer('');
    setNewSaleShippingAddress('billing');
    setNewSaleTaxRate('0');
    setNewSaleMemo('');
    setOpenTypeahead('');
    setStatus('');
  };

  const cancelCustomerReturn = () => {
    if (customerReturnLoading) return;
    setCustomerReturnQueue([]);
    setCustomerReturnCustomer('');
    setCustomerReturnLocation(DEFAULT_TRANSFER_FROM_LOCATION);
    setCustomerReturnSerial('');
    setCustomerReturnMemo('');
    closeCustomerReturnLocationChoice();
    setStatus('');
    setOpenTypeahead('');
  };

  const addSerialToCustomerReturn = async (e) => {
    e?.preventDefault();
    if (!canQueueCustomerReturnSerial) {
      setStatus('You do not have permission to view or create customer returns.');
      return;
    }
    const customer = findCustomerOption(customerReturnCustomer);
    if (!customer?.id) {
      setStatus('Select a valid Customer.');
      return;
    }
    if (!can(user, 'returns.create', customerForAccessCheck(customer))) {
      setStatus('You can only create customer returns for allowed customer types.');
      return;
    }

    const destination = findLocationOption(customerReturnLocation);
    if (!destination?.id) {
      setStatus('Select a valid Return To Location.');
      return;
    }

    const serialText = String(customerReturnSerial || '').trim();
    if (!serialText) {
      setStatus('Enter a serial number.');
      return;
    }
    if (/[\r\n,]/.test(serialText)) {
      setStatus('Enter only one serial number at a time.');
      return;
    }

    setCustomerReturnLoading(true);
    setStatus('');

    try {
      const res = await api.post('/transfers/customer-returns/serial', {
        customerId: customer.id,
        serial: serialText,
      });
      const row = res.data;
      const queuedSerials = new Set(customerReturnQueue.map((item) => String(item.serial || '').trim().toLowerCase()));
      if (queuedSerials.has(String(row.serial || serialText).trim().toLowerCase())) {
        setStatus('This serial number is already in the return queue.');
        return;
      }
      setCustomerReturnQueue((current) => [...current, row]);
      setCustomerReturnSerial('');
    } catch (err) {
      console.error('Failed to add serial number to return:', err);
      setStatus(err.response?.data?.error || 'Failed to add serial number to return.');
    } finally {
      setCustomerReturnLoading(false);
    }
  };

  const removeCustomerReturnSerial = (row) => {
    setCustomerReturnQueue((current) => current.filter(
      (item) => String(item.serial || '').trim().toLowerCase() !== String(row.serial || '').trim().toLowerCase()
    ));
  };

  const completeCustomerReturn = async () => {
    if (!canOpenCreateReturn) {
      setStatus('You do not have permission to create customer returns.');
      return;
    }
    const customer = findCustomerOption(customerReturnCustomer);
    const destination = findLocationOption(customerReturnLocation);

    if (!customer?.id) {
      setStatus('Select a valid Customer.');
      return;
    }
    if (!can(user, 'returns.create', customerForAccessCheck(customer))) {
      setStatus('You can only create customer returns for allowed customer types.');
      return;
    }
    if (!destination?.id) {
      setStatus('Select a valid Return To Location.');
      return;
    }
    if (customerReturnQueue.length === 0) {
      setStatus('Add at least one serial number to the return queue.');
      return;
    }

    setCustomerReturnLoading(true);
    setStatus('');

    try {
      const queueSnapshot = [];
      for (const row of customerReturnQueue) {
        const res = await api.post('/transfers/customer-returns/serial', {
          customerId: customer.id,
          serial: row.serial,
        });
        queueSnapshot.push(res.data);
      }

      const res = await api.post('/transfers/customer-returns/credit-memo', {
        customerId: customer.id,
        locationId: destination.id,
        serials: queueSnapshot.map((row) => row.serial),
        preparedBy,
        memo: customerReturnMemo.trim(),
      });
      const docNoResult = res.data?.docNo;
      setStatus(`Credit Memo #${docNoResult} created for ${customer.name || customer.full_name || customerReturnCustomer}.${autoEmailStatusText(res.data?.autoEmail)}`);
      setCustomerReturnQueue([]);
      setCustomerReturnCustomer('');
      setCustomerReturnLocation(DEFAULT_TRANSFER_FROM_LOCATION);
      setCustomerReturnSerial('');
      setCustomerReturnMemo('');
      setCompletedTransferInvoice(null);
      setCompletedTransferHighlightSerial('');
      setTransfer(null);
      setTransferHighlightSerial('');
      setDocNo(String(docNoResult || ''));
      if (docNoResult) {
        await loadTransfer(null, String(docNoResult), {
          preserveSerialResults: true,
          transactionTypeId: 10,
        });
      }
    } catch (err) {
      console.error('Failed to create credit memo:', err);
      setStatus(err.response?.data?.error || 'Failed to create credit memo.');
    } finally {
      setCustomerReturnLoading(false);
    }
  };

  const openRemoveQueuedSerial = (row) => {
    setRemoveConfirm({
      open: true,
      row,
      value: '',
      error: '',
    });
  };

  const closeRemoveQueuedSerial = () => {
    setRemoveConfirm({
      open: false,
      row: null,
      value: '',
      error: '',
    });
  };

  const closeLocationSwitchConfirm = () => {
    setLocationSwitchConfirm({
      open: false,
      row: null,
      serial: '',
      selectedLocation: '',
      currentLocation: '',
      value: '',
      error: '',
    });
  };

  const confirmLocationSwitch = (e) => {
    e?.preventDefault();
    const expectedSerial = String(locationSwitchConfirm.serial || '').trim();
    const enteredSerial = String(locationSwitchConfirm.value || '').trim();
    const row = locationSwitchConfirm.row;

    if (!row) return;
    if (enteredSerial !== expectedSerial) {
      setLocationSwitchConfirm((current) => ({
        ...current,
        error: 'Serial number does not match.',
      }));
      return;
    }

    const currentLocation = locations.find((location) => Number(location.id) === Number(row.locationId));
    if (!currentLocation?.id) {
      setLocationSwitchConfirm((current) => ({
        ...current,
        error: 'Current location could not be found.',
      }));
      return;
    }

    const destination = findTransferDestination(newTransferTo, newTransferToId);
    if (destination?.id && Number(destination.id) === Number(currentLocation.id)) {
      setLocationSwitchConfirm((current) => ({
        ...current,
        error: 'Current location matches Location To. Choose a different Location To before adding this serial.',
      }));
      return;
    }

    setNewTransferFrom(currentLocation.name || locationSwitchConfirm.currentLocation);
    setNewTransferQueue([row]);
    setNewTransferSerial('');
    setNewTransferMemo('');
    setOpenTypeahead('');
    closeLocationSwitchConfirm();
  };

  const removeQueuedSerial = (e) => {
    e?.preventDefault();
    const expectedSerial = String(removeConfirm.row?.serial || '').trim();
    const enteredSerial = String(removeConfirm.value || '').trim();

    if (enteredSerial !== expectedSerial) {
      setRemoveConfirm((current) => ({
        ...current,
        error: 'Serial number does not match.',
      }));
      return;
    }

    setNewTransferQueue((current) => current.filter(
      (row) => String(row.subLedgerId) !== String(removeConfirm.row?.subLedgerId)
    ));
    closeRemoveQueuedSerial();
  };

  const openDeleteTransfer = (row) => {
    const isShippingDelete = Number(row?.transactionTypeId) === 4;
    if (isShippingDelete ? !canDeleteSale : !canDeleteTransfer) {
      setStatus(`You do not have permission to delete ${isShippingDelete ? 'shipping docs' : 'transfers'}.`);
      return;
    }
    setDeleteTransferConfirm({
      open: true,
      row,
      value: '',
      error: '',
    });
  };

  const closeDeleteTransfer = () => {
    if (transferDeleteLoading) return;
    setDeleteTransferConfirm({
      open: false,
      row: null,
      value: '',
      error: '',
    });
  };

  const openDeleteShippingInvoice = (docNo) => {
    if (!canDeleteSale) {
      setStatus('You do not have permission to delete shipping docs.');
      return;
    }
    const sourceDocNo = String(docNo || '').trim();
    if (!sourceDocNo) return;
    setDeleteTransferConfirm({
      open: true,
      row: { sourceDocNo, transactionTypeId: 4 },
      value: '',
      error: '',
    });
  };

  const deleteTransferDoc = async (e) => {
    e?.preventDefault();
    const expectedDocNo = String(deleteTransferConfirm.row?.sourceDocNo || '').trim();
    const enteredDocNo = String(deleteTransferConfirm.value || '').trim();
    const transactionTypeId = Number(deleteTransferConfirm.row?.transactionTypeId);
    const isShippingDelete = transactionTypeId === 4;
    const docLabel = isShippingDelete ? 'Shipping' : 'Transfer';
    if (isShippingDelete ? !canDeleteSale : !canDeleteTransfer) {
      setDeleteTransferConfirm((current) => ({
        ...current,
        error: `You do not have permission to delete ${isShippingDelete ? 'shipping docs' : 'transfers'}.`,
      }));
      return;
    }

    if (enteredDocNo !== expectedDocNo) {
      setDeleteTransferConfirm((current) => ({
        ...current,
        error: 'Doc no does not match.',
      }));
      return;
    }

    setTransferDeleteLoading(true);
    setStatus('');

    try {
      const refreshSerials = parseSerialSearch(serialNo);
      if (refreshSerials.length === 0 && deleteTransferConfirm.row?.serial) {
        refreshSerials.push(deleteTransferConfirm.row.serial);
      }
      const { data: deleteResult } = await api.delete(isShippingDelete ? `/transfers/shipping/${expectedDocNo}` : `/transfers/${expectedDocNo}`);
      if (refreshSerials.length > 0) {
        const rows = await fetchSerialRows(refreshSerials);
        setSerialResults(rows);
        setSelectedSerialRowIds([]);
      } else {
        setSerialResults((current) => current.filter((row) => (
          String(row.sourceDocNo || '').trim() !== expectedDocNo
            || Number(row.transactionTypeId) !== transactionTypeId
        )));
        setSelectedSerialRowIds([]);
      }
      setVendorResults((current) => current.filter((row) => String(row.docNo || '').trim() !== expectedDocNo));
      setSalesResults((current) => current.filter((row) => String(row.docNo || row.sourceDocNo || '').trim() !== expectedDocNo));
      if (String(transfer?.docNo || '').trim() === expectedDocNo) {
        setTransfer(null);
        setTransferHighlightSerial('');
      }
      if (String(completedTransferInvoice?.invoiceNo || '').trim() === expectedDocNo) {
        setCompletedTransferInvoice(null);
        setCompletedTransferHighlightSerial('');
      }
      const returnedPaymentAmount = Number(deleteResult?.returnedPaymentAmount || 0);
      setStatus(`${docLabel} #${expectedDocNo} deleted.${returnedPaymentAmount > 0 ? ` Returned ${money(returnedPaymentAmount)} in applied payments.` : ''}`);
      setDeleteTransferConfirm({
        open: false,
        row: null,
        value: '',
        error: '',
      });
    } catch (err) {
      console.error(`Failed to delete ${docLabel.toLowerCase()}:`, err);
      setDeleteTransferConfirm((current) => ({
        ...current,
        error: err.response?.data?.error || `Failed to delete ${docLabel.toLowerCase()}.`,
      }));
    } finally {
      setTransferDeleteLoading(false);
    }
  };

  const completeNewTransfer = async () => {
    if (!canCreateTransfer) {
      setStatus('You do not have permission to create transfers.');
      return;
    }
    const source = findNewTransferSource();
    const destination = findTransferDestination(newTransferTo, newTransferToId);
    const destinationInactiveError = getInactiveTransferDestinationError(newTransferTo, newTransferToId);

    if (!source?.id) {
      setStatus('Select a valid Location From.', 'new-transfer-from');
      return;
    }
    if (destinationInactiveError) {
      setStatus(destinationInactiveError, 'new-transfer-to');
      return;
    }
    if (!destination?.id) {
      setStatus('Select a valid Location To.', 'new-transfer-to');
      return;
    }
    if (Number(source.id) === Number(destination.id)) {
      setStatus('Location From and Location To must be different.');
      return;
    }
    if (newTransferQueue.length === 0) {
      setStatus('Add at least one serial number to the queue.');
      return;
    }

    setSerialTransferLoading(true);
    setStatus('');

    try {
      const queueSnapshot = newTransferQueue;
      const limitViolation = await getTransferLimitViolation(destination, [], { force: true });
      if (limitViolation) {
        setTransferLimitsModal(limitViolation);
        return;
      }
      const res = await api.post('/transfers/serial-transfer', {
        subLedgerIds: queueSnapshot.map((row) => row.subLedgerId),
        sourceSerials: queueSnapshot.map((row) => ({
          subLedgerId: row.subLedgerId,
          serial: row.serial,
          duplicatePositiveSubLedgerIds: row.duplicatePositiveSubLedgerIds || [],
        })),
        sourceLocationId: source.id,
        locationId: destination.id,
        preparedBy,
        memo: newTransferMemo.trim(),
        comment: newTransferMemo.trim(),
      }, { timeout: LARGE_TRANSFER_SAVE_TIMEOUT_MS });
      transferSummaryCache.current.clear();
      const docNoResult = res.data?.docNo;
      const requestedPrintViews = [
        newTransferPrintOptions.invoice && 'invoice',
        newTransferPrintOptions.shippingLabel && 'shippingLabel',
        newTransferPrintOptions.pickupLabel && 'pickupLabel',
      ].filter(Boolean);
      setStatus(`Document #${docNoResult || ''} was created.${autoEmailStatusText(res.data?.autoEmail)}`);
      if (docNoResult && requestedPrintViews.length > 0) {
        let completedInvoice = null;
        if (newTransferPrintOptions.invoice) {
          try {
            const completedRes = await api.get(`/transfers/${docNoResult}`, {
              params: { transactionTypeId: 5 },
            });
            completedInvoice = transferToEmailInvoice(completedRes.data);
          } catch (printLoadError) {
            console.error('Failed to load completed transfer invoice for printing:', printLoadError);
            completedInvoice = {
              invoiceNo: docNoResult,
              date: getTodayDateInputValue(),
              type: 'Invoice',
              documentTitle: 'Invoice',
              preparedBy,
              comment: newTransferMemo.trim(),
              memo: newTransferMemo.trim(),
              from: source,
              to: destination,
              items: queueSnapshot.map((row) => ({
                id: row.subLedgerId,
                serial: row.serial,
                description: row.item || row.description || '',
                quantity: Number(row.qty ?? row.quantity ?? 1) || 1,
                price: Number(row.price) || 0,
              })),
            };
          }
        }
        setCompletedTransferPrintJob({
          docNo: docNoResult,
          invoice: completedInvoice,
          destination: { ...destination },
          views: requestedPrintViews,
        });
        setCompletedTransferPrintView(requestedPrintViews[0]);
      }
      setCompletedTransferInvoice(null);
      setCompletedTransferHighlightSerial('');
      setNewTransferQueue([]);
      setNewTransferSerial('');
      setNewTransferSerialInfo(null);
      setNewTransferFrom('');
      setNewTransferTo('');
      setNewTransferMemo('');
      setNewTransferPrintOptions({ invoice: true, shippingLabel: true, pickupLabel: false });
      setTransfer(null);
      setTransferHighlightSerial('');
      setDocNo(String(docNoResult || ''));
    } catch (err) {
      console.error('Failed to complete transfer:', err);
      setStatus(apiErrorMessage(err, 'Failed to complete transfer.'));
    } finally {
      setSerialTransferLoading(false);
    }
  };

  const completeNewSale = async () => {
    if (!canOpenCreateSale) {
      setStatus('You do not have permission to create sales.');
      return;
    }
    const queuedSource = newSaleQueue.length > 0
      ? (
        locations.find((location) => Number(location.id) === Number(newSaleQueue[0].locationId))
        || findAnyLocationOption(newSaleQueue[0].currentLocation)
      )
      : null;
    const source = queuedSource || findLocationOption(newSaleFrom);

    if (!source?.id) {
      setStatus('Select a valid From Location.');
      return;
    }

    let customer;
    try {
      customer = await ensureNewSaleCustomer(source);
    } catch (err) {
      console.error('Failed to prepare sale customer:', err);
      setStatus(err.response?.data?.error || 'Failed to prepare sale customer.');
      return;
    }
    if (!customer?.id) {
      setStatus('Select a valid Customer.');
      return;
    }
    if (!can(user, 'sales.create', customerForAccessCheck(customer))) {
      setStatus('You can only create sales for allowed customer types.');
      return;
    }
    const sourceCustomer = findCustomerOption(source.name);
    if (sourceCustomer?.id && isIrenesCompanyCustomer(customer)) {
      setStatus(`Customer and From Location appear reversed. Use Customer ${sourceCustomer.name || source.name} and From Location IRENES WIGS - NEW YORK.`);
      return;
    }
    const saleAddress = findSaleAddressOption(customer);
    if (newSaleQueue.length === 0) {
      setStatus('Add at least one serial number to the sale queue.');
      return;
    }

    setSaleLoading(true);
    setStatus('');

    try {
      const queuedSerials = newSaleQueue.map((row) => row.serial).filter(Boolean);
      const refreshedRows = await fetchSerialRows(queuedSerials, { availableOnly: true });
      const refreshedSourceRows = refreshedRows.filter((row) => Number(row.locationId) === Number(source.id));
      const refreshedBySubLedgerId = new Map(refreshedSourceRows.map((row) => [
        String(row.subLedgerId),
        row,
      ]));
      const refreshedBySerial = new Map(refreshedSourceRows.map((row) => [
        String(row.serial || '').trim().toLowerCase(),
        row,
      ]));
      const refreshedAllBySubLedgerId = new Map(refreshedRows.map((row) => [
        String(row.subLedgerId),
        row,
      ]));
      const refreshedAllBySerial = new Map(refreshedRows.map((row) => [
        String(row.serial || '').trim().toLowerCase(),
        row,
      ]));
      const refreshedQueueRows = newSaleQueue.map((row) => {
        const refreshed = refreshedBySubLedgerId.get(String(row.subLedgerId))
          || refreshedBySerial.get(String(row.serial || '').trim().toLowerCase());
        return {
          original: row,
          refreshed: refreshed ? { ...refreshed, price: row.price } : null,
        };
      });
      const queueSnapshot = refreshedQueueRows.map((row) => row.refreshed).filter(Boolean);

      if (queueSnapshot.length !== newSaleQueue.length) {
        const missingMessages = refreshedQueueRows
          .filter((row) => !row.refreshed)
          .map(({ original }) => {
            const serialText = String(original.serial || '').trim() || `sub-ledger row ${original.subLedgerId}`;
            const currentRow = refreshedAllBySubLedgerId.get(String(original.subLedgerId))
              || refreshedAllBySerial.get(String(original.serial || '').trim().toLowerCase());
            const currentLocation = String(currentRow?.location || currentRow?.currentLocation || '').trim();
            return currentLocation && currentLocation !== source.name
              ? `${serialText} is currently in ${currentLocation}`
              : serialText;
          });
        const missingText = missingMessages.length > 0 ? `: ${missingMessages.join(', ')}` : '';
        setStatus(`Queued sale serials are no longer available at ${source.name || 'the selected source location'}${missingText}. Please check the queue again.`);
        setNewSaleQueue(queueSnapshot);
        return;
      }

      setNewSaleQueue(queueSnapshot);
      const res = await api.post('/transfers/sales/ship-doc', {
        customerId: customer.id,
        locationId: source.id,
        shippingAddressSource: saleAddress?.source || 'billing',
        shippingAddressSeq: saleAddress?.seqNo || null,
        items: queueSnapshot.map((row) => ({
          subLedgerId: row.subLedgerId,
          serial: row.serial,
          price: row.price,
        })),
        subLedgerIds: queueSnapshot.map((row) => row.subLedgerId),
        taxRate: Number(newSaleTaxRate || 0),
        preparedBy,
        memo: newSaleMemo.trim(),
      });
      const docNoResult = res.data?.docNo;
      setStatus(`Ship Doc #${docNoResult} created for ${customer.name || customer.full_name || newSaleCustomer}.${autoEmailStatusText(res.data?.autoEmail)}`);
      setNewSaleQueue([]);
      setNewSaleSerial('');
      setNewSaleSerialInfo(null);
      setNewSaleFrom('');
      setNewSaleCustomer('');
      setNewSaleShippingAddress('billing');
      setNewSaleTaxRate('0');
      setNewSaleMemo('');
      setCompletedTransferInvoice(null);
      setCompletedTransferHighlightSerial('');
      setTransfer(null);
      setTransferHighlightSerial('');
      setDocNo(String(docNoResult || ''));
      if (docNoResult) {
        await loadSales(salesSearch, salesYear, { clearStatus: false });
      }
    } catch (err) {
      console.error('Failed to create ship doc:', err);
      setStatus(err.response?.data?.error || 'Failed to create ship doc.');
    } finally {
      setSaleLoading(false);
    }
  };

  const transferPopupSerials = async () => {
    if (!canCreateTransfer) {
      setStatus('You do not have permission to create transfers.');
      return;
    }
    if (selectedPopupSerials.length === 0) {
      setStatus('Select at least one serial number.');
      return;
    }

    const destination = resolveTransferDestination(activeLocations, popupSerialDestinationId, () => activeLocations.find(
      (location) => String(location.name || '').trim().toLowerCase() === popupSerialDestination.trim().toLowerCase()
    ));
    const destinationInactiveError = getInactiveTransferDestinationError(popupSerialDestination, popupSerialDestinationId);
    if (destinationInactiveError) {
      setStatus(destinationInactiveError);
      return;
    }
    if (!destination?.id) {
      setStatus('Select a valid destination location.');
      return;
    }

    setSerialTransferLoading(true);
    setStatus('');

    try {
      const rows = await fetchSerialRows(selectedPopupSerials, { availableOnly: true });
      if (rows.length === 0) {
        setStatus('No available items found for those serial numbers.');
        return;
      }

      if (rows.some((row) => Number(destination.id) === Number(row.locationId))) {
        setStatus('One or more selected serial numbers are already in the selected location.');
        return;
      }

      const res = await api.post('/transfers/serial-transfer', {
        subLedgerIds: rows.map((row) => row.subLedgerId),
        sourceSerials: rows.map((row) => ({
          subLedgerId: row.subLedgerId,
          serial: row.serial,
        })),
        locationId: destination.id,
      }, { timeout: LARGE_TRANSFER_SAVE_TIMEOUT_MS });
      const docNoResult = res.data?.docNo;
      setStatus(`${rows.length} serial number${rows.length === 1 ? '' : 's'} transferred to ${destination.name}. Doc # ${docNoResult}.${autoEmailStatusText(res.data?.autoEmail)}`);
      setSerialPopup(null);
      setSelectedPopupSerials([]);
      setPopupSerialDestination('');
    } catch (err) {
      console.error('Failed to transfer serial number:', err);
      setStatus(apiErrorMessage(err, 'Failed to transfer serial number.'));
    } finally {
      setSerialTransferLoading(false);
    }
  };

  const transferSelectedSerial = async () => {
    if (!canCreateTransfer) {
      setStatus('You do not have permission to create transfers.');
      return;
    }
    const selectedRows = serialResults.filter((row) => (
      selectedSerialRowIds.includes(String(row.subLedgerId)) && Number(row.quantityBalance) > 0
    ));
    if (selectedRows.length === 0) {
      setStatus('Select one or more available serial number rows to transfer.');
      return;
    }

    const destination = resolveTransferDestination(activeLocations, serialDestinationId, () => activeLocations.find(
      (location) => String(location.name || '').trim().toLowerCase() === serialDestination.trim().toLowerCase()
    ));
    const destinationInactiveError = getInactiveTransferDestinationError(serialDestination, serialDestinationId);
    if (destinationInactiveError) {
      setStatus(destinationInactiveError);
      return;
    }
    if (!destination?.id) {
      setStatus('Select a valid destination location.');
      return;
    }

    if (selectedRows.some((row) => Number(destination.id) === Number(row.locationId))) {
      setStatus('One or more selected serial numbers are already in the selected location.');
      return;
    }

    setSerialTransferLoading(true);
    setStatus('');

    try {
      const res = await api.post('/transfers/serial-transfer', {
        subLedgerIds: selectedRows.map((row) => row.subLedgerId),
        sourceSerials: selectedRows.map((row) => ({
          subLedgerId: row.subLedgerId,
          serial: row.serial,
        })),
        locationId: destination.id,
      }, { timeout: LARGE_TRANSFER_SAVE_TIMEOUT_MS });
      const docNoResult = res.data?.docNo;
      setStatus(`${selectedRows.length} serial number${selectedRows.length === 1 ? '' : 's'} transferred to ${destination.name}. Doc # ${docNoResult}.${autoEmailStatusText(res.data?.autoEmail)}`);
      setSerialDestination('');
      await loadSerialResults(parseSerialSearch(serialNo));
    } catch (err) {
      console.error('Failed to transfer serial number:', err);
      setStatus(apiErrorMessage(err, 'Failed to transfer serial number.'));
    } finally {
      setSerialTransferLoading(false);
    }
  };

  const itemCount = transfer?.items?.length || 0;
  const groupedItems = groupTransferItems(transfer?.items || []);
  const highlightedTransferSerial = String(transferHighlightSerial || '').trim().toLowerCase();
  const vendorOptions = vendors
    .map((vendor) => vendor.name || vendor.company_name || '')
    .filter(Boolean);
  const locationOptions = activeLocations
    .map((location) => ({
      id: location.id,
      label: location.name || '',
      searchText: [
        location.id,
        location.name,
        location.address1,
        location.address2,
        location.city,
        location.state,
        location.zip,
        location.phone,
      ].filter(Boolean).join(' '),
    }))
    .filter((option) => option.label);
  const customerOptions = customers
    .map((customer) => {
      const label = getCustomerDisplayName(customer);
      return {
        id: getCustomerId(customer) || label,
        label,
        customer,
        searchText: [
          label,
          getCustomerPhone(customer),
          ...getCustomerAddressLines(customer),
        ].filter(Boolean).join(' '),
      };
    })
    .filter((option) => option.label);
  const newSaleCustomerOptions = customerOptions.filter((option) => (
    !salesOnly || can(user, 'sales.create', customerForAccessCheck(option.customer))
  ));
  const shippingInvoiceCustomerLookupOptions = useMemo(() => {
    const query = String(shippingInvoiceEditor.draft?.customerQuery || '').trim();
    const text = normalizeLocationName(query);
    const digits = query.replace(/\D/g, '');
    return customers.filter((customer) => {
      if (!text) return true;
      const haystack = normalizeLocationName([
        getCustomerDisplayName(customer),
        getCustomerPhone(customer),
        ...getCustomerAddressLines(customer),
      ].filter(Boolean).join(' '));
      const phoneDigits = String(getCustomerPhone(customer) || '').replace(/\D/g, '');
      return haystack.includes(text) || (digits.length >= 3 && phoneDigits.includes(digits));
    }).slice(0, 30);
  }, [customers, shippingInvoiceEditor.draft?.customerQuery]);
  const selectedNewSaleCustomer = findCustomerOption(newSaleCustomer);
  const newSaleAddressOptions = getCustomerSaleAddressOptions(selectedNewSaleCustomer);
  const canCreateSale = can(user, 'sales.create', customerForAccessCheck(selectedNewSaleCustomer) || null);

  useEffect(() => {
    if (!transfer || !transferHighlightSerial) return;
    const timer = setTimeout(() => {
      highlightedTransferRowRef.current?.scrollIntoView({
        block: 'center',
        inline: 'center',
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [transfer, transferHighlightSerial]);
  const isNewSaleSerialReady = !salesOnly || Boolean(
    newSaleSerial
    && newSaleSerialInfo
    && String(newSaleSerialInfo.serial || '').trim().toLowerCase() === String(newSaleSerial || '').trim().toLowerCase()
  );
  const isNewTransferSerialReady = Boolean(
    newTransferSerial
    && newTransferSerialInfo
    && String(newTransferSerialInfo.serial || '').trim().toLowerCase() === String(newTransferSerial || '').trim().toLowerCase()
  );
  const serialCurrentLocation = serialResults.find((row) => row.currentLocation)?.currentLocation || '';
  const preparedBy = user?.name || user?.username || '';
  const displayedVendorYears = [...new Set([vendorYear, ...vendorYears].filter((year) => year && year !== 'all'))]
    .sort((a, b) => Number(b) - Number(a));
  const newTransferQueueTotalQty = newTransferQueue.reduce(
    (sum, row) => sum + (Number(row.qty ?? row.quantity ?? 1) || 1),
    0
  );
  const newTransferQueueTotalPrice = newTransferQueue.reduce((sum, row) => {
    const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
    const price = Number(row.price) || 0;
    return sum + (qty * price);
  }, 0);
  const newSaleQueueTotalQty = newSaleQueue.reduce(
    (sum, row) => sum + (Number(row.qty ?? row.quantity ?? 1) || 1),
    0
  );
  const newSaleQueueTotalPrice = newSaleQueue.reduce((sum, row) => {
    const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
    const price = Number(row.price) || 0;
    return sum + (qty * price);
  }, 0);
  const selectedNewSaleTaxRate = Number(newSaleTaxRate || 0);
  const newSaleQueueTaxAmount = Math.round(newSaleQueueTotalPrice * selectedNewSaleTaxRate) / 100;
  const newSaleQueueInvoiceTotal = Math.round((newSaleQueueTotalPrice + newSaleQueueTaxAmount) * 100) / 100;
  const customerReturnQueueTotalQty = customerReturnQueue.reduce(
    (sum, row) => sum + (Number(row.qty ?? row.quantity ?? 1) || 1),
    0
  );
  const customerReturnQueueTotalPrice = customerReturnQueue.reduce((sum, row) => {
    const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
    const price = Number(row.price) || 0;
    return sum + (qty * price);
  }, 0);
  const selectedCustomerReturnCustomer = findCustomerOption(customerReturnCustomer);
  const canCreateReturn = can(user, 'returns.create', customerForAccessCheck(selectedCustomerReturnCustomer) || null);
  const canQueueCustomerReturnSerial = canOpenCreateReturn || hasPermission(user, 'returns.read');
  const isNewTransferLocked = newTransferQueue.length > 0;
  const isCustomerReturnLocked = customerReturnQueue.length > 0;
  const renderSaleCustomerOption = (option, tooltipHandlers = {}) => {
    const customer = option?.customer || {};
    const phone = getCustomerPhone(customer);
    const addressLines = getCustomerAddressLines(customer);
    const addressText = addressLines.length > 0 ? addressLines.join('\n') : 'No address on file.';
    return (
      <span className="transfer-customer-option">
        <span className="transfer-customer-option-main">
          <span>{option?.label || '(No name)'}</span>
          <span
            className="transfer-customer-address-info"
            aria-label={`Customer address: ${addressText}`}
            tabIndex={-1}
            onMouseEnter={(event) => tooltipHandlers.show?.(event, addressLines)}
            onMouseLeave={() => tooltipHandlers.hide?.()}
          >
            i
          </span>
        </span>
        {phone && <small>{phone}</small>}
      </span>
    );
  };
  const transferUsesInvoiceDocument = shouldShowTransferInvoiceDocument(transfer);
  const canEditOpenedTransferTax = canEditInvoice && transferUsesInvoiceDocument && Number(transfer?.transactionTypeId) === 4;
  const completedTransferIsRepairInvoice = String(completedTransferInvoice?.type || '').toLowerCase() === 'repair invoice'
    || /^RO-/i.test(String(completedTransferInvoice?.invoiceNo || ''));
  const canEditCompletedTransferTax = canEditInvoice && (
    Boolean(completedTransferInvoice?.editableTax)
    || (!completedTransferIsRepairInvoice && Number(completedTransferInvoice?.transactionTypeId) === 4)
  );
  const canDeleteTransferRow = (row) => (
    Number(row?.transactionTypeId) === 4 ? canDeleteSale : canDeleteTransfer
  );
  const openedTransferInvoiceEditing = shippingInvoiceEditor.open
    && shippingInvoiceEditor.target === 'transfer'
    && String(shippingInvoiceEditor.docNo) === String(transfer?.docNo || '');
  const completedTransferInvoiceEditing = shippingInvoiceEditor.open
    && shippingInvoiceEditor.target === 'completed'
    && String(shippingInvoiceEditor.docNo) === String(completedTransferInvoice?.invoiceNo || completedTransferInvoice?.id || '');
  const activeInvoiceTaxOptions = invoiceEditTaxOptions(preferencesTaxRate, shippingInvoiceEditor.draft?.taxRate);
  const closeCompletedTransferInvoice = () => {
    setCompletedTransferInvoice(null);
    setCompletedTransferHighlightSerial('');
    setCompletedRepairPrintView('invoice');
    cancelShippingInvoiceEditor();
  };
  const printCompletedRepairOutput = () => {
    if (!completedTransferInvoice) return;
    if (completedRepairPrintView === 'shippingLabel' || completedRepairPrintView === 'pickupLabel') {
      printTransferLabel({
        location: completedTransferInvoice.to,
        pickup: completedRepairPrintView === 'pickupLabel',
        services: true,
        docNo: completedTransferInvoice.invoiceNo,
      });
      return;
    }

    const printClassName = 'invoice-document-printing';
    const cleanupPrintMode = () => {
      document.body.classList.remove(printClassName);
      window.removeEventListener('afterprint', cleanupPrintMode);
    };
    document.body.classList.add(printClassName);
    window.addEventListener('afterprint', cleanupPrintMode, { once: true });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.print());
    });
  };
  const printTransfer = () => {
    if (Number(transfer?.transactionTypeId) === 5) {
      printTransferPdf(transferToEmailInvoice(transfer)).catch(() => setStatus('Could not load transfer PDF. Please try Print again.'));
      return;
    }
    const isInvoiceDocument = shouldShowTransferInvoiceDocument(transfer);
    const source = document.querySelector(isInvoiceDocument ? '.shipdoc-invoice-workspace' : '.transfer-workspace');
    if (!source) return;

    const printNode = source.cloneNode(true);
    printNode.querySelectorAll('.transfer-print-button, .transfer-modal-close, .transfer-modal-action-row, .invoice-modal-header, .invoice-modal-actions, .invoice-modal-bottom-actions').forEach((node) => node.remove());
    printNode.querySelectorAll('[data-print-serials]').forEach((node) => {
      const serials = (node.getAttribute('data-print-serials') || '').split('|').filter(Boolean);
      if (serials.length > 0) {
        node.innerHTML = `<div class="transfer-print-serials">${serials.map((serial) => `<span>${escapeHtml(serial)}</span>`).join('')}</div>`;
      }
    });
    printNode.querySelectorAll('input').forEach((input) => {
      if (input.type === 'checkbox') {
        if (input.checked) {
          input.setAttribute('checked', 'checked');
        } else {
          input.removeAttribute('checked');
        }
      } else {
        input.setAttribute('value', input.value || '');
      }
    });

    const iframe = document.createElement('iframe');
    iframe.title = 'Transfer print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = isInvoiceDocument ? '816px' : '1122px';
    iframe.style.height = isInvoiceDocument ? '1056px' : '793px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Transfer ${transfer?.docNo || ''}</title>
          <style>
            @page { size: ${isInvoiceDocument ? 'letter portrait' : 'landscape'}; margin: ${isInvoiceDocument ? '5mm' : '10mm'}; }
            * { box-sizing: border-box; }
            body { color: #2b2b2b; font-family: Arial, sans-serif; margin: 0; }
            .transfer-workspace { background: #fff; border: 0; box-shadow: none; padding: 0; width: 100%; }
            .transfer-header { align-items: start; display: grid; gap: 1rem; grid-template-columns: minmax(360px, 1fr) minmax(280px, 360px); margin-bottom: 1rem; }
            .transfer-field-group { display: grid; gap: 0.75rem; grid-template-columns: repeat(3, minmax(140px, 1fr)); }
            .transfer-doc-card { padding: 1rem; position: relative; }
            .transfer-doc-card h2 { margin: 0 0 0.75rem; }
            .transfer-doc-grid { display: grid; gap: 0.75rem; grid-template-columns: 1fr 1fr; }
            .transfer-note { grid-column: 1 / -1; opacity: 0.75; }
            label { display: flex; flex-direction: column; gap: 0.25rem; font-weight: 700; }
            input { border: 1px solid #ccc; border-radius: 4px; font: inherit; padding: 0.5rem; width: 100%; }
            .transfer-check { align-items: center; flex-direction: row; font-weight: 500; }
            .transfer-check input { width: auto; }
            .transfer-meta-row { display: grid; gap: 0.75rem; grid-template-columns: repeat(4, minmax(140px, 1fr)); margin-bottom: 1rem; }
            .transfer-table-wrap { overflow: visible; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ccc; padding: 0.45rem; text-align: left; vertical-align: top; }
            th { background: #e5e5e5; font-weight: 700; }
            td:nth-child(1), td:nth-child(6), td:nth-child(7), td:nth-child(8), td:nth-child(10), td:nth-child(11), td:nth-child(12) { text-align: right; }
            .transfer-footer { display: flex; font-size: 1rem; gap: 1.5rem; justify-content: flex-end; padding-top: 1rem; }
            .transfer-print-serials { display: flex; flex-wrap: wrap; gap: 0.25rem 0.5rem; }
            .transfer-print-serials span:not(:last-child)::after { content: ","; }
            .shipdoc-invoice-workspace { background: #fff; padding: 0; }
            .shipdoc-invoice-actions { display: none; }
            .invoice-sheet { background: #fff; box-shadow: none; color: #1f1f1f; display: flex; flex-direction: column; font-family: Arial, Helvetica, sans-serif; margin: 0 auto; min-height: calc(100vh - 1px); padding: 22px 24px; position: relative; width: 100%; }
            .invoice-header { align-items: start; display: grid; gap: 20px; grid-template-columns: 1fr 1fr 250px; margin-bottom: 36px; }
            .invoice-header h1 { font-size: 24px; letter-spacing: 0; margin: 8px 0 0; }
            .invoice-brand { color: #5f6368; font-family: Georgia, serif; font-size: 28px; letter-spacing: 2px; line-height: 1; margin-top: 28px; text-align: center; }
            .invoice-brand span { display: block; font-family: Arial, Helvetica, sans-serif; font-size: 20px; font-weight: 700; letter-spacing: 0; margin-top: 4px; }
            .invoice-meta { border-collapse: collapse; font-size: 10px; text-align: center; width: 100%; }
            .invoice-meta th, .invoice-meta td { border: 1px solid #444; font-weight: 500; padding: 5px 7px; text-align: center; }
            .invoice-meta th { background: #e6e6e6; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .invoice-parties { display: grid; gap: 64px; grid-template-columns: 1fr 1fr; margin-bottom: 10px; }
            .invoice-address { border: 1px solid #4c4c4c; min-height: 130px; }
            .invoice-address-title { background: #e6e6e6; border-bottom: 1px solid #4c4c4c; font-size: 12px; padding: 4px 6px; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .invoice-address-body { display: flex; flex-direction: column; font-size: 12px; gap: 4px; line-height: 1.15; padding: 8px 10px; }
            .invoice-address-body strong { font-weight: 500; }
            .invoice-city-line { display: grid; gap: 12px; grid-template-columns: minmax(120px, 1fr) 60px 90px; }
            .invoice-items { border-collapse: collapse; font-size: 10px; width: 100%; }
            .invoice-items th { background: #e6e6e6; border: 1px solid #4c4c4c; font-weight: 500; padding: 4px 6px; text-align: center; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .invoice-items td { border: 0; padding: 5px 6px; text-align: left; vertical-align: top; }
            .invoice-items td:nth-child(3), .invoice-items td:nth-child(4), .invoice-items tfoot td:nth-child(2), .invoice-items tfoot td:nth-child(3) { text-align: right; }
            .invoice-items tfoot td { border-top: 2px solid #4c4c4c; font-weight: 700; padding: 10px 8px; }
            .invoice-items tfoot td:first-child, .invoice-items tfoot tr:last-child td { text-align: right; }
            .invoice-items th:first-child, .invoice-items td:first-child { width: 38%; }
            .invoice-items th:nth-child(2), .invoice-items td:nth-child(2) { width: 36%; }
            .invoice-items th:nth-child(3), .invoice-items td:nth-child(3) { width: 14%; }
            .invoice-items th:nth-child(4), .invoice-items td:nth-child(4) { width: 12%; }
            .invoice-highlight-row td { background: #fff3bf; font-weight: 700; }
            .transfer-invoice-footer-fields { align-items: end; border-top: 1px solid #4c4c4c; break-inside: avoid; column-gap: 28px; display: grid; grid-template-columns: 1fr 36%; grid-template-rows: auto auto; margin-top: auto !important; page-break-inside: avoid; padding-top: 10px; position: static; row-gap: 6px; }
            .transfer-invoice-total-table { border-collapse: collapse; font-size: 10px; grid-column: 2; grid-row: 1; margin-left: auto; table-layout: fixed; width: 100%; }
            .transfer-invoice-total-table td { border: 1px solid #4c4c4c; font-weight: 700; padding: 4px 6px; }
            .transfer-invoice-total-table td:first-child { text-align: right; width: 46%; }
            .transfer-invoice-total-table td:nth-child(2), .transfer-invoice-total-table td:nth-child(3) { text-align: right; }
            .transfer-invoice-total-table td:nth-child(2) { width: 18%; }
            .transfer-invoice-total-table td:nth-child(3) { width: 36%; }
            .transfer-invoice-prepared-by { font-size: 10px; font-weight: 700; grid-column: 2; grid-row: 2; text-align: right; }
            .transfer-invoice-memo { align-self: end; font-size: 10px; grid-column: 1; grid-row: 1 / span 2; margin: 0; padding: 4px 0; }
            .shipdoc-invoice-sheet { background: #fff; box-shadow: none; color: #000; display: flex; flex-direction: column; font-family: "Times New Roman", Times, serif; margin: 0; min-height: calc(100vh - 1px); padding: 34px 24px 28px; position: relative; width: 100%; }
            .shipdoc-paid-watermark { align-items: center; color: rgba(0, 0, 0, 0.72); display: flex; flex-direction: column; font-family: "Courier New", Courier, monospace; font-weight: 900; left: 50%; letter-spacing: 0; line-height: 0.92; pointer-events: none; position: absolute; text-align: center; text-transform: uppercase; top: 178px; transform: translateX(-50%) rotate(-18deg); z-index: 2; }
            .shipdoc-paid-watermark strong { border: 5px double currentColor; font-size: 44px; line-height: 0.9; padding: 3px 10px 0; }
            .shipdoc-paid-watermark span { font-size: 23px; margin-top: 2px; }
            .shipdoc-invoice-top { align-items: start; display: grid; grid-template-columns: 1fr 220px; margin: 0 auto 18px; width: 78%; }
            .shipdoc-company { font-size: 14px; line-height: 1.08; padding-top: 2px; }
            .shipdoc-title-block h1 { font-family: Arial, Helvetica, sans-serif; font-size: 26px; font-weight: 700; letter-spacing: 0; margin: 0 0 6px; text-align: right; }
            .shipdoc-invoice-meta { border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin-left: auto; table-layout: fixed; width: 190px; }
            .shipdoc-invoice-meta th, .shipdoc-invoice-meta td { border: 1px solid #000; height: 27px; padding: 4px 6px; text-align: center; }
            .shipdoc-invoice-meta th { background: #fff; font-weight: 400; }
            .shipdoc-bill-to-box { border: 1px solid #000; margin: 0 0 18px 34px; min-height: 108px; width: 360px; }
            .shipdoc-bill-to-title-row { border-bottom: 1px solid #000; display: block; }
            .shipdoc-bill-to-box h2 { font-family: Arial, Helvetica, sans-serif; font-size: 12px; font-weight: 400; margin: 0; padding: 6px 14px; }
            .shipdoc-bill-to-lookup { display: none !important; }
            .shipdoc-bill-to { font-size: 12px; line-height: 1.1; min-height: 0; padding: 4px 6px; }
            .shipdoc-lines-area { display: flex; flex: 1 1 auto; flex-direction: column; margin-top: 4px; position: relative; }
            .shipdoc-detail-strip { border-collapse: collapse; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin-left: auto; margin-right: 0; table-layout: fixed; width: 48.5%; }
            .shipdoc-detail-strip th, .shipdoc-detail-strip td { border: 1px solid #000; height: 26px; padding: 4px 5px; text-align: center; }
            .shipdoc-detail-strip th { background: #fff; font-weight: 400; }
            .shipdoc-invoice-items { border-collapse: collapse; break-after: avoid; font-size: 11px; margin-top: -2px; page-break-after: avoid; table-layout: fixed; width: 100%; }
            .shipdoc-invoice-items th { background: #fff; border: 1px solid #000; font-family: Arial, Helvetica, sans-serif; font-weight: 400; height: 28px; padding: 4px 5px; text-align: center; }
            .shipdoc-invoice-items td { border-bottom: 0; border-left: 1px solid #000; border-right: 1px solid #000; border-top: 0; line-height: 1.08; padding: 1px 5px; text-align: left; vertical-align: top; }
            .shipdoc-invoice-items tbody tr:first-child td { padding-top: 4px; }
            .shipdoc-invoice-items tbody tr:last-child td { padding-bottom: 190px; }
            .shipdoc-invoice-items th:nth-child(1), .shipdoc-invoice-items td:nth-child(1) { width: 30%; }
            .shipdoc-invoice-items th:nth-child(2), .shipdoc-invoice-items td:nth-child(2) { width: 35%; }
            .shipdoc-invoice-items th:nth-child(3), .shipdoc-invoice-items td:nth-child(3) { text-align: right; width: 19%; }
            .shipdoc-invoice-items th:nth-child(4), .shipdoc-invoice-items td:nth-child(4) { text-align: right; width: 16%; }
            .shipdoc-serial-list { display: block; overflow-wrap: anywhere; white-space: normal; word-break: break-word; }
            .shipdoc-serial-value { display: inline; }
            .shipdoc-serial-value:not(:last-child)::after { content: ","; }
            .shipdoc-highlight-row td { background: #fff3bf; }
            .shipdoc-invoice-memo { font-size: 12px; line-height: 1.25; overflow-wrap: anywhere; padding: 8px 10px; word-break: break-word; }
            .shipdoc-invoice-edit-input, .shipdoc-invoice-tax-select { border: 1px solid #777; box-sizing: border-box; font: inherit; max-width: 100%; min-width: 0; padding: 3px 4px; text-align: right; width: 100%; }
            .shipdoc-invoice-tax-select { background: #fff; }
            .shipdoc-invoice-memo-edit { display: grid; gap: 6px; }
            .shipdoc-invoice-memo-edit textarea { border: 1px solid #777; box-sizing: border-box; font: inherit; min-height: 74px; resize: vertical; width: 100%; }
            .shipdoc-summary-row { break-inside: avoid; display: grid; grid-template-columns: 1fr 35%; margin-top: -2px; page-break-inside: avoid; }
            .shipdoc-summary-spacer { border: 1px solid #000; border-right: 0; min-height: 32px; }
            .shipdoc-summary { border-bottom: 1px solid #000; border-right: 1px solid #000; box-sizing: border-box; margin: 0; min-width: 0; width: auto; }
            .shipdoc-summary div { align-items: center; border-left: 1px solid #000; border-top: 1px solid #000; box-sizing: border-box; display: grid; grid-template-columns: minmax(0, 1fr) 96px; min-height: 32px; overflow: hidden; }
            .shipdoc-summary span { font-family: Arial, Helvetica, sans-serif; font-size: 16px; font-weight: 700; min-width: 0; padding-left: 12px; white-space: nowrap; }
            .shipdoc-summary strong { display: block; font-size: 11px; font-weight: 400; min-width: 0; padding-right: 8px; text-align: right; white-space: nowrap; }
            .shipdoc-summary div:last-child span { font-size: 18px; }
          </style>
        </head>
        <body>${printNode.outerHTML}</body>
      </html>
    `);
    doc.close();
    setTimeout(runPrint, 100);
  };

  const closeCompletedTransferPrintJob = () => {
    setCompletedTransferPrintJob(null);
    setCompletedTransferPrintView('invoice');
  };

  const printCompletedTransferOutput = () => {
    if (completedTransferPrintView === 'invoice' && Number(completedTransferPrintJob?.invoice?.transactionTypeId) === 5) {
      printTransferPdf(completedTransferPrintJob.invoice).catch(() => setStatus('Could not load transfer PDF. Please try Print again.'));
      return;
    }
    if (completedTransferPrintView !== 'invoice') {
      printTransferLabel({
        location: completedTransferPrintJob?.destination,
        pickup: completedTransferPrintView === 'pickupLabel',
        docNo: completedTransferPrintJob?.docNo,
      });
      return;
    }
    const source = document.querySelector(`[data-transfer-print-output="${completedTransferPrintView}"]`);
    if (!source) return;

    const iframe = document.createElement('iframe');
    iframe.title = 'Transfer print output';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '816px';
    iframe.style.height = '1056px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => setTimeout(() => iframe.remove(), 250);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    const stylesheetMarkup = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join('');
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Transfer ${completedTransferPrintJob?.docNo || ''}</title>
          ${stylesheetMarkup}
          <style>
            @page { size: letter portrait; margin: 7mm; }
            html, body { background: #fff; margin: 0; padding: 0; }
            [data-transfer-print-output] { display: block !important; }
            .transfer-print-label { box-shadow: none !important; height: 6in !important; width: 4in !important; }
          </style>
        </head>
        <body>${source.outerHTML}</body>
      </html>
    `);
    doc.close();
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    }, 150);
  };

  const printOpenedTransferOutput = () => {
    if (openedTransferPrintView === 'invoice') {
      printTransfer();
      return;
    }

    printTransferLabel({
      location: transferToEmailInvoice(transfer)?.to,
      pickup: openedTransferPrintView === 'pickupLabel',
      docNo: transfer?.docNo,
    });
  };

  return (
    <>
      <TopNav />
      <ConfirmModal
        open={leaveConfirm.open}
        message="Current Transaction will be lost. Leave the page?"
        onConfirm={leaveConfirm.onConfirm}
        onCancel={() => setLeaveConfirm({ open: false, onConfirm: () => {} })}
      />
      <main className="inventory-container transfers-page">
        <h2 className="inventory-title">{pageTitle}</h2>

        <section className="transfer-search-card">
          <div className="transfer-search-tabs" role="tablist" aria-label="Transfer search options">
            {!salesOnly && (
              <>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'new'}
                  className={activeSearchTab === 'new' ? 'active' : ''}
                  onClick={() => changeSearchTab('new')}
                >
                  New Transfer
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'transfers'}
                  className={activeSearchTab === 'transfers' ? 'active' : ''}
                  onClick={() => changeSearchTab('transfers')}
                >
                  Transfers
                </button>
              </>
            )}
            {salesOnly ? (
              <>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'sale'}
                  className={activeSearchTab === 'sale' ? 'active' : ''}
                  onClick={() => changeSearchTab('sale')}
                >
                  New Sale
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'sales'}
                  className={activeSearchTab === 'sales' ? 'active' : ''}
                  onClick={() => changeSearchTab('sales')}
                >
                  Sales
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'serial'}
                  className={activeSearchTab === 'serial' ? 'active' : ''}
                  onClick={() => changeSearchTab('serial')}
                >
                  Find by Serial Number
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'adjustments'}
                  className={activeSearchTab === 'adjustments' ? 'active' : ''}
                  onClick={() => changeSearchTab('adjustments')}
                >
                  Adjustments
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'location'}
                  className={activeSearchTab === 'location' ? 'active' : ''}
                  onClick={() => changeSearchTab('location')}
                >
                  Find by Receiving Location
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'doc'}
                  className={activeSearchTab === 'doc' ? 'active' : ''}
                  onClick={() => changeSearchTab('doc')}
                >
                  Find by Doc #
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeSearchTab === 'vendor'}
                  className={activeSearchTab === 'vendor' ? 'active' : ''}
                  onClick={() => changeSearchTab('vendor')}
                >
                  Vendor
                </button>
              </>
            )}
          </div>

          {activeSearchTab === 'new' && (
            <div role="tabpanel">
              <form className="inventory-form transfer-toolbar transfer-new-toolbar transfer-labeled-toolbar" onSubmit={addSerialsToNewTransfer}>
                <div className="transfer-toolbar-field">
                  <label htmlFor="new-transfer-serial">Serial #</label>
                  <input
                    id="new-transfer-serial"
                    ref={newTransferSerialRef}
                    type="text"
                    placeholder="Serial #"
                    value={newTransferSerial}
                    onFocus={() => setStatus('')}
                    onChange={(e) => resetNewTransferSerialLookup(e.target.value)}
                    onBlur={() => {
                      if (skipNewTransferSerialBlurLookupRef.current) {
                        skipNewTransferSerialBlurLookupRef.current = false;
                        return;
                      }
                      lookupNewTransferSerial();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        skipNewTransferSerialBlurLookupRef.current = true;
                        e.currentTarget.blur();
                        lookupNewTransferSerial();
                      }
                    }}
                    autoFocus
                  />
                </div>
                <div className="transfer-toolbar-field">
                  <label htmlFor="new-transfer-from">Location From</label>
                  <TypeaheadSelect
                    id="new-transfer-from"
                    placeholder="Location From"
                    value={newTransferFrom}
                    onChange={setNewTransferFrom}
                    options={locationOptions}
                    openTypeahead={openTypeahead}
                    setOpenTypeahead={setOpenTypeahead}
                    disabled={!isNewTransferSerialReady || isNewTransferLocked}
                  />
                </div>
                <div className="transfer-toolbar-field">
                  <label htmlFor="new-transfer-to">Location To</label>
                  <TypeaheadSelect
                    id="new-transfer-to"
                    placeholder="Location To"
                    value={newTransferTo}
                    onChange={setNewTransferTo}
                    options={locationOptions}
                    openTypeahead={openTypeahead}
                    setOpenTypeahead={setOpenTypeahead}
                    disabled={!isNewTransferSerialReady || isNewTransferLocked}
                  />
                </div>
                <button type="submit" className="button small blue" disabled={serialLoading || !isNewTransferSerialReady || !canCreateTransfer}>
                  {serialLoading ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={cancelNewTransfer}
                  disabled={serialLoading || serialTransferLoading}
                >
                  Clear
                </button>
              </form>

              {newTransferQueue.length === 0 && newTransferSerialInfo && (
                <section className="inventory-card transfer-results repair-serial-panel">
                  <div className="transfer-results-header">
                    <h3>Serial Number Details</h3>
                  </div>
                  <div className="repair-serial-grid">
                    <div>
                      <span>Serial #</span>
                      <strong>{newTransferSerialInfo.serial || '-'}</strong>
                    </div>
                    <div>
                      <span>Item</span>
                      <strong>{newTransferSerialInfo.item || newTransferSerialInfo.itemName || '-'}</strong>
                    </div>
                    <div>
                      <span>Description</span>
                      <strong>{newTransferSerialInfo.description || newTransferSerialInfo.itemDescription || '-'}</strong>
                    </div>
                    <div>
                      <span>Current Location</span>
                      <strong>{newTransferSerialInfo.currentLocation || newTransferFrom || '-'}</strong>
                    </div>
                    <div>
                      <span>Price</span>
                      <strong>{money(newTransferSerialInfo.price)}</strong>
                    </div>
                    <div>
                      <span>Available Qty</span>
                      <strong>{formatNumber(newTransferSerialInfo.quantityBalance || newTransferSerialInfo.qty || 1)}</strong>
                    </div>
                  </div>
                </section>
              )}

              {newTransferQueue.length > 0 && (
                <section className="inventory-card transfer-results transfer-queue-card">
                  <div className="transfer-results-header">
                    <h3>
                      Queued Serial Numbers for {findTransferDestination(newTransferTo, newTransferToId)?.name || newTransferTo || 'Location To'}: {newTransferQueue.length}
                    </h3>
                    <div className="transfer-results-actions">
                      <div className="transfer-print-options" role="group" aria-label="Print options">
                        <span className="transfer-print-options-title">Print:</span>
                        <label>
                          <input
                            type="checkbox"
                            checked={newTransferPrintOptions.invoice}
                            onChange={(e) => setNewTransferPrintOptions((current) => ({
                              ...current,
                              invoice: e.target.checked,
                            }))}
                          />
                          Invoice
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={newTransferPrintOptions.shippingLabel}
                            onChange={(e) => setNewTransferPrintOptions((current) => ({
                              ...current,
                              shippingLabel: e.target.checked,
                            }))}
                          />
                          Delivery Label
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={newTransferPrintOptions.pickupLabel}
                            onChange={(e) => setNewTransferPrintOptions((current) => ({
                              ...current,
                              pickupLabel: e.target.checked,
                            }))}
                          />
                          Pick Up Label
                        </label>
                      </div>
                      <button
                        type="button"
                        className="button small blue"
                        onClick={completeNewTransfer}
                        disabled={serialTransferLoading || !canCreateTransfer}
                      >
                        {serialTransferLoading ? 'Transferring...' : 'Transfer'}
                      </button>
                      <button
                        type="button"
                        className="button small transfer-confirm-cancel"
                        onClick={cancelNewTransfer}
                        disabled={serialTransferLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="inventory-table-wrapper transfer-results-table-wrap">
                    <table className="inventory-table">
                      <thead>
                        <tr>
                          <th>Line #</th>
                          <th>Serial #</th>
                          <th>Item</th>
                          <th>Price</th>
                          <th>Qty</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {newTransferQueue.map((row, index) => {
                          const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
                          return (
                          <tr key={row.subLedgerId}>
                            <td>{index + 1}</td>
                            <td>{row.serial}</td>
                            <td>{row.item || row.description || '-'}</td>
                            <td>{money(row.price)}</td>
                            <td>{formatNumber(qty)}</td>
                            <td>
                              <button
                                type="button"
                                className="button small red transfer-remove-row"
                                onClick={() => openRemoveQueuedSerial(row)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3">Total</td>
                          <td className="transfer-queue-total-value">{money(newTransferQueueTotalPrice)}</td>
                          <td className="transfer-queue-total-value">{formatNumber(newTransferQueueTotalQty)}</td>
                          <td className="transfer-queue-prepared-by">Prepared By: {preparedBy || '-'}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="transfer-queue-memo">
                    <label htmlFor="new-transfer-comment">Comment</label>
                    <textarea
                      id="new-transfer-comment"
                      value={newTransferMemo}
                      onChange={(e) => setNewTransferMemo(e.target.value)}
                      rows="3"
                      placeholder="Comment for this transfer"
                    />
                  </div>
                </section>
              )}
            </div>
          )}

          {activeSearchTab === 'transfers' && (
            <div role="tabpanel">
              <form
                className="inventory-form transfer-toolbar transfer-sales-search-row"
                onSubmit={searchTransferList}
                aria-busy={transferListLoading}
              >
                <input
                  value={transferListSearch}
                  onChange={(e) => setTransferListSearch(e.target.value)}
                  placeholder="Search by location, vendor, serial number, or doc number"
                  aria-label="Search transfers"
                />
                <button
                  type="submit"
                  className="button small transfer-sales-clear-button"
                >
                  Clear
                </button>
                <select
                  value={transferListYear}
                  onChange={(e) => {
                    const year = e.target.value;
                    setTransferListYear(year);
                  }}
                  aria-label="Transfers Year"
                >
                  {salesYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </form>

              <div className="transfer-list-date-range">
                <label>
                  <span>From Date</span>
                  <input
                    type="date"
                    value={transferListDateRange.start}
                    max={transferListDateRange.end || undefined}
                    onChange={(event) => setTransferListDateRange((current) => ({
                      ...current,
                      start: event.target.value,
                    }))}
                  />
                </label>
                <label>
                  <span>To Date</span>
                  <input
                    type="date"
                    value={transferListDateRange.end}
                    min={transferListDateRange.start || undefined}
                    onChange={(event) => setTransferListDateRange((current) => ({
                      ...current,
                      end: event.target.value,
                    }))}
                  />
                </label>
                {(hasTransferListDateFilter || transferListDateRangeChanged) && (
                  <button
                    type="button"
                    className="button small transfer-list-date-clear"
                    onClick={searchTransferList}
                  >
                    Apply
                  </button>
                )}
              </div>

              <div className={`inventory-card transfer-results transfer-list-results ${transferListLoading ? 'loading' : ''}`}>
                {transferListLoading && (
                  <div className="transfer-table-loading">
                    <span></span>
                    <strong>Loading results...</strong>
                  </div>
                )}
                <table className="inventory-table transfer-list-table">
                  <colgroup>
                    <col className="transfer-list-doc-col" />
                    <col className="transfer-list-date-col" />
                    <col className="transfer-list-type-col" />
                    <col className="transfer-list-party-col" />
                    <col className="transfer-list-party-col" />
                    <col className="transfer-list-items-col" />
                    <col className="transfer-list-total-col" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Doc #</th>
                      <th>Date</th>
                      {renderTransferListFilterHeader('type')}
                      {renderTransferListFilterHeader('from')}
                      {renderTransferListFilterHeader('to')}
                      <th>Items</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransferListResults.map((row) => (
                      <tr
                        key={`${row.transactionTypeId || ''}-${row.docNo || row.id}`}
                        role="button"
                        tabIndex="0"
                        onClick={() => loadTransfer(null, String(row.docNo || row.id), { transactionTypeId: row.transactionTypeId })}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            loadTransfer(null, String(row.docNo || row.id), { transactionTypeId: row.transactionTypeId });
                          }
                        }}
                      >
                        <td>
                          <span className="repair-ro-link">
                            {row.docNo || row.id}
                          </span>
                        </td>
                        <td>{renderTransactionTimestamp(row.transferDate)}</td>
                        <td>{row.transactionType || '-'}</td>
                        <td>{row.fromName || '-'}</td>
                        <td>{row.toName || '-'}</td>
                        <td>{row.itemCount ? `${row.itemCount} item${Number(row.itemCount) === 1 ? '' : 's'}` : '-'}</td>
                        <td>{money(row.total)}</td>
                      </tr>
                    ))}
                    {filteredTransferListResults.length === 0 && (
                      <tr>
                        <td colSpan="7">{transferListLoading ? 'Searching...' : 'No transfers found.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeSearchTab === 'sale' && (
            <div role="tabpanel">
              <form className="inventory-form transfer-toolbar transfer-new-toolbar" onSubmit={addSerialToNewSale}>
                <input
                  ref={newSaleSerialRef}
                  type="text"
                  placeholder="Serial #"
                  value={newSaleSerial}
                  onChange={(e) => resetNewSaleSerialLookup(e.target.value)}
                  onBlur={() => {
                    if (salesOnly) lookupNewSaleSerial();
                  }}
                  onKeyDown={(e) => {
                    if (salesOnly && e.key === 'Enter') {
                      e.preventDefault();
                      lookupNewSaleSerial();
                    }
                  }}
                  autoFocus
                />
                <TypeaheadSelect
                  id="new-sale-customer"
                  placeholder="Customer"
                  value={newSaleCustomer}
                  onChange={setNewSaleCustomer}
                  options={newSaleCustomerOptions}
                  openTypeahead={openTypeahead}
                  setOpenTypeahead={setOpenTypeahead}
                  disabled={saleLoading || newSaleQueue.length > 0}
                  renderOption={renderSaleCustomerOption}
                />
                <button
                  type="button"
                  className="button sale-add-customer-button"
                  onClick={() => setRetailCustomerModalOpen(true)}
                  disabled={saleLoading || newSaleQueue.length > 0 || !canOpenCreateCustomer || !canCreateRetailCustomer}
                  title="Add retail customer"
                  aria-label="Add retail customer"
                >
                  +
                </button>
                <select
                  value={newSaleShippingAddress}
                  onChange={(e) => setNewSaleShippingAddress(e.target.value)}
                  disabled={saleLoading || !selectedNewSaleCustomer || newSaleQueue.length > 0}
                  aria-label="Shipping address"
                >
                  {newSaleAddressOptions.map((address) => (
                    <option key={address.key} value={address.key}>
                      {address.label || address.title || 'Shipping Address'}
                    </option>
                  ))}
                </select>
                <TypeaheadSelect
                  id="new-sale-from"
                  placeholder="From Location"
                  value={newSaleFrom}
                  onChange={setNewSaleFrom}
                  options={locationOptions}
                  openTypeahead={openTypeahead}
                  setOpenTypeahead={setOpenTypeahead}
                  disabled={!isNewSaleSerialReady || saleLoading}
                />
                <button type="submit" className="button small blue" disabled={saleLoading || !isNewSaleSerialReady || !canCreateSale}>
                  {saleLoading ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={clearNewSale}
                  disabled={saleLoading}
                >
                  Clear
                </button>
              </form>

              {salesOnly && newSaleQueue.length === 0 && newSaleSerialInfo && (
                <section className="inventory-card transfer-results repair-serial-panel">
                  <div className="transfer-results-header">
                    <h3>Serial Number Details</h3>
                  </div>
                  <div className="repair-serial-grid">
                    <div>
                      <span>Serial #</span>
                      <strong>{newSaleSerialInfo.serial || '-'}</strong>
                    </div>
                    <div>
                      <span>Item</span>
                      <strong>{newSaleSerialInfo.item || newSaleSerialInfo.itemName || '-'}</strong>
                    </div>
                    <div>
                      <span>Description</span>
                      <strong>{newSaleSerialInfo.description || newSaleSerialInfo.itemDescription || '-'}</strong>
                    </div>
                    <div>
                      <span>Current Location</span>
                      <strong>{newSaleSerialInfo.currentLocation || newSaleFrom || '-'}</strong>
                    </div>
                    <div>
                      <span>Price</span>
                      <strong>{money(newSaleSerialInfo.price)}</strong>
                    </div>
                    <div>
                      <span>Available Qty</span>
                      <strong>{formatNumber(newSaleSerialInfo.quantityBalance || newSaleSerialInfo.qty || 1)}</strong>
                    </div>
                  </div>
                </section>
              )}

              {newSaleQueue.length > 0 && (
                <section className="inventory-card transfer-results transfer-queue-card">
                  <div className="transfer-results-header">
                    <h3>
                      Queued Sale Serial Numbers for {getCustomerDisplayName(selectedNewSaleCustomer) || newSaleCustomer || 'Customer'} : {newSaleQueue.length}
                    </h3>
                    <button
                      type="button"
                      className="button small blue"
                      onClick={completeNewSale}
                      disabled={saleLoading || !canCreateSale}
                    >
                      {saleLoading ? 'Creating...' : 'Create Ship Doc'}
                    </button>
                  </div>
                  <div className="inventory-table-wrapper transfer-results-table-wrap">
                    <table className="inventory-table">
                      <thead>
                        <tr>
                          <th>Line #</th>
                          <th>Serial #</th>
                          <th>Item</th>
                          <th>Price</th>
                          <th>Qty</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {newSaleQueue.map((row, index) => {
                          const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
                          return (
                            <tr key={row.subLedgerId}>
                              <td>{index + 1}</td>
                              <td>{row.serial}</td>
                              <td>{row.item || row.description || '-'}</td>
                              <td>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  step="0.01"
                                  min="0"
                                  value={row.price ?? ''}
                                  onChange={(e) => updateSaleSerialPrice(row, e.target.value)}
                                  aria-label={`Sale price for serial ${row.serial}`}
                                />
                              </td>
                              <td>{formatNumber(qty)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="button small red transfer-remove-row"
                                  onClick={() => removeSaleSerial(row)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3">Subtotal</td>
                          <td className="transfer-queue-total-value">{money(newSaleQueueTotalPrice)}</td>
                          <td className="transfer-queue-total-value">{formatNumber(newSaleQueueTotalQty)}</td>
                          <td className="transfer-queue-prepared-by">Prepared By: {preparedBy || '-'}</td>
                        </tr>
                        <tr>
                          <td colSpan="3">
                            <label className="transfer-sale-tax-field">
                              <span>Tax</span>
                              <select
                                value={newSaleTaxRate}
                                onChange={(event) => setNewSaleTaxRate(event.target.value)}
                                disabled={saleLoading}
                                aria-label="Sales tax"
                              >
                                <option value="0">0%</option>
                                {preferencesTaxRate > 0 && (
                                  <option value={String(preferencesTaxRate)}>{String(preferencesTaxRate)}%</option>
                                )}
                              </select>
                            </label>
                          </td>
                          <td className="transfer-queue-total-value">{money(newSaleQueueTaxAmount)}</td>
                          <td></td>
                          <td></td>
                        </tr>
                        <tr>
                          <td colSpan="3">Total</td>
                          <td className="transfer-queue-total-value">{money(newSaleQueueInvoiceTotal)}</td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="transfer-queue-memo">
                    <label htmlFor="new-sale-memo">Memo</label>
                    <textarea
                      id="new-sale-memo"
                      value={newSaleMemo}
                      onChange={(e) => setNewSaleMemo(e.target.value)}
                      rows="3"
                      placeholder="Memo for this ship doc"
                    />
                  </div>
                </section>
              )}
            </div>
          )}

          {activeSearchTab === 'sales' && (
            <div role="tabpanel">
              <form
                className="inventory-form transfer-toolbar transfer-sales-search-row"
                onSubmit={searchSales}
                aria-busy={salesListLoading}
              >
                <input
                  value={salesSearch}
                  onChange={(e) => setSalesSearch(e.target.value)}
                  placeholder="Search by customer, serial number, or invoice number"
                  aria-label="Search sales"
                />
                <button
                  type="button"
                  className="button small transfer-sales-clear-button"
                  onClick={() => setSalesSearch('')}
                  disabled={!salesSearch.trim()}
                >
                  Clear
                </button>
                <select
                  value={salesYear}
                  onChange={(e) => {
                    const year = e.target.value;
                    setSalesYear(year);
                  }}
                  aria-label="Sales Year"
                >
                  {salesYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </form>

              <div className={`inventory-card transfer-results transfer-list-results ${salesListLoading ? 'loading' : ''}`}>
                {salesListLoading && (
                  <div className="transfer-table-loading">
                    <span></span>
                    <strong>Loading results...</strong>
                  </div>
                )}
                <table className="inventory-table transfer-list-table sales-list-table">
                  <colgroup>
                    <col className="transfer-list-doc-col" />
                    <col className="transfer-list-date-col" />
                    <col className="transfer-list-party-col" />
                    <col className="transfer-list-party-col" />
                    <col className="transfer-list-type-col" />
                    <col className="transfer-list-items-col" />
                    <col className="transfer-list-qty-col" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Serial</th>
                      <th>Status</th>
                      <th>Items</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesResults.map((sale) => (
                      <tr
                        key={sale.id}
                        role="button"
                        tabIndex="0"
                        onClick={() => loadTransfer(null, String(sale.docNo || sale.id), { transactionTypeId: 4 })}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            loadTransfer(null, String(sale.docNo || sale.id), { transactionTypeId: 4 });
                          }
                        }}
                      >
                        <td>
                          <span className="repair-ro-link">
                            {sale.docNo || sale.id}
                          </span>
                        </td>
                        <td>{renderTransactionTimestamp(sale.orderDate)}</td>
                        <td>{sale.customerName}</td>
                        <td>{sale.serial}</td>
                        <td>{sale.status}</td>
                        <td>{sale.warrantyStatus || '-'}</td>
                        <td>{money(sale.total)}</td>
                      </tr>
                    ))}
                    {salesResults.length === 0 && (
                      <tr>
                        <td colSpan="7">{salesListLoading ? 'Searching...' : 'No sales found.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeSearchTab === 'return' && (
            <div role="tabpanel">
              <form className="inventory-form transfer-toolbar transfer-new-toolbar" onSubmit={addSerialToCustomerReturn}>
                <TypeaheadSelect
                  id="customer-return-customer"
                  placeholder="Customer"
                  value={customerReturnCustomer}
                  onChange={handleCustomerReturnCustomerChange}
                  options={customerOptions}
                  openTypeahead={openTypeahead}
                  setOpenTypeahead={setOpenTypeahead}
                  disabled={isCustomerReturnLocked}
                />
                <TypeaheadSelect
                  id="customer-return-location"
                  placeholder="Return To Location"
                  value={customerReturnLocation}
                  onChange={setCustomerReturnLocation}
                  options={locationOptions}
                  openTypeahead={openTypeahead}
                  setOpenTypeahead={setOpenTypeahead}
                  disabled={isCustomerReturnLocked}
                />
                <input
                  type="text"
                  placeholder="Serial #"
                  value={customerReturnSerial}
                  onChange={(e) => setCustomerReturnSerial(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="button small blue" disabled={customerReturnLoading || !canQueueCustomerReturnSerial}>
                  {customerReturnLoading ? 'Adding...' : 'Add'}
                </button>
              </form>

              {customerReturnQueue.length > 0 && (
                <section className="inventory-card transfer-results transfer-queue-card">
                  <div className="transfer-results-header">
                    <h3>Queued Customer Return Serial Numbers</h3>
                    <div className="transfer-results-actions">
                      <button
                        type="button"
                        className="button small blue"
                        onClick={completeCustomerReturn}
                        disabled={customerReturnLoading || !canCreateReturn}
                      >
                        {customerReturnLoading ? 'Creating...' : 'Create Credit Memo'}
                      </button>
                      <button
                        type="button"
                        className="button small transfer-confirm-cancel"
                        onClick={cancelCustomerReturn}
                        disabled={customerReturnLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="inventory-table-wrapper transfer-results-table-wrap">
                    <table className="inventory-table">
                      <thead>
                        <tr>
                          <th>Line #</th>
                          <th>Serial #</th>
                          <th>Item</th>
                          <th>Price</th>
                          <th>Qty</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerReturnQueue.map((row, index) => {
                          const qty = Number(row.qty ?? row.quantity ?? 1) || 1;
                          return (
                            <tr key={`${row.sourceDocNo}-${row.itemId}-${row.serial}`}>
                              <td>{index + 1}</td>
                              <td>{row.serial}</td>
                              <td>{row.item || row.description || '-'}</td>
                              <td>{money(row.price)}</td>
                              <td>{formatNumber(qty)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="button small red transfer-remove-row"
                                  onClick={() => removeCustomerReturnSerial(row)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3">Total</td>
                          <td className="transfer-queue-total-value">{money(customerReturnQueueTotalPrice)}</td>
                          <td className="transfer-queue-total-value">{formatNumber(customerReturnQueueTotalQty)}</td>
                          <td className="transfer-queue-prepared-by">Prepared By: {preparedBy || '-'}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="transfer-queue-memo">
                    <label htmlFor="customer-return-memo">Memo</label>
                    <textarea
                      id="customer-return-memo"
                      value={customerReturnMemo}
                      onChange={(e) => setCustomerReturnMemo(e.target.value)}
                      rows="3"
                      placeholder="Memo for this credit memo"
                    />
                  </div>
                </section>
              )}
            </div>
          )}

          {activeSearchTab === 'doc' && (
            <form className="inventory-form transfer-toolbar" onSubmit={loadTransfer} role="tabpanel">
              <input
                type="text"
                placeholder="Doc #"
                value={docNo}
                onChange={(e) => setDocNo(e.target.value)}
                autoFocus
              />
              <button type="submit" className="button small blue" disabled={loading}>
                {loading ? 'Loading...' : 'Find'}
              </button>
            </form>
          )}

          {activeSearchTab === 'serial' && (
            <form className="inventory-form transfer-toolbar transfer-serial-search-form" onSubmit={searchBySerial} role="tabpanel">
              <input
                ref={serialSearchRef}
                type="text"
                placeholder="Serial #"
                value={serialNo}
                onChange={(e) => setSerialNo(e.target.value)}
                autoFocus
              />
              <button type="submit" className="button small blue" disabled={serialLoading}>
                {serialLoading ? 'Searching...' : 'Find'}
              </button>
            </form>
          )}

          {activeSearchTab === 'adjustments' && (
            <div className="transfer-adjustments-panel" role="tabpanel">
              <form className="inventory-form transfer-toolbar transfer-adjustment-toolbar" onSubmit={loadAdjustmentSerial}>
                <input
                  id={ADJUSTMENT_SERIAL_SEARCH_INPUT_ID}
                  type="text"
                  placeholder="Serial #"
                  value={adjustmentSerial}
                  onChange={(e) => setAdjustmentSerial(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="button small blue" disabled={adjustmentLoading}>
                  {adjustmentLoading ? 'Finding...' : 'Find'}
                </button>
                <button
                  type="button"
                  className="button small transfer-adjustment-reset"
                  onClick={resetAdjustmentSearch}
                  disabled={adjustmentLoading}
                >
                  Reset
                </button>
              </form>

              {adjustmentSource && (
                <section className="inventory-card transfer-adjustment-card">
                  <div className="transfer-adjustment-grid">
                    <div className="transfer-adjustment-current">
                      <h3>Current Serial</h3>
                      <dl>
                        <div>
                          <dt>Serial #</dt>
                          <dd>{adjustmentSource.serial}</dd>
                        </div>
                        <div>
                          <dt>Current Item</dt>
                          <dd>{adjustmentSource.item || adjustmentSource.description || '-'}</dd>
                        </div>
                        <div>
                          <dt>Location</dt>
                          <dd>{adjustmentSource.shipTo || adjustmentSource.location || '-'}</dd>
                        </div>
                        <div>
                          <dt>Source Doc #</dt>
                          <dd>{adjustmentSource.sourceDocNo || '-'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="transfer-adjustment-target">
                      <h3>New Item</h3>
                      <div className="transfer-adjustment-item-search">
                        <input
                          type="text"
                          placeholder="Search item by name or number"
                          value={adjustmentItemQuery}
                          onChange={(e) => {
                            setAdjustmentItemQuery(e.target.value);
                            setAdjustmentTargetItem(null);
                          }}
                          autoComplete="off"
                        />
                        {adjustmentTargetItem && (
                          <button
                            type="button"
                            className="button small transfer-adjustment-clear"
                            onClick={clearAdjustmentItem}
                          >
                            Clear
                          </button>
                        )}
                        {!adjustmentTargetItem && adjustmentItemResults.length > 0 && (
                          <div className="transfer-adjustment-item-results">
                            {adjustmentItemResults.map((item) => (
                              <button
                                type="button"
                                key={item.id}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  selectAdjustmentItem(item);
                                }}
                              >
                                <strong>{item.name || item.fullName}</strong>
                                <span>
                                  #{item.id}
                                  {item.listId ? ` | ${item.listId}` : ''}
                                  {item.capSize ? ` | Cap ${item.capSize}` : ''}
                                  {item.color ? ` | ${item.color}` : ''}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        {!adjustmentTargetItem && adjustmentItemLoading && (
                          <div className="transfer-adjustment-item-loading">Searching...</div>
                        )}
                      </div>

                      {adjustmentTargetItem && (
                        <div className="transfer-adjustment-selected">
                          <strong>{adjustmentTargetItem.name || adjustmentTargetItem.fullName}</strong>
                          <span>Item #{adjustmentTargetItem.id}</span>
                          {adjustmentTargetItem.description && <span>{adjustmentTargetItem.description}</span>}
                        </div>
                      )}

                      <label className="transfer-adjustment-memo">
                        Memo
                        <textarea
                          value={adjustmentMemo}
                          onChange={(e) => setAdjustmentMemo(e.target.value)}
                          rows="3"
                          placeholder="Reason for adjustment"
                        />
                      </label>

                      <button
                        type="button"
                        className="button small blue transfer-adjustment-submit"
                        onClick={submitAdjustment}
                        disabled={adjustmentLoading || !adjustmentTargetItem || !canEditInventory}
                      >
                        {adjustmentLoading ? 'Saving...' : 'Reassign Serial'}
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          )}

          {activeSearchTab === 'location' && (
            <form className="inventory-form transfer-toolbar" onSubmit={searchByLocation} role="tabpanel">
              <TypeaheadSelect
                id="location"
                placeholder="Receiving Location"
                value={selectedLocation}
                onChange={setSelectedLocation}
                options={locationOptions}
                openTypeahead={openTypeahead}
                setOpenTypeahead={setOpenTypeahead}
                className="transfer-serial-location-select"
                onEnter={(e, location) => {
                  e?.preventDefault();
                  setVendorYear(currentYear);
                  searchTransfers({ location, yearOverride: currentYear });
                }}
              />
              <button type="submit" className="button small blue" disabled={vendorLoading}>
                {vendorLoading ? 'Searching...' : 'Find'}
              </button>
            </form>
          )}

          {activeSearchTab === 'vendor' && (
            <form className="inventory-form transfer-toolbar" onSubmit={searchByVendor} role="tabpanel">
              <TypeaheadSelect
                id="vendor"
                placeholder="Vendor"
                value={selectedVendor}
                onChange={setSelectedVendor}
                options={vendorOptions}
                openTypeahead={openTypeahead}
                setOpenTypeahead={setOpenTypeahead}
                onEnter={(e, vendor) => {
                  e?.preventDefault();
                  setVendorYear(currentYear);
                  searchTransfers({ vendor, yearOverride: currentYear });
                }}
              />
              <button type="submit" className="button small blue" disabled={vendorLoading}>
                {vendorLoading ? 'Searching...' : 'Find'}
              </button>
            </form>
          )}
        </section>

        {activeSearchTab === 'serial' && serialResults.length > 0 && (
          <section className="inventory-card transfer-results transfer-serial-results">
            <div className="transfer-results-header">
              <h3>Serial Number Transaction History</h3>
              {serialCurrentLocation && (
                <div className="transfer-current-location">
                  <span>Current Location</span>
                  <strong>{serialCurrentLocation}</strong>
                </div>
              )}
            </div>
            <div className="transfer-serial-action">
              <TypeaheadSelect
                id="serial-location"
                placeholder="Transfer To Location"
                value={serialDestination}
                onChange={setSerialDestination}
                options={locationOptions}
                openTypeahead={openTypeahead}
                setOpenTypeahead={setOpenTypeahead}
                className="transfer-serial-location-select"
              />
                <button
                  type="button"
                  className="button small blue"
                  onClick={transferSelectedSerial}
                  disabled={serialTransferLoading || selectedSerialRowIds.length === 0 || !canCreateTransfer}
                >
                  {serialTransferLoading ? 'Transferring...' : 'Transfer'}
                </button>
            </div>
            <div className="inventory-table-wrapper transfer-results-table-wrap">
              <table className="inventory-table transfer-serial-history-table">
                <thead>
                  <tr>
                    <th>Doc No</th>
                    <th>Transaction Type</th>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {serialResults.map((row) => {
                    const canOpenSerialDoc = row.canOpenDocument !== false;
                    const openSerialDoc = () => openHistoryDocument(row);

                    return (
                    <tr
                      key={`${row.transactionTypeId || ''}-${row.sourceDocNo || ''}-${row.subLedgerId || row.itemId || ''}-${row.sourceDate || ''}`}
                      className={canOpenSerialDoc ? 'transfer-clickable-row' : ''}
                      onClick={canOpenSerialDoc ? openSerialDoc : undefined}
                      onKeyDown={(event) => {
                        if (canOpenSerialDoc && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          openSerialDoc();
                        }
                      }}
                      role={canOpenSerialDoc ? 'button' : undefined}
                      tabIndex={canOpenSerialDoc ? 0 : undefined}
                    >
                      <td>
                        {canOpenSerialDoc ? (
                          <button
                            type="button"
                            className="transfer-serial-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              openSerialDoc();
                            }}
                          >
                            {row.sourceDocNo}
                          </button>
                        ) : (
                          row.sourceDocNo
                        )}
                      </td>
                      <td>{row.transactionType || row.transactionTypeId || ''}</td>
                      <td>{renderTransactionTimestamp(row.sourceDate)}</td>
                      <td>
                        {Number(row.transactionTypeId) === 5
                          ? (row.name || [row.shipFrom, row.shipTo].filter(Boolean).join('/') || row.location || '')
                          : (row.shipTo || row.name || row.location || row.vendor || '')}
                      </td>
                      <td>
                        {[4, 5].includes(Number(row.transactionTypeId)) && canDeleteTransferRow(row) && (
                          <button
                            type="button"
                            className="button small red transfer-delete-doc-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDeleteTransfer(row);
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeSearchTab === 'adjustments' && adjustmentHistory.length > 0 && (
          <section className="inventory-card transfer-results transfer-adjustment-history">
            <div className="transfer-results-header">
              <h3>Serial # History</h3>
            </div>
            <div className="inventory-table-wrapper transfer-results-table-wrap">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Doc No</th>
                    <th>Tran. Type</th>
                    <th>Date</th>
                    <th>Item</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustmentHistory.map((row) => {
                    const canOpenSerialDoc = row.canOpenDocument !== false;
                    const openSerialDoc = () => openHistoryDocument(row);

                    return (
                      <tr
                        key={row.subLedgerId || `${row.sourceDocNo}-${row.transactionTypeId}`}
                        className={canOpenSerialDoc ? 'transfer-clickable-row' : ''}
                        onClick={canOpenSerialDoc ? openSerialDoc : undefined}
                        onKeyDown={(event) => {
                          if (canOpenSerialDoc && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            openSerialDoc();
                          }
                        }}
                        role={canOpenSerialDoc ? 'button' : undefined}
                        tabIndex={canOpenSerialDoc ? 0 : undefined}
                      >
                        <td>
                          {canOpenSerialDoc ? (
                            <button
                              type="button"
                              className="transfer-serial-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                openSerialDoc();
                              }}
                            >
                              {row.sourceDocNo}
                            </button>
                          ) : (
                            row.sourceDocNo
                          )}
                        </td>
                        <td>{row.transactionType || row.transactionTypeId || ''}</td>
                        <td>{renderTransactionTimestamp(row.sourceDate)}</td>
                        <td>{row.item || row.description || ''}</td>
                        <td>{row.name || row.location || row.vendor || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeSearchTab === 'adjustments' && (
          <section className="inventory-card transfer-results transfer-adjustment-list">
            <div className="transfer-results-header transfer-adjustment-list-header">
              <h3>Adjustments</h3>
              <label>
                Date
                <select
                  value={adjustmentDatePreset}
                  onChange={(event) => changeAdjustmentDatePreset(event.target.value)}
                  disabled={adjustmentListLoading}
                >
                  {ADJUSTMENT_DATE_PRESETS.map((preset) => (
                    <option key={preset} value={preset}>{preset}</option>
                  ))}
                </select>
              </label>
            </div>
            {adjustmentListError && (
              <p className="transfer-adjustment-list-error">{adjustmentListError}</p>
            )}
            <div className="inventory-table-wrapper transfer-results-table-wrap">
              <table className="inventory-table transfer-adjustment-list-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Doc No</th>
                    <th>Serial #</th>
                    <th>From Item</th>
                    <th>To Item</th>
                    <th>Location</th>
                    <th>Entered By</th>
                    <th>Memo</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustmentList.map((row) => (
                    <tr key={row.docNo || `${row.adjustmentDate}-${row.serial}`}>
                      <td>{renderTransactionTimestamp(row.adjustmentDate)}</td>
                      <td>{row.docNo || '-'}</td>
                      <td>{row.serial || '-'}</td>
                      <td>{row.sourceItem || '-'}</td>
                      <td>{row.targetItem || '-'}</td>
                      <td>{row.location || '-'}</td>
                      <td>{row.enteredBy || '-'}</td>
                      <td>{row.memo || '-'}</td>
                    </tr>
                  ))}
                  {adjustmentList.length === 0 && (
                    <tr>
                      <td colSpan="8">
                        {adjustmentListLoading ? 'Loading adjustments...' : 'No adjustments found for this date range.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {['location', 'vendor'].includes(activeSearchTab) && vendorResults.length > 0 && (
          <section className="inventory-card transfer-results">
            <div className="transfer-results-header">
              <h3>Transfer Results</h3>
              <label>
                Year
                <select
                  value={vendorYear}
                  onChange={(e) => {
                    const year = e.target.value;
                    setVendorYear(year);
                    searchTransfers({ ...lastSearchFilters, yearOverride: year });
                  }}
                >
                  {displayedVendorYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="inventory-table-wrapper transfer-results-table-wrap">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Doc #</th>
                    <th>Receive Date</th>
                    <th>Vendor</th>
                    <th>Receive Location</th>
                    <th>Items</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorResults.map((row) => (
                    <tr
                      key={row.docNo}
                      className={Number(transfer?.docNo) === Number(row.docNo) ? 'selected' : ''}
                      onClick={() => loadTransfer(null, row.docNo, { highlightSerial: '' })}
                    >
                      <td>{row.docNo}</td>
                      <td>{formatDateInput(row.receiveDate)}</td>
                      <td>{row.vendor}</td>
                      <td>{row.receiveLocation}</td>
                      <td>{row.itemCount}</td>
                      <td>{formatNumber(row.totalQuantity)}</td>
                    </tr>
                  ))}
                  {vendorResults.length === 0 && (
                    <tr>
                      <td colSpan="6">No vendor results for this year.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {transfer && (
          <div
            className={transferUsesInvoiceDocument ? 'invoice-modal-overlay print-modal-overlay' : 'transfer-modal-overlay'}
            onClick={closeTransferModal}
          >
            <div
              className={transferUsesInvoiceDocument ? 'invoice-modal print-modal transfer-invoice-modal' : 'transfer-modal'}
              onClick={(e) => e.stopPropagation()}
            >
              {!transferUsesInvoiceDocument && (
                <button
                  type="button"
                  className="transfer-modal-close"
                  onClick={closeTransferModal}
                >
                  x
                </button>
              )}

              {transferUsesInvoiceDocument ? (
                <section className="shipdoc-invoice-workspace">
                  <div className="invoice-modal-header">
                    <div className="transfer-opened-print-heading">
                      <strong>{getTransferInvoiceTitle(transfer)} #{transfer.docNo}</strong>
                      <div className="transfer-output-tabs" role="tablist" aria-label="Transfer print documents">
                        {[
                          ['invoice', 'Invoice'],
                          ['shippingLabel', 'Delivery Label'],
                          ['pickupLabel', 'Pick Up Label'],
                        ].map(([view, label]) => (
                          <button
                            key={view}
                            type="button"
                            role="tab"
                            aria-selected={openedTransferPrintView === view}
                            className={openedTransferPrintView === view ? 'active' : ''}
                            onClick={() => setOpenedTransferPrintView(view)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="invoice-modal-actions">
                      <button type="button" className="button small blue" onClick={printOpenedTransferOutput}>
                        Print {openedTransferPrintView === 'invoice' ? 'Invoice' : openedTransferPrintView === 'shippingLabel' ? 'Delivery Label' : 'Pick Up Label'}
                      </button>
                      <InvoicePrintEmailActions
                        invoice={transferToEmailInvoice(transfer)}
                        showPrint={false}
                        loading={invoiceEmailLoading}
                        onLoadingChange={setInvoiceEmailLoading}
                        successMessage={(to) => `${getTransferInvoiceTitle(transfer)} #${transfer.docNo} emailed to ${to}.`}
                        onError={setStatus}
                      />
                      {canEditOpenedTransferTax && openedTransferPrintView === 'invoice' && (
                        <button
                          type="button"
                          className="button small yellow"
                          onClick={() => (openedTransferInvoiceEditing
                            ? saveShippingInvoiceEditor()
                            : openShippingInvoiceEditor('transfer', transfer))}
                          disabled={shippingInvoiceEditor.saving}
                        >
                          {openedTransferInvoiceEditing
                            ? shippingInvoiceEditor.saving ? 'Saving...' : 'Save Invoice'
                            : 'Edit Invoice'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="button small red"
                        onClick={openedTransferInvoiceEditing ? cancelShippingInvoiceEditor : closeTransferModal}
                        disabled={openedTransferInvoiceEditing && shippingInvoiceEditor.saving}
                      >
                        {openedTransferInvoiceEditing ? 'Cancel' : 'Close'}
                      </button>
                    </div>
                  </div>
                  {openedTransferPrintView === 'invoice' && (
                    <>
                      {openedTransferInvoiceEditing && shippingInvoiceEditor.error && (
                        <p className="shipdoc-invoice-edit-error">{shippingInvoiceEditor.error}</p>
                      )}
                      <ShipDocInvoiceDocument
                        transfer={transfer}
                        highlightSerial={transferHighlightSerial}
                        editable={openedTransferInvoiceEditing}
                        editDraft={openedTransferInvoiceEditing ? shippingInvoiceEditor.draft : null}
                        taxOptions={activeInvoiceTaxOptions}
                        billToLookup={openedTransferInvoiceEditing ? {
                          query: shippingInvoiceEditor.draft?.customerQuery || '',
                          open: shippingInvoiceCustomerLookupOpen,
                          options: shippingInvoiceCustomerLookupOptions,
                          activeIndex: shippingInvoiceCustomerLookupIndex,
                          onQueryChange: updateShippingInvoiceCustomerQuery,
                          onFocus: () => setShippingInvoiceCustomerLookupOpen(true),
                          onBlur: () => setTimeout(() => setShippingInvoiceCustomerLookupOpen(false), 120),
                          onKeyDown: handleShippingInvoiceCustomerLookupKeyDown,
                          onSelect: selectShippingInvoiceCustomer,
                          onActiveIndexChange: setShippingInvoiceCustomerLookupIndex,
                          getLabel: getCustomerDisplayName,
                          getMeta: getCustomerPhone,
                        } : null}
                        onDraftItemChange={updateShippingInvoiceDraftItem}
                        onDraftMemoChange={updateShippingInvoiceDraftMemo}
                        onDraftTaxRateChange={updateShippingInvoiceDraftTaxRate}
                      />
                      {salesOnly && canDeleteSale && (
                        <div className="invoice-modal-bottom-actions">
                          <button
                            type="button"
                            className="button small red"
                            onClick={() => openDeleteShippingInvoice(transfer.docNo)}
                            disabled={shippingInvoiceEditor.saving}
                          >
                            Delete Invoice
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {openedTransferPrintView === 'shippingLabel' && (
                    <div className="transfer-output-preview transfer-opened-label-preview" data-open-transfer-print-output="shippingLabel">
                      <TransferPrintLabel location={transferToEmailInvoice(transfer).to} />
                    </div>
                  )}
                  {openedTransferPrintView === 'pickupLabel' && (
                    <div className="transfer-output-preview transfer-opened-label-preview" data-open-transfer-print-output="pickupLabel">
                      <TransferPrintLabel location={transferToEmailInvoice(transfer).to} pickup />
                    </div>
                  )}
                </section>
              ) : (
              <section className="inventory-card transfer-workspace">
                <div className="transfer-header">
                  <div className="transfer-field-group transfer-party-fields">
                    <label>
                      From Location
                      <input value={transfer.fromLocation || transfer.vendor || ''} readOnly />
                    </label>
                    <label>
                      Pay To
                      <input value={transfer.payTo || ''} readOnly />
                    </label>
                    <label>
                      Receive To Location
                      <input value={transfer.receiveLocation || ''} readOnly />
                    </label>
                  </div>

                  <div className="transfer-doc-card">
                    <div className="transfer-modal-action-row">
                      <button type="button" className="button small blue transfer-print-button" onClick={printTransfer}>
                        Print
                      </button>
                      <InvoicePrintEmailActions
                        invoice={transferToEmailInvoice(transfer)}
                        showPrint={false}
                        loading={invoiceEmailLoading}
                        onLoadingChange={setInvoiceEmailLoading}
                        successMessage={(to) => `${transfer.documentTitle || 'Transfer'} #${transfer.docNo} emailed to ${to}.`}
                        onError={setStatus}
                      />
                      <button type="button" className="button small red transfer-print-button" onClick={closeTransferModal}>
                        Close
                      </button>
                    </div>
                    <h2>Receive Items</h2>
                    <div className="transfer-doc-grid">
                      <label>
                        Doc #
                        <input value={transfer.docNo || ''} readOnly />
                      </label>
                      <label>
                        Receive Date
                        <input type="date" value={formatDateInput(transfer.receiveDate)} readOnly />
                      </label>
                      <span className="transfer-note">Not Recorded in QuickBooks</span>
                      <label className="transfer-check">
                        <input type="checkbox" checked={Boolean(transfer.received)} readOnly />
                        Received
                      </label>
                      <label className="transfer-check">
                        <input type="checkbox" checked={Boolean(transfer.ncOnly)} readOnly />
                        NC Only
                      </label>
                    </div>
                  </div>
                </div>

                <div className="transfer-meta-row">
                  <label>
                    Expected Date
                    <input type="date" value={formatDateInput(transfer.expectedDate)} readOnly />
                  </label>
                  <label>
                    # Pckg
                    <input value={itemCount} readOnly />
                  </label>
                </div>

                <div className="transfer-table-wrap">
                  <table className="inventory-table transfer-table">
                    <thead>
                      <tr>
                        <th>Line #</th>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Serial #</th>
                        <th>Vend Part #</th>
                        <th>Qty</th>
                        <th>Ordered</th>
                        <th>Back Order</th>
                        <th>UOM</th>
                        <th>Cost Per</th>
                        <th>Total Cost</th>
                        <th>PO #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedItems.map((item) => {
                        const rowSerials = item.serials?.length ? item.serials : [item.serial].filter(Boolean);
                        const isHighlighted = highlightedTransferSerial
                          && rowSerials.some((serial) => (
                            String(serial || '').trim().toLowerCase() === highlightedTransferSerial
                          ));

                        return (
                          <tr
                            key={item.id}
                            ref={isHighlighted ? highlightedTransferRowRef : null}
                            className={isHighlighted ? 'transfer-highlight-row' : ''}
                          >
                            <td>{item.lineNo}</td>
                            <td>{item.item}</td>
                            <td>{item.description}</td>
                            <td>
                              <span data-print-serials={rowSerials.join('|')}>
                                <SerialList
                                  serials={rowSerials}
                                  highlightedSerial={isHighlighted ? transferHighlightSerial : ''}
                                />
                              </span>
                            </td>
                            <td>{item.vendorPart}</td>
                            <td>{formatNumber(item.quantity)}</td>
                            <td>{formatNumber(item.ordered)}</td>
                            <td>{formatNumber(item.backOrder)}</td>
                            <td>{item.uom}</td>
                            <td>{money(item.costPer)}</td>
                            <td>{money(item.totalCost)}</td>
                            <td>{item.poNo}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="transfer-footer">
                  <span>Subtotal&nbsp; {money(transfer.subtotal)}</span>
                  <span>Charges&nbsp; {money(transfer.charges)}</span>
                  <strong>Total&nbsp; {money(transfer.total)}</strong>
                </div>
              </section>
              )}

              {serialPopup && (
                <div
                  className="transfer-serial-modal-overlay"
                  onClick={() => {
                    setSerialPopup(null);
                    setSelectedPopupSerials([]);
                    setPopupSerialDestination('');
                  }}
                >
                  <div className="transfer-serial-modal" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="transfer-modal-close"
                      onClick={() => {
                        setSerialPopup(null);
                        setSelectedPopupSerials([]);
                        setPopupSerialDestination('');
                      }}
                    >
                      x
                    </button>
                    <div className="transfer-serial-modal-header">
                      <div>
                        <h3>Serial Numbers</h3>
                        <p>{serialPopup.item}</p>
                      </div>
                      {selectedPopupSerials.length > 0 && (
                        <div className="transfer-serial-popup-actions">
                          <TypeaheadSelect
                            id="popup-serial-location"
                            placeholder="Transfer To Location"
                            value={popupSerialDestination}
                            onChange={setPopupSerialDestination}
                            options={locationOptions}
                            openTypeahead={openTypeahead}
                            setOpenTypeahead={setOpenTypeahead}
                            className="transfer-serial-location-select"
                          />
                          <button
                            type="button"
                            className="button small blue transfer-serial-modal-transfer"
                            onClick={transferPopupSerials}
                            disabled={serialTransferLoading || !canCreateTransfer}
                          >
                            {serialTransferLoading ? 'Transferring...' : 'Transfer'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="transfer-serial-list">
                      {serialPopup.serials.map((serial) => (
                        <label key={serial} className="transfer-serial-option">
                          <input
                            type="checkbox"
                            checked={selectedPopupSerials.includes(serial)}
                            onChange={() => {
                              setSelectedPopupSerials((current) => (
                                current.includes(serial)
                                  ? current.filter((item) => item !== serial)
                                  : [...current, serial]
                              ));
                            }}
                          />
                          <span>{serial}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {completedTransferInvoice && (
          <div
            className="invoice-modal-overlay print-modal-overlay"
            onClick={closeCompletedTransferInvoice}
          >
            <div className="invoice-modal print-modal" onClick={(e) => e.stopPropagation()}>
              <div className="invoice-modal-header">
                <div className="transfer-opened-print-heading">
                  <h3>{completedTransferInvoice.documentTitle || completedTransferInvoice.type || 'Invoice'} #{completedTransferInvoice.invoiceNo}</h3>
                  {completedTransferIsRepairInvoice && (
                    <div className="transfer-output-tabs" role="tablist" aria-label="Repair invoice print documents">
                      {[
                        ['invoice', 'Invoice'],
                        ['shippingLabel', 'Delivery Label'],
                        ['pickupLabel', 'Pick Up Label'],
                      ].map(([view, label]) => (
                        <button
                          key={view}
                          type="button"
                          role="tab"
                          aria-selected={completedRepairPrintView === view}
                          className={completedRepairPrintView === view ? 'active' : ''}
                          onClick={() => setCompletedRepairPrintView(view)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="invoice-modal-actions">
                  <InvoicePrintEmailActions
                    invoice={completedTransferInvoice}
                    loading={invoiceEmailLoading}
                    onLoadingChange={setInvoiceEmailLoading}
                    onPrint={completedTransferIsRepairInvoice ? printCompletedRepairOutput : undefined}
                    printLabel={completedTransferIsRepairInvoice
                      ? `Print ${completedRepairPrintView === 'invoice'
                        ? 'Invoice'
                        : completedRepairPrintView === 'shippingLabel'
                          ? 'Delivery Label'
                          : 'Pick Up Label'}`
                      : 'Print'}
                    successMessage={(to) => `Invoice #${completedTransferInvoice.invoiceNo} emailed to ${to}.`}
                    onError={setStatus}
                  />
                  {canEditCompletedTransferTax && !completedTransferIsRepairInvoice && (
                    <button
                      type="button"
                      className="button small yellow"
                      onClick={() => (completedTransferInvoiceEditing
                        ? saveShippingInvoiceEditor()
                        : openShippingInvoiceEditor('completed', completedTransferInvoice))}
                      disabled={shippingInvoiceEditor.saving}
                    >
                      {completedTransferInvoiceEditing
                        ? shippingInvoiceEditor.saving ? 'Saving...' : 'Save Invoice'
                        : 'Edit Invoice'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="button small red"
                    onClick={completedTransferInvoiceEditing ? cancelShippingInvoiceEditor : closeCompletedTransferInvoice}
                    disabled={completedTransferInvoiceEditing && shippingInvoiceEditor.saving}
                  >
                    {completedTransferInvoiceEditing ? 'Cancel' : 'Close'}
                  </button>
                </div>
              </div>
              {completedTransferIsRepairInvoice && completedRepairPrintView === 'invoice' ? (
                <ShipDocInvoiceDocument invoice={completedTransferInvoice} />
              ) : !completedTransferIsRepairInvoice ? (
                <>
                  {completedTransferInvoiceEditing && shippingInvoiceEditor.error && (
                    <p className="shipdoc-invoice-edit-error">{shippingInvoiceEditor.error}</p>
                  )}
                  <ShipDocInvoiceDocument
                    invoice={completedTransferInvoice}
                    highlightSerial={completedTransferHighlightSerial}
                    editable={completedTransferInvoiceEditing}
                    editDraft={completedTransferInvoiceEditing ? shippingInvoiceEditor.draft : null}
                    taxOptions={activeInvoiceTaxOptions}
                    billToLookup={completedTransferInvoiceEditing ? {
                      query: shippingInvoiceEditor.draft?.customerQuery || '',
                      open: shippingInvoiceCustomerLookupOpen,
                      options: shippingInvoiceCustomerLookupOptions,
                      activeIndex: shippingInvoiceCustomerLookupIndex,
                      onQueryChange: updateShippingInvoiceCustomerQuery,
                      onFocus: () => setShippingInvoiceCustomerLookupOpen(true),
                      onBlur: () => setTimeout(() => setShippingInvoiceCustomerLookupOpen(false), 120),
                      onKeyDown: handleShippingInvoiceCustomerLookupKeyDown,
                      onSelect: selectShippingInvoiceCustomer,
                      onActiveIndexChange: setShippingInvoiceCustomerLookupIndex,
                      getLabel: getCustomerDisplayName,
                      getMeta: getCustomerPhone,
                    } : null}
                    onDraftItemChange={updateShippingInvoiceDraftItem}
                    onDraftMemoChange={updateShippingInvoiceDraftMemo}
                    onDraftTaxRateChange={updateShippingInvoiceDraftTaxRate}
                  />
                  {salesOnly && canDeleteSale && (
                    <div className="invoice-modal-bottom-actions">
                      <button
                        type="button"
                        className="button small red"
                        onClick={() => openDeleteShippingInvoice(completedTransferInvoice.invoiceNo)}
                        disabled={shippingInvoiceEditor.saving}
                      >
                        Delete Invoice
                      </button>
                    </div>
                  )}
                </>
              ) : null}
              {completedTransferIsRepairInvoice && completedRepairPrintView === 'shippingLabel' && (
                <div className="transfer-output-preview transfer-opened-label-preview">
                  <TransferPrintLabel location={completedTransferInvoice.to} services />
                </div>
              )}
              {completedTransferIsRepairInvoice && completedRepairPrintView === 'pickupLabel' && (
                <div className="transfer-output-preview transfer-opened-label-preview">
                  <TransferPrintLabel location={completedTransferInvoice.to} pickup services />
                </div>
              )}
            </div>
          </div>
        )}
        {completedTransferPrintJob && (
          <div className="invoice-modal-overlay print-modal-overlay" onClick={closeCompletedTransferPrintJob}>
            <div className="invoice-modal print-modal transfer-output-modal" onClick={(e) => e.stopPropagation()}>
              <div className="invoice-modal-header transfer-output-modal-header">
                <div>
                  <h3>Print Transfer #{completedTransferPrintJob.docNo}</h3>
                  <div className="transfer-output-tabs" role="tablist" aria-label="Transfer print documents">
                    {completedTransferPrintJob.views.map((view) => (
                      <button
                        key={view}
                        type="button"
                        role="tab"
                        aria-selected={completedTransferPrintView === view}
                        className={completedTransferPrintView === view ? 'active' : ''}
                        onClick={() => setCompletedTransferPrintView(view)}
                      >
                        {view === 'invoice' ? 'Invoice' : view === 'shippingLabel' ? 'Delivery Label' : 'Pick Up Label'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="invoice-modal-actions">
                  <button type="button" className="button small blue" onClick={printCompletedTransferOutput}>
                    Print {completedTransferPrintView === 'invoice' ? 'Invoice' : completedTransferPrintView === 'shippingLabel' ? 'Delivery Label' : 'Pick Up Label'}
                  </button>
                  <button type="button" className="button small red" onClick={closeCompletedTransferPrintJob}>
                    Close
                  </button>
                </div>
              </div>
              <div className="transfer-output-preview">
                {completedTransferPrintView === 'invoice' && completedTransferPrintJob.invoice && (
                  <div data-transfer-print-output="invoice">
                    <ShipDocInvoiceDocument invoice={completedTransferPrintJob.invoice} />
                  </div>
                )}
                {completedTransferPrintView === 'shippingLabel' && (
                  <div data-transfer-print-output="shippingLabel">
                    <TransferPrintLabel location={completedTransferPrintJob.destination} />
                  </div>
                )}
                {completedTransferPrintView === 'pickupLabel' && (
                  <div data-transfer-print-output="pickupLabel">
                    <TransferPrintLabel location={completedTransferPrintJob.destination} pickup />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {transferLimitsModal && (
          <TransferLimitsModal
            modal={transferLimitsModal}
            canEdit={can(user, 'customers.adjust_limits', transferLimitsModal.customer) || can(user, 'customers.edit', transferLimitsModal.customer)}
            canViewCollectionReport={can(user, 'customers.read')}
            onClose={closeTransferLimits}
            onSave={saveTransferLimits}
            onAdjust={() => setTransferLimitsModal((current) => ({ ...current, editing: true }))}
            onChange={(key, value) => setTransferLimitsModal((current) => ({
              ...current, draft: { ...current.draft, [key]: value }, error: '',
            }))}
          />
        )}
        {statusDialog.open && (
          <div className="transfer-alert-overlay">
            <div className="transfer-alert-modal" onClick={(e) => e.stopPropagation()}>
              <p>{statusDialog.message}</p>
              <button type="button" className="button small red" onClick={closeStatusDialog} autoFocus>
                OK
              </button>
            </div>
          </div>
        )}
        {managerAdjustmentModal.open && (
          <div className="transfer-alert-overlay">
            <div className="transfer-alert-modal" onClick={(e) => e.stopPropagation()}>
              <p>
                Serial number <strong>{managerAdjustmentModal.serial}</strong> requires Manager's adjustment.
                Please hand this item over to your manager.
              </p>
              <button type="button" className="button small red" onClick={closeManagerAdjustmentModal} autoFocus>
                OK
              </button>
            </div>
          </div>
        )}
        {serialChoiceModal.open && (
          <div className="transfer-confirm-overlay" onClick={closeSerialChoiceModal}>
            <div className="transfer-confirm-modal transfer-serial-choice-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeSerialChoiceModal}
              >
                x
              </button>
              <h3>Choose Serial Item</h3>
              <p>
                Serial number <strong>{serialChoiceModal.serial}</strong> has more than one available item.
              </p>
              <div className="transfer-serial-choice-list">
                {serialChoiceModal.rows.map((row) => (
                  <button
                    key={row.subLedgerId || `${row.serial}-${row.itemId}-${row.sourceDocNo}`}
                    type="button"
                    className="transfer-serial-choice-row"
                    onClick={() => confirmSerialChoice(row)}
                  >
                    <span className="transfer-serial-choice-main">
                      <strong>{row.item || row.description || '-'}</strong>
                      <span>{row.currentLocation || row.location || row.shipTo || '-'}</span>
                    </span>
                    <span className="transfer-serial-choice-meta">
                      <span>Item #{row.itemId || '-'}</span>
                      <span>Doc #{row.sourceDocNo || '-'}</span>
                      <span>{formatShortDate(row.sourceDate) || '-'}</span>
                      <span>Sub-ledger #{row.subLedgerId || '-'}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="transfer-confirm-actions">
                <button type="button" className="button small transfer-confirm-cancel" onClick={closeSerialChoiceModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {locationSwitchConfirm.open && (
          <div className="transfer-confirm-overlay" onClick={closeLocationSwitchConfirm}>
            <form className="transfer-confirm-modal" onSubmit={confirmLocationSwitch} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeLocationSwitchConfirm}
              >
                x
              </button>
              <h3>Switch Location From</h3>
              <p>
                Serial number <strong>{locationSwitchConfirm.serial}</strong> was not found in{' '}
                <strong>{locationSwitchConfirm.selectedLocation}</strong>. It is currently located at{' '}
                <strong>{locationSwitchConfirm.currentLocation}</strong>.
              </p>
              {newTransferQueue.length > 0 && (
                <p className="transfer-confirm-error">
                  This action will reset the current transfer order and replace the queued serial numbers.
                </p>
              )}
              <p>
                Type serial number <strong>{locationSwitchConfirm.serial}</strong> to switch Location From and add it to the queue.
              </p>
              <input
                type="text"
                value={locationSwitchConfirm.value}
                onChange={(e) => setLocationSwitchConfirm((current) => ({
                  ...current,
                  value: e.target.value,
                  error: '',
                }))}
                autoFocus
                className="transfer-confirm-input"
              />
              {locationSwitchConfirm.error && (
                <p className="transfer-confirm-error">{locationSwitchConfirm.error}</p>
              )}
              <div className="transfer-confirm-actions">
                <button type="button" className="button small transfer-confirm-cancel" onClick={closeLocationSwitchConfirm}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button small blue"
                  disabled={
                    String(locationSwitchConfirm.value || '').trim()
                      !== String(locationSwitchConfirm.serial || '').trim()
                  }
                >
                  Switch & Add
                </button>
              </div>
            </form>
          </div>
        )}
        {retailCustomerModalOpen && (
          <div className="transfer-confirm-overlay" onClick={closeRetailCustomerModal}>
            <form
              className="transfer-confirm-modal sale-customer-add-modal"
              onSubmit={createRetailCustomer}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeRetailCustomerModal}
                disabled={retailCustomerSaving}
              >
                x
              </button>
              <h3>Add Retail Customer</h3>
              <label>
                <span>Customer</span>
                <input
                  className="transfer-confirm-input"
                  type="text"
                  value={newRetailCustomer.companyname}
                  onChange={(event) => setNewRetailCustomer((current) => ({
                    ...current,
                    companyname: event.target.value,
                  }))}
                  autoFocus
                  required
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  className="transfer-confirm-input"
                  type="tel"
                  value={newRetailCustomer.phone}
                  onChange={(event) => {
                    setNewRetailCustomer((current) => ({ ...current, phone: event.target.value }));
                    setRetailDuplicatePhoneCustomers([]);
                    setRetailCustomerStatus('');
                  }}
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  className="transfer-confirm-input"
                  type="email"
                  value={newRetailCustomer.email}
                  onChange={(event) => setNewRetailCustomer((current) => ({
                    ...current,
                    email: event.target.value,
                  }))}
                />
              </label>
              {retailCustomerStatus && <p className="transfer-confirm-error">{retailCustomerStatus}</p>}
              {retailDuplicatePhoneCustomers.length > 0 && (
                <div className="customer-phone-matches" role="alert">
                  <strong>Customers with this phone number</strong>
                  <ul>
                    {retailDuplicatePhoneCustomers.map((customer) => (
                      <li key={customer.id}>
                        <strong>{customer.name}</strong> (#{customer.id})
                        <div>Phone: {customer.phone || '—'}</div>
                        {customer.altphone && <div>Alternate: {customer.altphone}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="transfer-confirm-actions">
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={closeRetailCustomerModal}
                  disabled={retailCustomerSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="button small blue" disabled={retailCustomerSaving || !canCreateRetailCustomer}>
                  {retailCustomerSaving ? 'Adding...' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        )}
        {customerReturnLocationChoice.open && (
          <div className="transfer-confirm-overlay" onClick={closeCustomerReturnLocationChoice}>
            <div className="transfer-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeCustomerReturnLocationChoice}
              >
                x
              </button>
              <h3>Return Location</h3>
              <p>
                Return items for <strong>{getCustomerDisplayName(customerReturnLocationChoice.customer)}</strong> to which location?
              </p>
              <div className="transfer-confirm-actions">
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={() => chooseCustomerReturnLocation(customerReturnLocationChoice.customerLocationName)}
                  disabled={!customerReturnLocationChoice.customerLocationName}
                >
                  {customerReturnLocationChoice.customerLocationName || 'Customer Location'}
                </button>
                <button
                  type="button"
                  className="button small blue"
                  onClick={() => chooseCustomerReturnLocation(DEFAULT_TRANSFER_FROM_LOCATION)}
                >
                  Irenes Wigs - New York
                </button>
              </div>
            </div>
          </div>
        )}
        {removeConfirm.open && (
          <div className="transfer-confirm-overlay" onClick={closeRemoveQueuedSerial}>
            <form className="transfer-confirm-modal" onSubmit={removeQueuedSerial} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeRemoveQueuedSerial}
              >
                x
              </button>
              <h3>Remove Serial Number from Queue</h3>
              <p>
                Type serial number <strong>{removeConfirm.row?.serial}</strong> to remove it from the transfer queue.
              </p>
              <input
                type="text"
                value={removeConfirm.value}
                onChange={(e) => setRemoveConfirm((current) => ({
                  ...current,
                  value: e.target.value,
                  error: '',
                }))}
                autoFocus
                className="transfer-confirm-input"
              />
              {removeConfirm.error && <p className="transfer-confirm-error">{removeConfirm.error}</p>}
              <div className="transfer-confirm-actions">
                <button type="button" className="button small transfer-confirm-cancel" onClick={closeRemoveQueuedSerial}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button small red"
                  disabled={
                    String(removeConfirm.value || '').trim() !== String(removeConfirm.row?.serial || '').trim()
                  }
                >
                  Remove
                </button>
              </div>
            </form>
          </div>
        )}
        {deleteTransferConfirm.open && (
          <div className="transfer-confirm-overlay" onClick={closeDeleteTransfer}>
            <form className="transfer-confirm-modal" onSubmit={deleteTransferDoc} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="transfer-modal-close"
                onClick={closeDeleteTransfer}
                disabled={transferDeleteLoading}
              >
                x
              </button>
              <h3>Delete {Number(deleteTransferConfirm.row?.transactionTypeId) === 4 ? 'Shipping' : 'Transfer'}</h3>
              <p>
                Type doc no <strong>{deleteTransferConfirm.row?.sourceDocNo}</strong> to delete this {Number(deleteTransferConfirm.row?.transactionTypeId) === 4 ? 'shipping doc' : 'transfer'}.
              </p>
              <input
                type="text"
                value={deleteTransferConfirm.value}
                onChange={(e) => setDeleteTransferConfirm((current) => ({
                  ...current,
                  value: e.target.value,
                  error: '',
                }))}
                autoFocus
                className="transfer-confirm-input"
              />
              {deleteTransferConfirm.error && (
                <p className="transfer-confirm-error">{deleteTransferConfirm.error}</p>
              )}
              <div className="transfer-confirm-actions">
                <button
                  type="button"
                  className="button small transfer-confirm-cancel"
                  onClick={closeDeleteTransfer}
                  disabled={transferDeleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="button small red"
                  disabled={
                    transferDeleteLoading
                    || String(deleteTransferConfirm.value || '').trim()
                      !== String(deleteTransferConfirm.row?.sourceDocNo || '').trim()
                  }
                >
                  {transferDeleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </>
  );
}

export default TransfersPage;
